import { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { jsPDF } from 'jspdf'
import { listingsAPI, usersAPI, ordersAPI, messagesAPI, adoptionAPI, deliveryAPI, animalDocsAPI } from '../../api'
import { generateAdoptionPDF } from '../AdoptionFormPage/AdoptionFormPage'
import { compressImages } from '../ChatThread/imageCompression'
import ChatThread from '../ChatThread/ChatThread'
import { CarnetSanteForm, AttestationVetForm, IdentificationForm, CessionForm } from './DocForms'
import './AdminDashboard.css'

const CATEGORIES = ['Chiens','Chats','Oiseaux','Reptiles','Lapins','Rongeurs','Poissons','Chevaux']

const BREEDS_BY_CAT = {
  'Chiens':   ['Berger Allemand','Golden Retriever','Labrador','Husky de Sibérie','Border Collie','Bouledogue Français','Spitz Nain','Cavalier King Charles','Berger Australien','Yorkshire Terrier','Beagle','Chihuahua','Rottweiler','Malinois','Caniche'],
  'Chats':    ['Maine Coon','Bengal','Ragdoll','British Shorthair','Persan','Sphynx','Scottish Fold','Birman','Sibérien','Norvégien des Forêts','Abyssin','Chartreux'],
  'Oiseaux':  ['Gris du Gabon','Conure Soleil','Calopsitte','Amazone','Cacatoès','Perruche Ondulée','Inséparable'],
  'Reptiles': ['Dragon Barbu','Gecko Léopard','Python Royal','Boa Constrictor','Iguane Vert','Tortue Grecque'],
  'Lapins':   ['Bélier Nain','Angora','Rex','Nain de Hollande','Lionhead'],
  'Rongeurs': ['Cochon d\'Inde','Hamster Syrien','Chinchilla','Rat Domestique','Gerbille'],
  'Poissons': ['Carpe Koï','Guppy','Betta','Discus','Scalaire','Poisson Rouge'],
  'Chevaux':  ['Poney Shetland','Âne','Quarter Horse','Pur-sang','Andalou'],
}

const REGIONS = ['Île-de-France','Auvergne-Rhône-Alpes','Bretagne','Nouvelle-Aquitaine','Occitanie','PACA','Hauts-de-France','Grand Est','Normandie','Pays de la Loire','Centre-Val de Loire']

const DELIVERY_OPTIONS = ['Livraison incluse', 'Livraison non incluse']

const Req = () => <span style={{ color:'var(--red)', fontWeight:700, marginLeft:2 }}>*</span>

const MONTHS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']
const FALLBACK_REVENUE = [2800,3200,2900,4100,5600,4800,6200,7100,5400,8200,8900,9800]
const FALLBACK_VIEWS   = [320,410,380,520,690,580,720,810,650,890,940,1050]
const FALLBACK_ORDERS  = [12,16,13,19,24,21,27,31,22,36,39,44]

const fmt     = (n) => n.toLocaleString('fr-FR') + ' €'
const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toISOString().slice(0, 10) } catch { return '—' } }

// ── Select avec recherche + saisie libre (Race, Région, Espèce) ──────────────
function BreedSelect({ category, options, value, onChange, placeholder }) {
  const [search, setSearch] = useState('')
  const [open, setOpen]     = useState(false)
  const ref      = useRef(null)
  const inputRef = useRef(null)

  const allOptions = options || BREEDS_BY_CAT[category] || []
  const filtered   = allOptions.filter(o => o.toLowerCase().includes(search.toLowerCase()))
  const ph         = placeholder || (category ? 'Sélectionner une race…' : 'Sélectionner…')

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleOpen   = () => { setOpen(true); setSearch(''); setTimeout(() => inputRef.current?.focus(), 30) }
  const handleSelect = (val) => { onChange(val); setOpen(false); setSearch('') }
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && search.trim()) handleSelect(search.trim())
    if (e.key === 'Escape') setOpen(false)
  }

  return (
    <div className="breed-wrap" ref={ref}>
      <div className="adm-input breed-display" onClick={handleOpen}>
        <span style={{ color: value ? 'var(--blue)' : 'var(--slate-lt)', fontSize: 14 }}>
          {value || ph}
        </span>
        <svg className="breed-arrow-svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition:'transform .2s', color:'var(--slate-lt)', flexShrink:0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {open && (
        <div className="breed-dropdown">
          <div className="breed-search">
            <input ref={inputRef} placeholder="Rechercher ou taper…" value={search} onChange={e => setSearch(e.target.value)} onKeyDown={handleKeyDown} onClick={e => e.stopPropagation()} />
          </div>
          <div className="breed-list">
            {search.trim() && !allOptions.some(o => o.toLowerCase() === search.toLowerCase()) && (
              <div className="breed-item breed-item-custom" onClick={() => handleSelect(search.trim())}>
                <span style={{ fontSize:11, color:'var(--slate)', marginRight:5 }}>Utiliser :</span>
                <strong>{search.trim()}</strong>
              </div>
            )}
            {filtered.length === 0 && !search && <div className="breed-empty">Commencez à taper…</div>}
            {filtered.length === 0 && search && <div className="breed-empty">Aucun résultat — appuyez sur Entrée pour utiliser votre saisie</div>}
            {filtered.map(o => (
              <div key={o} className={`breed-item${value === o ? ' selected' : ''}`} onClick={() => handleSelect(o)}>{o}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── SVG Icons ─────────────────────────────────────────────
const IcoDashboard = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
const IcoRevenue  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
const IcoListings = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
const IcoPublish  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
const IcoUsers    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
const IcoOrders   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>
const IcoMessages = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
const IcoSettings = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
const IcoGlobe    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
const IcoLogout   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
const IcoEdit     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
const IcoTrash    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
const IcoCheck    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
const IcoBan      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
const IcoSave     = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
const IcoRocket   = () => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
const IcoUpload   = () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/></svg>
const IcoPin      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
const IcoTrend    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
const IcoPie      = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.21 15.89A10 10 0 1 1 8 2.83"/><path d="M22 12A10 10 0 0 0 12 2v10z"/></svg>
const IcoSearch   = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
const IcoEye      = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
const IcoSyringe  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m18 2 4 4"/><path d="m17 7 1-5"/><path d="M7 21 16.293 11.707a1 1 0 0 0 0-1.414l-2.586-2.586a1 1 0 0 0-1.414 0L2 17"/><path d="m2 17 5 5"/><path d="m6 13 4 4"/></svg>
const IcoShield   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
const IcoTruck    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
const IcoClock    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
const IcoChevronLeft = () => <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
const IcoInvoice  = () => <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>

// ── Main Component ─────────────────────────────────────────

// ═══════════════════════════════════════════════════════════════
// TRACKING — fonctions hors-composant (identiques à TrackingService.jsx)
// ═══════════════════════════════════════════════════════════════
const timeToMinutes = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m }

const calculateWaypointTimelines = (waypoints) => {
  if (!waypoints || waypoints.length < 2) return { waypointsWithProgress: [], totalDuration: 0 }
  const startMin = timeToMinutes(waypoints[0].time)
  let prevMin = startMin
  const timelines = waypoints.map((wp, i) => {
    let cur = timeToMinutes(wp.time)
    if (i > 0 && cur < prevMin) cur += 1440
    const off = cur - startMin
    prevMin = cur
    return { ...wp, minutesFromStart: off >= 0 ? off : off + 1440, rawMinutes: cur }
  })
  const totalDuration = timelines[timelines.length - 1].minutesFromStart
  const waypointsWithProgress = timelines.map(wp => ({ ...wp, progressThreshold: wp.minutesFromStart / totalDuration }))
  return { waypointsWithProgress, totalDuration }
}

const calculateSegmentSpeeds = (wps, totalDurSecs) => {
  const segs = []
  for (let i = 0; i < wps.length - 1; i++) {
    const from = wps[i], to = wps[i + 1]
    const segDurMin = to.minutesFromStart - from.minutesFromStart
    const segDurSec = segDurMin * 60
    const segRange  = to.progressThreshold - from.progressThreshold
    segs.push({ fromProgress: from.progressThreshold, toProgress: to.progressThreshold, speed: segRange / (segDurSec * 60), durationSeconds: segDurSec, fromWaypoint: from, toWaypoint: to })
  }
  return segs
}

const drawDog = (ctx, cx, cy) => {
  ctx.save(); ctx.translate(cx, cy); ctx.scale(-1.15, 1.15)
  ctx.beginPath(); ctx.arc(0, 0, 17, 0, 2 * Math.PI); ctx.fillStyle = 'rgba(29,158,117,0.2)'; ctx.fill()
  ctx.beginPath(); ctx.ellipse(-1, 3, 10, 6, -0.15, 0, 2 * Math.PI); ctx.fillStyle = '#C8832A'; ctx.fill()
  ctx.beginPath(); ctx.arc(-11, -3, 7, 0, 2 * Math.PI); ctx.fillStyle = '#C8832A'; ctx.fill()
  ctx.beginPath(); ctx.ellipse(-16, -1, 3.5, 2.5, 0, 0, 2 * Math.PI); ctx.fillStyle = '#A0622A'; ctx.fill()
  ctx.beginPath(); ctx.arc(-18, -2, 1.4, 0, 2 * Math.PI); ctx.fillStyle = '#111'; ctx.fill()
  ctx.beginPath(); ctx.arc(-10, -5.5, 1.8, 0, 2 * Math.PI); ctx.fillStyle = '#111'; ctx.fill()
  ctx.beginPath(); ctx.arc(-9.3, -6.2, 0.6, 0, 2 * Math.PI); ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.fill()
  ctx.beginPath(); ctx.ellipse(-8, -9, 3.5, 5, -0.4, 0, 2 * Math.PI); ctx.fillStyle = '#9B5E1A'; ctx.fill()
  ctx.restore()
}

const drawMapOnCanvas = (canvas, ship, progress, adjWps) => {
  if (!canvas || !ship) return
  const wps = adjWps || ship.waypoints
  const ctx = canvas.getContext('2d')
  const w = canvas.width, h = canvas.height
  if (!w || !h) return
  ctx.clearRect(0, 0, w, h)
  ctx.fillStyle = '#f0efe7'; ctx.fillRect(0, 0, w, h)
  const n = wps.length - 1, margin = 60
  const usableW = w - 2 * margin
  const startMin = timeToMinutes(wps[0].time)
  let prevMin = startMin
  const absMin = wps.map((wp, i) => { let m = timeToMinutes(wp.time); if (i > 0 && m < prevMin) m += 1440; prevMin = m; return m })
  const totalDur = absMin[n] - absMin[0]
  const positions = absMin.map(m => totalDur > 0 ? margin + ((m - absMin[0]) / totalDur) * usableW : margin + (absMin.indexOf(m) / Math.max(n, 1)) * usableW)
  let currentX
  if (progress >= 1) currentX = positions[n]
  else if (progress <= 0) currentX = positions[0]
  else {
    const thresholds = absMin.map(m => totalDur > 0 ? (m - absMin[0]) / totalDur : 0)
    let si = n - 1
    for (let i = 0; i < n; i++) { if (progress >= thresholds[i] && progress < thresholds[i + 1]) { si = i; break } }
    const sf = thresholds[si + 1] > thresholds[si] ? (progress - thresholds[si]) / (thresholds[si + 1] - thresholds[si]) : 0
    currentX = positions[si] + (positions[si + 1] - positions[si]) * sf
  }
  ctx.beginPath(); ctx.moveTo(positions[0], h / 2); positions.slice(1).forEach(x => ctx.lineTo(x, h / 2)); ctx.strokeStyle = '#ddd'; ctx.lineWidth = 4; ctx.stroke()
  ctx.beginPath(); ctx.moveTo(positions[0], h / 2)
  for (let i = 1; i < positions.length; i++) { if (positions[i] <= currentX) ctx.lineTo(positions[i], h / 2); else { ctx.lineTo(currentX, h / 2); break } }
  ctx.strokeStyle = '#D9AE5C'; ctx.lineWidth = 4; ctx.stroke()
  positions.forEach((x, i) => {
    const color = wps[i].type === 'start' ? '#12224A' : wps[i].type === 'end' ? '#D9AE5C' : '#4C84D9'
    ctx.beginPath(); ctx.arc(x, h / 2, 8, 0, 2 * Math.PI); ctx.fillStyle = color; ctx.fill()
    ctx.font = '12px sans-serif'; ctx.textAlign = 'center'
    if (wps[i].type === 'start') ctx.fillText('🚩', x, h / 2 + 4)
    else if (wps[i].type === 'end') ctx.fillText('🏁', x, h / 2 + 4)
    else ctx.fillText('📍', x, h / 2 + 4)
    ctx.fillStyle = '#12224A'; ctx.font = 'bold 10px sans-serif'
    ctx.fillText(wps[i].name.length > 12 ? wps[i].name.slice(0, 10) + '…' : wps[i].name, x, h / 2 - 14)
    ctx.fillStyle = '#5A6A85'; ctx.font = '9px monospace'
    ctx.fillText(wps[i].time, x, h / 2 - 4)
  })
  const dogY = h / 2 - 22
  if (progress > 0) drawDog(ctx, progress >= 1 ? positions[n] : currentX, dogY)
  else drawDog(ctx, positions[0], dogY)
}

// ── Composant canvas signature ──────────────────────────────────
function SignatureCanvas({ canvasRef, onSign, label }) {
  const drawing = useRef(false)
  const [signed, setSigned] = useState(false)

  const getPos = (e, canvas) => {
    const r = canvas.getBoundingClientRect()
    const src = e.touches ? e.touches[0] : e
    return { x: (src.clientX - r.left) * (canvas.width / r.width), y: (src.clientY - r.top) * (canvas.height / r.height) }
  }

  const startDraw = (e) => {
    e.preventDefault()
    const canvas = canvasRef.current
    if (!canvas) return
    drawing.current = true
    const ctx = canvas.getContext('2d')
    const pos = getPos(e, canvas)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }

  const draw = (e) => {
    e.preventDefault()
    if (!drawing.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#1e3a8a'
    const pos = getPos(e, canvas)
    ctx.lineTo(pos.x, pos.y)
    ctx.stroke()
    setSigned(true)
  }

  const stopDraw = (e) => {
    e.preventDefault()
    if (!drawing.current) return
    drawing.current = false
    const canvas = canvasRef.current
    if (!canvas) return
    onSign(canvas.toDataURL('image/png'))
  }

  const clear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height)
    setSigned(false)
    onSign(null)
  }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:6}}>
      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>
      <div style={{position:'relative',border:'1.5px solid var(--border)',borderRadius:8,background:'#f8faff',overflow:'hidden'}}>
        <canvas
          ref={canvasRef}
          width={340} height={90}
          style={{display:'block',width:'100%',height:90,cursor:'crosshair',touchAction:'none'}}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
        />
        {!signed && (
          <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none'}}>
            <span style={{fontSize:11,color:'var(--slate-lt)'}}>✍️ Signez ici</span>
          </div>
        )}
      </div>
      <button type="button" className="adm-btn adm-btn-ghost" style={{fontSize:11,padding:'3px 10px',alignSelf:'flex-start'}} onClick={clear}>Effacer</button>
    </div>
  )
}

// Cache logo pour éviter un fetch réseau à chaque génération PDF
let _cachedLogo = null
const _getLogoDataUrl = async () => {
  if (_cachedLogo) return _cachedLogo
  try {
    const r = await fetch(require('../../assets/monlogo.jpeg'))
    const b = await r.blob()
    _cachedLogo = await new Promise(res => { const rd = new FileReader(); rd.onload = () => res(rd.result); rd.readAsDataURL(b) })
  } catch (e) {}
  return _cachedLogo
}

export default function AdminDashboard({
  listings             = [],
  setListings          = () => {},
  onBack               = () => {},
  onLogout             = () => {},
  onCartClick          = () => {},
  cartCount            = 0,
  notifBadge           = 0,
  autoOpenListing      = false,
  onListingFormOpened  = () => {},
}) {
  const [view, setView]         = useState('dashboard')
  const [sbOpen, setSbOpen]     = useState(false)
  // ── Données backend ──────────────────────────────────────
  const [users, setUsers]             = useState([])
  const [localOrders, setLocalOrders] = useState([])
  const [conversations, setConversations]   = useState([])
  // ────────────────────────────────────────────────────────
  const [newOrdersBadge, setNewOrdersBadge] = useState(0)
  const [newUsersBadge,  setNewUsersBadge]  = useState(0)
  const [adoptionBadge,  setAdoptionBadge]  = useState(0)
  const [docSignedBadge, setDocSignedBadge] = useState(0)
  const lastUserCount                        = useRef(-1)   // -1 = non initialisé
  const knownSignedIds                       = useRef(null) // Set des _id déjà vus comme 'signed'
  const docFormOpenRef                       = useRef(false) // true quand un formulaire de document est ouvert → stoppe le polling livraison
  // ── Section Formulaires d'adoption ──
  const [docTab,         setDocTab]         = useState('adoption')
  const [livraisonForms, setLivraisonForms] = useState([])
  const [facturesForms,  setFacturesForms]  = useState([])
  const [lf, setLf] = useState({ entreprise:'Air Transport Animalière', tel:'07 50 57 75 00 92', localisation:'', responsable:'', clientNom:'', clientTel:'', clientEmail:'', clientAdresse:'', idEntreprise:'', prixTransport:'', depart:'', arrivee:'', date:new Date().toLocaleDateString('fr-FR'), dateCommande:'', heure:'', modeTransport:'Transport terrestre', animal:'', espece:'', race:'', ageAnimal:'', poids:'', couleur:'', puce:'' })
  const [ff, setFf] = useState({ entreprise:'Air Transport Animalière', tel:'07 50 57 75 00 92', emailEntreprise:'', localisation:'', responsable:'', clientNom:'', clientTel:'', clientEmail:'', clientAdresse:'', ref:`F-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`, date:new Date().toLocaleDateString('fr-FR'), montant:'', description:'Transport animalier', animal:'', espece:'', modePaiement:'', iban:'', bic:'', banque:'', dateEcheance:'', references:'' })
  const [showLfForm,    setShowLfForm]    = useState(false)
  const [showFfForm,    setShowFfForm]    = useState(false)
  const [showCsForm,    setShowCsForm]    = useState(false)
  const [showAvForm,    setShowAvForm]    = useState(false)
  const [showDiForm,    setShowDiForm]    = useState(false)
  const [showAcForm,    setShowAcForm]    = useState(false)
  const [carnetForms,   setCarnetForms]   = useState([])
  const [attestForms,   setAttestForms]   = useState([])
  const [identifForms,  setIdentifForms]  = useState([])
  const [cessionForms,  setCessionForms]  = useState([])
  // Pagination des 4 tableaux animalDocs
  const [carnetPage,    setCarnetPage]    = useState(1)
  const [attestPage,    setAttestPage]    = useState(1)
  const [identifPage,   setIdentifPage]   = useState(1)
  const [cessionPage,   setCessionPage]   = useState(1)
  const ADOC_PER = 8
  // Signatures livraison
  const [lfSigEntreprise, setLfSigEntreprise] = useState(null)
  const [_lfSigClient,    setLfSigClient]      = useState(null)

  const lfSigEntRef = useRef(null)
  const _lfSigCliRef = useRef(null)
  // Signatures facture
  const [ffSigEntreprise, setFfSigEntreprise] = useState(null)
  const [_ffSigClient,    setFfSigClient]      = useState(null)

  const ffSigEntRef = useRef(null)
  const _ffSigCliRef = useRef(null)
  // ── États recherche utilisateur — formulaire livraison ──
  const [lfUserResolved, setLfUserResolved] = useState(null)
  const [lfUserSearch,   setLfUserSearch]   = useState('')
  const [lfUserMatches,  setLfUserMatches]  = useState([])

  // ── États recherche utilisateur — formulaire facture ──
  const [ffUserResolved, setFfUserResolved] = useState(null)
  const [ffUserSearch,   setFfUserSearch]   = useState('')
  const [ffUserMatches,  setFfUserMatches]  = useState([])

  const [adoptionForms,     setAdoptionForms]     = useState([])
  const [adoptionLoading,   setAdoptionLoading]   = useState(false)
  const [deliveryLoading,   setDeliveryLoading]   = useState(false)
  const [selectedAdoption,  setSelectedAdoption]  = useState(null) // formulaire sélectionné pour le détail
  const [adoptionDetailLoading, setAdoptionDetailLoading] = useState(false)
  const [downloadingAdoptionId, setDownloadingAdoptionId] = useState(null)
  // Clé localStorage : nombre de commandes vues par l'admin lors de sa dernière visite
  const ORDERS_SEEN_KEY = 'admin_orders_seen_count'
  const [toast, setToast]       = useState(null)
  const [confirmDel, setConfirmDel] = useState(null)
  const [rejectModal, setRejectModal]   = useState(null) // annonce en cours de rejet, ou null
  const [rejectReason, setRejectReason] = useState('')
  const [rejectSaving, setRejectSaving] = useState(false)
  const [selectedConvId, setSelectedConvId] = useState(null)   // userId sélectionné (panneau 1)
  const [userListingConvs, setUserListingConvs]     = useState([]) // annonces discutées avec cet user (panneau 2)
  const [listingConvsLoading, setListingConvsLoading] = useState(false)
  const [listingPanelOpen, setListingPanelOpen] = useState(false) // panneau 2 ouvert/fermé (façon tiroir)
  const [selectedListingKey, setSelectedListingKey] = useState(null) // annonce sélectionnée (ou 'general')
  const [threadMsgs, setThreadMsgs]         = useState([])   // fil de la conversation sélectionnée (panneau 3)
  const [threadLoading, setThreadLoading]   = useState(false)
  const [chatSending, setChatSending]       = useState(false)
  const [userStatus, setUserStatus]         = useState(null) // { online, lastActive } — statut de l'user sélectionné

  // Annonce actuellement sélectionnée dans le panneau 2 — la barre et le
  // rattachement des réponses en dépendent directement (plus d'inférence
  // automatique : l'admin choisit explicitement l'annonce avant de répondre).
  const selectedListingConv = userListingConvs.find(c => c.key === selectedListingKey)
  const threadListing = selectedListingConv?.listing || null
  const threadBanner = threadListing ? {
    img:   threadListing.images?.[0]?.url,
    breed: threadListing.breed,
    title: threadListing.title,
    price: threadListing.price,
  } : null

  const [showForm, setShowForm] = useState(false)
  const [editItem, setEditItem] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all') // all | pending | active | rejected
  const [searchQ, setSearchQ]   = useState('')
  const [chartMode, setChartMode] = useState('revenue')
  const [listingsPage, setListingsPage] = useState(1)
  const [usersPage, setUsersPage]       = useState(1)
  const [ordersAdmPage, setOrdersAdmPage] = useState(1)
  const LISTINGS_PER_PAGE = 10

  // Form state
  const [fTitle, setFTitle]   = useState('')
  const [fBreed, setFBreed]   = useState('')
  const [fCat, setFCat]       = useState('Chiens')
  const [fPrice, setFPrice]   = useState('')
  const [fAge, setFAge]       = useState('')
  const [fSex, setFSex]       = useState('Mâle')
  const [fCity, setFCity]     = useState('')
  const [fRegion, setFRegion] = useState('')
  const [fSeller, setFSeller] = useState('')
  const [fSellerType, setFSellerType] = useState('Pro')
  const [fPhone, setFPhone]   = useState('')
  const [fDesc, setFDesc]     = useState('')
  const [fStatus, setFStatus] = useState('active')
  const [fVacc, setFVacc]     = useState(false)
  const [fMicro, setFMicro]   = useState(false)
  const [fPedi, setFPedi]     = useState(false)
  const [fFeat, setFFeat]     = useState(false)
  const [fDelivery, setFDelivery] = useState('Livraison incluse')
  const [fImgs, setFImgs]     = useState([])
  const [fSaving, setFSaving]           = useState(false)
  const [revenueStats, setRevenueStats] = useState(null)  // stats backend
  const [monthlyData, setMonthlyData]   = useState(null)  // activité mensuelle backend
  const [drag, setDrag]                 = useState(false)
  const fileRef               = useRef(null)

  const orders = localOrders

  const updateOrderStatus = async (orderId, newStatus) => {
    // orderId = o.id = orderNumber (ex: CMD-2026-xxx) — affiché mais pas utilisable en API
    // On récupère le vrai _mongoId MongoDB depuis la liste locale
    const order = localOrders.find(o => o.id === orderId)
    const mongoId = order?._mongoId || orderId
    try {
      await ordersAPI.updateStatus(mongoId, newStatus)
      setLocalOrders(lo => lo.map(o => o.id === orderId ? { ...o, status: newStatus } : o))
      try { new BroadcastChannel('app_sync_event').postMessage({ type: 'order_update' }) } catch {}
      showToast('Statut mis à jour — visible côté utilisateur')
    } catch (err) {
      showToast(err.message || 'Erreur lors de la mise à jour du statut')
    }
  }

  // ── Change le statut d'une annonce en le persistant réellement en base ──
  // (approuver / rejeter avec motif / remettre en attente / marquer vendue).
  // C'est le point de passage unique utilisé par le badge de statut cliquable,
  // le bouton ✓ d'approbation rapide et la modale de rejet.
  const handleListingStatusChange = async (listing, newStatus, rejectionReason = '') => {
    const id = listing._id || listing.id
    try {
      await listingsAPI.updateStatus(id, newStatus, rejectionReason)
      setListings(ls => ls.map(i => ((i.id || i._id) === id)
        ? { ...i, status: newStatus, rejectionReason: newStatus === 'rejected' ? rejectionReason : '' }
        : i
      ))
      showToast(
        newStatus === 'active'   ? 'Annonce approuvée — visible sur le site' :
        newStatus === 'rejected' ? 'Annonce rejetée — motif visible par l’utilisateur' :
        newStatus === 'pending'  ? 'Annonce remise en attente' :
        'Statut mis à jour'
      )
      return true
    } catch (err) {
      showToast(err.message || 'Erreur lors de la mise à jour du statut')
      return false
    }
  }

  // ── Ouvre la modale de rejet pour une annonce donnée ──
  const openRejectModal = (listing) => { setRejectModal(listing); setRejectReason('') }

  // ── Confirme le rejet avec le motif saisi ──
  const confirmReject = async () => {
    if (!rejectModal) return
    setRejectSaving(true)
    const ok = await handleListingStatusChange(rejectModal, 'rejected', rejectReason.trim())
    setRejectSaving(false)
    if (ok) { setRejectModal(null); setRejectReason('') }
  }

  // ── Charger utilisateurs, commandes, messages depuis le backend ──
  useEffect(() => {
    const loadAll = async () => {
      // Utilisateurs
      try {
        const uData = await usersAPI.getAll({ limit: 50 })
        if (uData?.users) {
          const mappedUsers = uData.users.map(u => ({
            id:        u._id,
            name:      u.name,
            email:     u.email,
            type:      u.role === 'admin' ? 'Admin' : 'Part',
            city:      u.city || '—',
            joined:    fmtDate(u.createdAt),
            status:    u.isActive === false ? 'banned' : 'active',
            purchases: u.purchases || 0,
          }))
          setUsers(mappedUsers)
          if (lastUserCount.current === -1) {
            lastUserCount.current = mappedUsers.length  // init sans badge
          } else if (mappedUsers.length > lastUserCount.current) {
            setNewUsersBadge(n => n + (mappedUsers.length - lastUserCount.current))
            lastUserCount.current = mappedUsers.length
          }
        }
      } catch (err) { console.warn('Utilisateurs:', err.message) }

      // Commandes
      try {
        const oData = await ordersAPI.getAllAdmin({ limit: 50 })
        if (oData?.orders) {
          const mappedOrders = oData.orders.map(o => ({
            id:            o.orderNumber || o._id,  // affiché en référence lisible
            _mongoId:      o._id,                   // vrai _id MongoDB pour les appels API
            buyer:         o.buyer?.name  || 'Utilisateur',
            animal:        o.listing?.title || o.listingSnapshot?.title || '—',
            amount:        o.amount || 0,
            commission:    Math.round((o.amount || 0) * 0.1),
            date:          fmtDate(o.createdAt),
            status:        o.deliveryStatus  || 'pending',
            paymentStatus: o.paymentStatus   || 'pending',
            payMethod:     o.paymentMethod   || 'wire',
            wireReference: o.wireReference   || '',
            cardBrand:     o.cardBrand       || '',
            cardLast4:     o.cardLast4       || '',
          }))

          // Badge nouvelles commandes : compare avec le total sauvegardé en localStorage
          // Le badge persiste même si l'admin ferme et rouvre le dashboard
          const rawSeen = localStorage.getItem(ORDERS_SEEN_KEY)
          if (rawSeen === null) {
            // Première connexion : on considère toutes les commandes actuelles comme vues
            localStorage.setItem(ORDERS_SEEN_KEY, String(mappedOrders.length))
            setNewOrdersBadge(0)
          } else {
            const seenCount = parseInt(rawSeen, 10)
            if (mappedOrders.length > seenCount) {
              setNewOrdersBadge(mappedOrders.length - seenCount)
            } else {
              setNewOrdersBadge(0)
            }
          }

          setLocalOrders(mappedOrders)
        }
      } catch (err) { console.warn('Commandes:', err.message) }

      // Stats revenus (byCategory + monthlyRevenue + KPIs)
      try {
        const sData = await ordersAPI.getStats()
        if (sData?.stats) {
          setRevenueStats(sData.stats)
          if (sData.stats.monthlyRevenue) setMonthlyData(sData.stats.monthlyRevenue)
        }
      } catch (err) { console.warn('Stats:', err.message) }

      // Conversations (messagerie) — une entrée par utilisateur
      try {
        const cData = await messagesAPI.getAdminConversations()
        if (cData?.conversations) setConversations(cData.conversations)
      } catch (err) { console.warn('Conversations:', err.message) }

      // Formulaires d'adoption non lus
      try {
        const aData = await adoptionAPI.getUnreadCountAdmin()
        setAdoptionBadge(aData.count || 0)
      } catch { /* silencieux */ }
    }
    loadAll()
    const pollInterval = setInterval(loadAll, 15000)
    return () => clearInterval(pollInterval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // ══════════════════════════════════════════════════════════════
  // TRANSPORT & TRACKING — logique identique à Air Transport Animal
  // ══════════════════════════════════════════════════════════════
  const API_URL_T = process.env.REACT_APP_API_URL || 'http://localhost:5000'

  // États
  const [trips,           setTrips]           = useState({})
  const [tripsProgress,   setTripsProgress]   = useState({})
  const [tripsSegments,   setTripsSegments]   = useState({})
  const [isSyncing,       setIsSyncing]       = useState(false)
  const [activeShipAdmin, setActiveShipAdmin] = useState(null)
  const [isPlayingAdmin,  setIsPlayingAdmin]  = useState(false)
  const [sharedProgressAdmin, setSharedProgressAdmin] = useState(0)
  const [opForm,          setOpForm]          = useState({
    name: '', species: 'Chien', owner: '',
    date: new Date().toISOString().slice(0, 10),
    start: '', startTime: '08:00', end: '', endTime: '18:00',
    userEmail: '',   // email du client auquel assigner le ticket
  })
  const [stopsArray,      setStopsArray]      = useState([])
  const [stopName,        setStopName]        = useState('')
  const [stopTime,        setStopTime]        = useState('12:00')
  const [generatedCode,   setGeneratedCode]   = useState('')
  const [showGenBox,      setShowGenBox]      = useState(false)
  const [opSearchCode,    setOpSearchCode]    = useState('')
  const [opSearchError,   setOpSearchError]   = useState('')
  const [opSearchResult,  setOpSearchResult]  = useState(null)
  const [isOpSearchPlaying, setIsOpSearchPlaying] = useState(false)
  const [opSearchProgress,  setOpSearchProgress]  = useState(0)
  const [adjOpSearchWps,  setAdjOpSearchWps]  = useState(null)
  const skipNextSaveRef  = useRef(false)
  const activeShipAdminRef = useRef(null)
  const tripsProgressRef = useRef({})
  const opSearchResultRef = useRef(null)
  const animAdminRef     = useRef(null)
  const opSearchRafRef   = useRef(null)
  const canvasAdminRef   = useRef(null)
  const canvasOpSearchRef = useRef(null)
  useEffect(() => { tripsProgressRef.current = tripsProgress }, [tripsProgress])
  useEffect(() => { activeShipAdminRef.current = activeShipAdmin }, [activeShipAdmin])
  useEffect(() => { opSearchResultRef.current = opSearchResult }, [opSearchResult])

  // ── Helpers ──────────────────────────────────────────────────
  const timeToMin = (t) => { if (!t) return 0; const [h, m] = t.split(':').map(Number); return h * 60 + m }
  const getNowMin = () => { const n = new Date(); return n.getHours() * 60 + n.getMinutes() + n.getSeconds() / 60 }
  const genId = () => 'ANI-' + Math.floor(1000 + Math.random() * 9000)
  const escHtml = (s) => s ? s.replace(/[&<>]/g, m => m === '&' ? '&amp;' : m === '<' ? '&lt;' : '&gt;') : ''

  // ── Calcul progression réelle (identique à TrackingService) ─
  const computeRealProgressAdmin = useCallback((tripId) => {
    const trip = trips[tripId] || (activeShipAdminRef.current?.id === tripId ? activeShipAdminRef.current : null) || (opSearchResultRef.current?.id === tripId ? opSearchResultRef.current : null)
    if (!trip?.waypoints || trip.waypoints.length < 2) return 0
    const meta = tripsProgressRef.current[tripId]
    if (!meta?.isPlaying) return Math.min(Math.max(meta?.progressAtStart ?? 0, 0), 1)
    const { progressAtStart = 0, pauseOffsetMinutes = 0 } = meta
    const wps = trip.waypoints
    const startMin = timeToMin(wps[0].time)
    let prevMin = startMin
    const timeline = wps.map((wp, i) => { let m = timeToMin(wp.time); if (i > 0 && m < prevMin) m += 1440; prevMin = m; return { ...wp, absMin: m } })
    const totalDur = timeline[timeline.length - 1].absMin - timeline[0].absMin
    if (totalDur <= 0) return progressAtStart
    let nowMin = getNowMin()
    if (nowMin < startMin && timeline[timeline.length - 1].absMin > 1440) nowMin += 1440
    const effNow = nowMin - pauseOffsetMinutes
    if (effNow <= timeline[0].absMin) return Math.max(progressAtStart, 0)
    if (effNow >= timeline[timeline.length - 1].absMin) return 1
    for (let i = 0; i < timeline.length - 1; i++) {
      if (effNow >= timeline[i].absMin && effNow < timeline[i + 1].absMin) {
        const segFrom = (timeline[i].absMin - timeline[0].absMin) / totalDur
        const segTo   = (timeline[i + 1].absMin - timeline[0].absMin) / totalDur
        const frac    = (effNow - timeline[i].absMin) / (timeline[i + 1].absMin - timeline[i].absMin)
        return Math.min(segFrom + (segTo - segFrom) * frac, 1)
      }
    }
    return 1
  }, [trips])

  // ── Waypoints ajustés (décalage pause) ───────────────────────
  const computeAdjWpsAdmin = useCallback((tripId) => {
    const trip = trips[tripId] || (activeShipAdminRef.current?.id === tripId ? activeShipAdminRef.current : null) || (opSearchResultRef.current?.id === tripId ? opSearchResultRef.current : null)
    if (!trip?.waypoints) return trip?.waypoints || []
    const offset = tripsProgressRef.current[tripId]?.pauseOffsetMinutes ?? 0
    if (!offset) return trip.waypoints
    return trip.waypoints.map(wp => {
      const adj = (timeToMin(wp.time) + offset) % 1440
      const h = Math.floor(adj / 60), m = Math.round(adj % 60)
      return { ...wp, time: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}` }
    })
  }, [trips])

  // ── API Backend ───────────────────────────────────────────────
  const saveToBackend = useCallback(async (tripsD, progD, segsD) => {
    try {
      await fetch(`${API_URL_T}/api/tracking/trips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ trips: tripsD, tripsProgress: progD, tripsSegments: segsD })
      })
    } catch (e) { console.error('Erreur sauvegarde:', e) }
  }, [API_URL_T])

  const updateProgressBackend = async (tripId, prog, playing, pauseOff) => {
    try {
      await fetch(`${API_URL_T}/api/tracking/trips/${tripId}/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ progress: prog, isPlaying: playing, startedAt: pauseOff, pauseOffsetMinutes: pauseOff })
      })
    } catch (e) { console.error('Erreur update progress:', e) }
  }

  const loadTrips = useCallback(async () => {
    setIsSyncing(true)
    try {
      const r = await fetch(`${API_URL_T}/api/tracking/trips`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      const d = await r.json()
      if (d?.trips) { setTrips(d.trips); setTripsProgress(d.tripsProgress || {}); setTripsSegments(d.tripsSegments || {}) }
    } catch (e) { showToast('Erreur chargement trajets') }
    finally { setIsSyncing(false) }
  }, [API_URL_T])

  const deleteTripAdmin = async (tripId) => {
    if (!window.confirm(`Supprimer définitivement le trajet ${tripId} ?`)) return
    skipNextSaveRef.current = true
    setTrips(prev => { const n = { ...prev }; delete n[tripId]; return n })
    setTripsProgress(prev => { const n = { ...prev }; delete n[tripId]; return n })
    setTripsSegments(prev => { const n = { ...prev }; delete n[tripId]; return n })
    if (activeShipAdmin?.id === tripId) { setActiveShipAdmin(null); setIsPlayingAdmin(false); setSharedProgressAdmin(0) }
    if (opSearchResult?.id === tripId) { setOpSearchResult(null); setOpSearchCode(''); setIsOpSearchPlaying(false) }
    try {
      await fetch(`${API_URL_T}/api/tracking/trips/${tripId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      showToast('Trajet supprimé')
    } catch (e) { showToast('Erreur suppression') }
  }

  // ── Sauvegarde automatique (identique à TrackingService) ─────
  useEffect(() => {
    const save = async () => {
      if (skipNextSaveRef.current) { skipNextSaveRef.current = false; return }
      if (Object.keys(trips).length > 0) await saveToBackend(trips, tripsProgress, tripsSegments)
    }
    const t = setTimeout(save, 1000)
    return () => clearTimeout(t)
  }, [trips, tripsProgress, tripsSegments, saveToBackend])

  // ── Polling sync toutes les 5s ────────────────────────────────
  useEffect(() => {
    if (view !== 'transport') return
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL_T}/api/tracking/trips`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        const d = await r.json()
        if (d?.tripsProgress) {
          setTripsProgress(prev => {
            const updated = { ...prev }
            Object.entries(d.tripsProgress).forEach(([id, srv]) => {
              const loc = prev[id] || {}
              updated[id] = { ...srv, pauseOffsetMinutes: srv.pauseOffsetMinutes ?? srv.startedAt ?? loc.pauseOffsetMinutes ?? 0, pausedAtMinutes: loc.pausedAtMinutes ?? null }
            })
            return updated
          })
        }
      } catch (e) {}
    }, 5000)
    return () => clearInterval(interval)
  }, [view, API_URL_T])

  // ── Boucle RAF panneau principal admin ───────────────────────
  useEffect(() => {
    if (!activeShipAdmin) { if (animAdminRef.current) cancelAnimationFrame(animAdminRef.current); return }
    const loop = () => {
      const ship = activeShipAdminRef.current
      if (!ship || !canvasAdminRef.current) return
      const prog = computeRealProgressAdmin(ship.id)
      const adjWps = computeAdjWpsAdmin(ship.id)
      setSharedProgressAdmin(prev => Math.abs(prog - prev) > 0.00001 ? prog : prev)
      const cv = canvasAdminRef.current
      if (cv?.parentElement) {
        const cw = cv.parentElement.clientWidth, ch = cv.parentElement.clientHeight
        if (cv.width !== cw || cv.height !== ch) { cv.width = cw; cv.height = ch }
        drawMapOnCanvas(cv, ship, prog, adjWps)
      }
      animAdminRef.current = requestAnimationFrame(loop)
    }
    animAdminRef.current = requestAnimationFrame(loop)
    return () => { if (animAdminRef.current) cancelAnimationFrame(animAdminRef.current) }
  }, [activeShipAdmin, computeRealProgressAdmin, computeAdjWpsAdmin])

  // ── Boucle RAF recherche opérateur ───────────────────────────
  useEffect(() => {
    if (!opSearchResult) { if (opSearchRafRef.current) cancelAnimationFrame(opSearchRafRef.current); return }
    const loop = () => {
      const ship = opSearchResultRef.current
      if (!ship || !canvasOpSearchRef.current) return
      const prog = computeRealProgressAdmin(ship.id)
      const adjWps = computeAdjWpsAdmin(ship.id)
      setOpSearchProgress(prog)
      setAdjOpSearchWps(adjWps)
      const cv = canvasOpSearchRef.current
      if (cv?.parentElement) {
        const cw = cv.parentElement.clientWidth, ch = cv.parentElement.clientHeight
        if (cv.width !== cw || cv.height !== ch) { cv.width = cw; cv.height = ch }
        drawMapOnCanvas(cv, ship, prog, adjWps)
      }
      opSearchRafRef.current = requestAnimationFrame(loop)
    }
    opSearchRafRef.current = requestAnimationFrame(loop)
    return () => { if (opSearchRafRef.current) cancelAnimationFrame(opSearchRafRef.current) }
  }, [opSearchResult, computeRealProgressAdmin, computeAdjWpsAdmin])

  // ── Étapes ───────────────────────────────────────────────────
  const addStop = () => {
    if (!stopName.trim()) { showToast("Entrez un nom pour l'étape"); return }
    setStopsArray(prev => [...prev, { name: stopName.trim(), time: stopTime, type: 'mid' }])
    setStopName(''); setStopTime('12:00')
  }
  const removeStop = (i) => setStopsArray(prev => prev.filter((_, j) => j !== i))

  // ── Générer un trajet (identique à generateTrip) ─────────────
  const generateTripAdmin = () => {
    if (!opForm.name.trim() || !opForm.start.trim() || !opForm.end.trim()) { showToast("Remplissez le nom, le départ et l'arrivée"); return }
    const id = genId()
    const allWps = [{ name: opForm.start, time: opForm.startTime, type: 'start' }, ...stopsArray, { name: opForm.end, time: opForm.endTime, type: 'end' }]
    const { waypointsWithProgress, totalDuration } = calculateWaypointTimelines(allWps)
    const segs = calculateSegmentSpeeds(waypointsWithProgress, totalDuration * 60)
    const newTrip = { id, animal: opForm.name, species: opForm.species, owner: opForm.owner || '—', date: opForm.date, waypoints: allWps, createdAt: new Date().toISOString(), startTime: opForm.startTime, endTime: opForm.endTime, totalDurationMinutes: totalDuration, waypointsWithProgress, userEmail: opForm.userEmail || undefined }
    const newProg = { progressAtStart: 0, isPlaying: false, startedAt: null, pauseOffsetMinutes: 0, pausedAtMinutes: null }
    setTrips(prev => ({ ...prev, [id]: newTrip }))
    setTripsSegments(prev => ({ ...prev, [id]: segs }))
    setTripsProgress(prev => ({ ...prev, [id]: newProg }))
    setGeneratedCode(id); setShowGenBox(true)
    setActiveShipAdmin(newTrip); setSharedProgressAdmin(0); setIsPlayingAdmin(false)
    setOpSearchResult(null); setOpSearchCode('')
    setOpForm({ name: '', species: 'Chien', owner: '', date: new Date().toISOString().slice(0, 10), start: '', startTime: '08:00', end: '', endTime: '18:00', userEmail: '' })
    setStopsArray([])
  }

  // ── Charger un trajet (identique à loadShip) ─────────────────
  const loadShipAdmin = (ship) => {
    if (animAdminRef.current) cancelAnimationFrame(animAdminRef.current)
    if (activeShipAdmin?.id === ship.id) { setActiveShipAdmin(null); setIsPlayingAdmin(false); setSharedProgressAdmin(0); return }
    setActiveShipAdmin(ship); setSharedProgressAdmin(computeRealProgressAdmin(ship.id)); setIsPlayingAdmin(false)
  }

  // ── Toggle play (identique à togglePlay) ─────────────────────
  const togglePlayAdmin = async () => {
    if (!activeShipAdmin) return
    const currentProg = computeRealProgressAdmin(activeShipAdmin.id)
    const prevMeta = tripsProgressRef.current[activeShipAdmin.id] || {}
    if (isPlayingAdmin) {
      const pausedAt = getNowMin()
      const newMeta = { progressAtStart: currentProg, isPlaying: false, startedAt: null, pausedAtMinutes: pausedAt, pauseOffsetMinutes: prevMeta.pauseOffsetMinutes || 0 }
      setIsPlayingAdmin(false); setSharedProgressAdmin(currentProg)
      setTripsProgress(prev => ({ ...prev, [activeShipAdmin.id]: newMeta }))
      await updateProgressBackend(activeShipAdmin.id, currentProg, false, prevMeta.pauseOffsetMinutes || 0)
    } else {
      const now = getNowMin()
      const pausedAt = prevMeta.pausedAtMinutes ?? now
      const totalOffset = (prevMeta.pauseOffsetMinutes || 0) + (now - pausedAt)
      const newMeta = { progressAtStart: currentProg, isPlaying: true, startedAt: now, pausedAtMinutes: null, pauseOffsetMinutes: totalOffset }
      setTripsProgress(prev => ({ ...prev, [activeShipAdmin.id]: newMeta }))
      setIsPlayingAdmin(true)
      await updateProgressBackend(activeShipAdmin.id, currentProg, true, totalOffset)
    }
  }

  const resetAnimAdmin = async () => {
    if (animAdminRef.current) cancelAnimationFrame(animAdminRef.current)
    setIsPlayingAdmin(false); setSharedProgressAdmin(0)
    if (activeShipAdmin) {
      const newMeta = { progressAtStart: 0, isPlaying: false, startedAt: null, pauseOffsetMinutes: 0, pausedAtMinutes: null }
      setTripsProgress(prev => ({ ...prev, [activeShipAdmin.id]: newMeta }))
      await updateProgressBackend(activeShipAdmin.id, 0, false, 0)
    }
  }

  // ── Toggle play recherche opérateur ──────────────────────────
  const toggleOpSearchPlay = async () => {
    if (!opSearchResult) return
    const currentProg = computeRealProgressAdmin(opSearchResult.id)
    const prevMeta = tripsProgressRef.current[opSearchResult.id] || {}
    if (isOpSearchPlaying) {
      const pausedAt = getNowMin()
      const newMeta = { progressAtStart: currentProg, isPlaying: false, startedAt: null, pausedAtMinutes: pausedAt, pauseOffsetMinutes: prevMeta.pauseOffsetMinutes || 0 }
      setIsOpSearchPlaying(false); setOpSearchProgress(currentProg)
      setTripsProgress(prev => ({ ...prev, [opSearchResult.id]: newMeta }))
      await updateProgressBackend(opSearchResult.id, currentProg, false, prevMeta.pauseOffsetMinutes || 0)
    } else {
      const now = getNowMin()
      const pausedAt = prevMeta.pausedAtMinutes ?? now
      const totalOffset = (prevMeta.pauseOffsetMinutes || 0) + (now - pausedAt)
      const newMeta = { progressAtStart: currentProg, isPlaying: true, startedAt: now, pausedAtMinutes: null, pauseOffsetMinutes: totalOffset }
      setTripsProgress(prev => ({ ...prev, [opSearchResult.id]: newMeta }))
      setIsOpSearchPlaying(true)
      await updateProgressBackend(opSearchResult.id, currentProg, true, totalOffset)
    }
  }

  const resetOpSearchAnim = async () => {
    setIsOpSearchPlaying(false); setOpSearchProgress(0)
    if (opSearchResult) {
      const newMeta = { progressAtStart: 0, isPlaying: false, startedAt: null, pauseOffsetMinutes: 0, pausedAtMinutes: null }
      setTripsProgress(prev => ({ ...prev, [opSearchResult.id]: newMeta }))
      await updateProgressBackend(opSearchResult.id, 0, false, 0)
    }
  }

  // ── Recherche opérateur ───────────────────────────────────────
  const doOpSearch = async () => {
    const code = opSearchCode.trim().toUpperCase()
    if (!code) { setOpSearchError('Entrez un numéro de suivi'); setOpSearchResult(null); return }
    let found = trips[code]
    if (!found) {
      try {
        const r = await fetch(`${API_URL_T}/api/tracking/trips`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
        if (r.ok) { const d = await r.json(); if (d.trips) { setTrips(d.trips); setTripsProgress(d.tripsProgress || {}); setTripsSegments(d.tripsSegments || {}); found = d.trips[code] } }
      } catch (e) {}
    }
    if (!found) { setOpSearchError(`Numéro "${code}" introuvable.`); setOpSearchResult(null); return }
    setOpSearchError(''); setOpSearchResult(found); setOpSearchProgress(computeRealProgressAdmin(found.id)); setIsOpSearchPlaying(false)
  }

  const isTripPlayingAdmin = (tripId) => { const m = tripsProgress[tripId]; return m && m.isPlaying === true }

  const formatDurAdmin = (min) => { if (!min) return ''; const h = Math.floor(min / 60), m = min % 60; return h > 0 ? `${h}h${m > 0 ? ` ${m}min` : ''}` : `${m}min` }

  // ── Charger les données au montage ───────────────────────────
  // (appelé aussi depuis le nav click)


  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500) }

  // ── Recherche utilisateur par nom ou email (livraison & facture) ──
  const _searchUsers = (query, setMatches) => {
    if (!query || query.trim().length < 2) { setMatches([]); return }
    const q = query.toLowerCase()
    const results = users
      .filter(u => u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
      .slice(0, 6)
      .map(u => ({ id: u.id, name: u.name, email: u.email }))
    setMatches(results)
  }

  // ── Formulaires d'adoption ────────────────────────────────────────────────

  const loadDeliveryData = async () => {
    setDeliveryLoading(true)
    try {
      const [fD, iD, cs, av, id_, ac] = await Promise.all([
        deliveryAPI.getForms(),
        deliveryAPI.getInvoices(),
        animalDocsAPI.getAll('carnet'),
        animalDocsAPI.getAll('attestation'),
        animalDocsAPI.getAll('identification'),
        animalDocsAPI.getAll('cession'),
      ])
      setLivraisonForms(fD.forms   || [])
      setFacturesForms(iD.invoices || [])
      setCarnetForms(cs.docs   || [])
      setAttestForms(av.docs   || [])
      setIdentifForms(id_.docs || [])
      setCessionForms(ac.docs  || [])
    } catch (e) { console.error('delivery:', e) }
    finally { setDeliveryLoading(false) }
  }

  // ═══════════════════════════════════════════════════════════════
  // HELPERS PDF PARTAGÉS — même header/footer que livraison
  // ═══════════════════════════════════════════════════════════════
  const _pdfHeader = async (doc, W, M, N, G, WH, hH) => {
    const fc=(...c)=>doc.setFillColor(...c)
    const tc=(...c)=>doc.setTextColor(...c)
    const txt=(s,x,y,o={})=>doc.text(String(s==null||s===''?'\u2014':s),x,y,o)
    const logo=await _getLogoDataUrl()
    const lZW=52,lS=30
    fc(...WH);doc.rect(0,0,lZW,hH,'F');fc(...N);doc.rect(lZW,0,W-lZW,hH,'F')
    fc(...G);doc.rect(lZW-1.5,0,2,hH,'F');fc(...G);doc.rect(0,hH,W,1,'F')
    if(logo){try{doc.addImage(logo,'JPEG',(lZW-lS)/2,(hH-lS)/2,lS,lS)}catch(e){}}
    tc(...WH);doc.setFontSize(12);doc.setFont('helvetica','bold');txt('ANIMAUX POUR ADOPTION',W-M,11,{align:'right'})
    tc(...G);doc.setFontSize(7.5);doc.setFont('helvetica','bold');txt('DE LA FRANCE',W-M,18,{align:'right'})
    tc(180,200,230);doc.setFontSize(6);doc.setFont('helvetica','normal')
    txt('123 Rue des Compagnons - 75001 Paris, France',W-M,25,{align:'right'})
    txt('T\u00e9l: +33 07 57 75 00 92  |  Email: animauxdecompagnie@animauxpouradoptiondelafrance.com',W-M,31,{align:'right'})
  }

  // eslint-disable-next-line no-unused-vars
  const _pdfSigBlock = (doc, y, W, M, N, G, WH, BD, label1, sig1, date1, label2, sig2, date2) => {
    const fc=(...c)=>doc.setFillColor(...c);const tc=(...c)=>doc.setTextColor(...c)
    const dc=(...c)=>doc.setDrawColor(...c);const lw=w=>doc.setLineWidth(w)
    const txt=(s,x,y2,o={})=>doc.text(String(s==null||s===''?'\u2014':s),x,y2,o)
    const sigGap=4,sigColW=(W-M*2-sigGap)/2,sigTH=7,sigBH=28
    ;[[label1,sig1,date1],[label2,sig2,date2]].forEach(([label,sigData,signedAt],i)=>{
      const sx=M+i*(sigColW+sigGap)
      fc(...N);doc.rect(sx,y,sigColW,sigTH,'F')
      tc(...WH);doc.setFontSize(6.5);doc.setFont('helvetica','bold');txt(label,sx+sigColW/2,y+4.8,{align:'center'})
      fc(...WH);doc.rect(sx,y+sigTH,sigColW,sigBH,'F');dc(...BD);lw(0.3);doc.rect(sx,y+sigTH,sigColW,sigBH);dc(...N);lw(0.4);doc.rect(sx,y,sigColW,sigTH)
      if(sigData){try{doc.addImage(sigData,'PNG',sx+4,y+sigTH+3,sigColW-8,sigBH-8)}catch(e){};if(signedAt){tc(100,115,140);doc.setFontSize(5.5);doc.setFont('helvetica','normal');txt(new Date(signedAt).toLocaleDateString('fr-FR'),sx+sigColW/2,y+sigTH+sigBH-3,{align:'center'})}}
      else{tc(160,170,190);doc.setFontSize(6.5);doc.setFont('helvetica','normal');txt('En attente de signature',sx+sigColW/2,y+sigTH+sigBH/2,{align:'center'})}
    })
    return y+sigTH+sigBH+3
  }

  const _pdfFooter = (doc, H, W, N, G, WH, text1, text2) => {
    const fc=(...c)=>doc.setFillColor(...c);const tc=(...c)=>doc.setTextColor(...c)
    fc(...G);doc.rect(0,H-22,W,1,'F');fc(...N);doc.rect(0,H-21,W,21,'F')
    tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold');doc.text(text1,W/2,H-13,{align:'center'})
    tc(160,175,200);doc.setFontSize(6);doc.setFont('helvetica','normal');doc.text(text2,W/2,H-5,{align:'center'})
  }

  // eslint-disable-next-line no-unused-vars
  const _drawBlk = (doc,bx,bw,by,title,rows,N,G,WH,BD) => {
    const ROW=5.8,TH=7,PAD=5,bh=TH+rows.length*ROW+PAD
    const fc=(...c)=>doc.setFillColor(...c);const tc=(...c)=>doc.setTextColor(...c)
    const dc=(...c)=>doc.setDrawColor(...c);const lw=w=>doc.setLineWidth(w)
    const txt=(s,x,y,o={})=>doc.text(String(s==null||s===''?'\u2014':s),x,y,o)
    fc(...N);doc.rect(bx,by,bw,TH,'F');tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold');txt(title,bx+bw/2,by+4.8,{align:'center'})
    fc(...WH);doc.rect(bx,by+TH,bw,bh-TH,'F');dc(...BD);lw(0.3);doc.rect(bx,by+TH,bw,bh-TH);dc(...N);lw(0.4);doc.rect(bx,by,bw,bh)
    let ry=by+TH+5.5
    rows.forEach(([label,val])=>{
      tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','bold');const lbl=label+' :';doc.text(lbl,bx+5,ry);const lw2=doc.getTextWidth(lbl)
      tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','normal');const vs=String(val||'\u2014').slice(0,32);txt(vs,bx+5+lw2+2,ry)
      const sx=bx+5+lw2+3+doc.getTextWidth(vs);if(sx<bx+bw-4){dc(...BD);doc.setLineWidth(0.25);doc.setLineDashPattern([0.8,0.8],0);doc.line(sx,ry-0.5,bx+bw-4,ry-0.5);doc.setLineDashPattern([],0)}
      ry+=ROW
    });return bh
  }

  // ── PDF Carnet de santé ────────────────────────────────────────────────────
  const generateCarnetSantePDF = async (d, vaccins, antiparas, sig, photo, cachet) => {
    const doc = new jsPDF({ unit:'mm', format:'a4' })
    const W=210, H=297, M=12, CW=W-M*2
    const NV=[30,58,120], GD=[217,174,92], WH=[255,255,255], BD=[203,213,225]

    const safe=s=>{if(s==null||s==='')return '';return String(s).replace(/[\u2018\u2019]/g,"'").replace(/[\u201C\u201D]/g,'"').replace(/\u2014/g,'-').replace(/\u2013/g,'-').replace(/[^\u0000-\u00FF]/g,'')}
    const t=(s,x,y2,o={})=>doc.text(safe(String(s==null?'':s))||' ',x,y2,o)
    const fmt=dt=>{if(!dt)return '';try{return new Date(dt).toLocaleDateString('fr-FR')}catch(e){return String(dt)}}
    const docRef=d.docNumber||('CS-'+new Date().getFullYear()+'-'+(d.name||'ANI').replace(/\s+/g,'').slice(0,6).toUpperCase())

    const hH=42

    const secBand=(label,x,yy,w,h=7)=>{
      doc.setFillColor(...NV);doc.rect(x,yy,w,h,'F')
      doc.setTextColor(...WH);doc.setFontSize(8.5);doc.setFont('helvetica','bold')
      t(label,x+4,yy+h-1.5)
    }

    const fld=(lbl,val,x,yy,w)=>{
      doc.setTextColor(...NV);doc.setFontSize(6.5);doc.setFont('helvetica','bold');t(lbl,x,yy)
      doc.setTextColor(0,0,0);doc.setFontSize(9);doc.setFont('helvetica','normal');t(safe(String(val||'')).slice(0,Math.floor(w/1.5)),x,yy+7)
      doc.setDrawColor(...BD);doc.setLineWidth(0.2);doc.line(x,yy+9.5,x+w,yy+9.5)
    }

    const PAGE_BOTTOM=H-12
    const drawFooterFn=()=>{
      doc.setFillColor(...NV);doc.rect(0,H-10,W,10,'F')
      doc.setFillColor(...GD);doc.rect(0,H-10,W,0.8,'F')
      doc.setTextColor(180,205,235);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
      t('Animaux pour Adoption de la France  -  Carnet de Sante  -  Document officiel a conserver',W/2,H-4,{align:'center'})
      doc.setTextColor(...GD);doc.setFontSize(6.5);doc.setFont('helvetica','bold');t(docRef,W-M,H-4,{align:'right'})
    }
    const drawContHeader=()=>{
      doc.setFillColor(...NV);doc.rect(0,0,W,11,'F')
      doc.setFillColor(...GD);doc.rect(0,11,W,0.6,'F')
      doc.setTextColor(...GD);doc.setFontSize(7.5);doc.setFont('helvetica','bold');t('CARNET DE SANTE - Suite',M,7.5)
      doc.setTextColor(180,205,235);doc.setFontSize(6.5);doc.setFont('helvetica','normal');t(docRef,W-M,7.5,{align:'right'})
    }
    const chkPage=(need)=>{if(y+need>PAGE_BOTTOM){drawFooterFn();doc.addPage();drawContHeader();y=16}}

    let y=0

    // ── HEADER ──────────────────────────────────────────────────────────────
    doc.setFillColor(...NV);doc.rect(0,0,W,hH,'F')
    doc.setFillColor(...GD);doc.rect(0,0,2,hH,'F')
    doc.setTextColor(180,205,235);doc.setFontSize(8);doc.setFont('helvetica','normal');t('LE CARNET',W/2,13,{align:'center'})
    doc.setTextColor(255,255,255);doc.setFontSize(20);doc.setFont('helvetica','bold');t('DE SANTE',W/2,28,{align:'center'})
    doc.setFillColor(...GD);doc.rect(W/2-40,32,80,0.7,'F')
    doc.setFontSize(8);doc.setFont('helvetica','bold') // set font before measuring width
    const badgeText=docRef, badgeWidth=doc.getTextWidth(badgeText)+10
    try{doc.setFillColor(...GD);doc.roundedRect(W-M-badgeWidth,11,badgeWidth,9,2,2,'F')}catch(e){doc.setFillColor(...GD);doc.rect(W-M-badgeWidth,11,badgeWidth,9,'F')}
    doc.setTextColor(...NV);t(badgeText,W-M-badgeWidth/2,17.5,{align:'center'})
    doc.setFillColor(...GD);doc.rect(0,hH,W,1,'F')
    y=hH

    // ── BANDE NOM ───────────────────────────────────────────────────────────
    doc.setFillColor(252,246,226);doc.rect(0,y,W,10,'F')
    const nlb='DE:', nVal=safe(d.name||'').toUpperCase()
    doc.setFontSize(7.5);doc.setFont('helvetica','bold');doc.setTextColor(...GD)
    const nlbW=doc.getTextWidth(nlb)
    doc.setFontSize(12);doc.setFont('helvetica','bold');doc.setTextColor(...NV)
    const nValW=doc.getTextWidth(nVal)
    const bStart=W/2-(nlbW+5+nValW)/2
    doc.setFontSize(7.5);doc.setFont('helvetica','bold');doc.setTextColor(...GD);t(nlb,bStart,y+7)
    doc.setFontSize(12);doc.setFont('helvetica','bold');doc.setTextColor(...NV);t(nVal,bStart+nlbW+5,y+7)
    doc.setFillColor(...GD);doc.rect(0,y+10,W,0.5,'F')
    y+=11

    // ── IDENTITE DE L'ANIMAL ─────────────────────────────────────────────────
    secBand('IDENTITE DE L\'ANIMAL',M,y,CW);y+=7
    const phW=46, phH=62
    const iX=M+phW+4, iW=CW-phW-4, hfw=(iW-6)/2
    const specStr=safe(d.species||'Animal').toUpperCase()+(d.breed?' - '+safe(d.breed).toUpperCase():'')+(d.age?' ['+safe(d.age)+']':'')
    // Photo and gold bar start at same y; bar is right-column only
    if(photo){try{doc.addImage(photo,'JPEG',M,y,phW,phH)}catch(e){try{doc.addImage(photo,'PNG',M,y,phW,phH)}catch(e2){}}}
    else{
      doc.setFillColor(241,245,249);doc.rect(M,y,phW,phH,'F')
      doc.setDrawColor(...GD);doc.setLineWidth(0.5);doc.setLineDashPattern([2,1.5],0);doc.rect(M,y,phW,phH);doc.setLineDashPattern([],0)
      doc.setTextColor(...NV);doc.setFontSize(8);doc.setFont('helvetica','normal');t('PHOTO',M+phW/2,y+phH/2-4,{align:'center'});t('ANIMAL',M+phW/2,y+phH/2+4,{align:'center'})
    }
    doc.setFillColor(...GD);doc.rect(iX,y,iW,7.5,'F')
    doc.setTextColor(...NV);doc.setFontSize(8.5);doc.setFont('helvetica','bold');t(specStr,iX+4,y+5.5)
    let gy=y+7.5+3
    fld('Nom',safe(d.name||''),iX,gy,hfw);fld('Espece',safe(d.species||''),iX+hfw+6,gy,hfw);gy+=14
    fld('Race',safe(d.breed||''),iX,gy,hfw);fld('Age',safe(d.age||''),iX+hfw+6,gy,hfw);gy+=14
    fld('Sexe',safe(d.sex||''),iX,gy,hfw);fld('Sterilise(e)',safe(d.sterilise||''),iX+hfw+6,gy,hfw);gy+=14
    fld('Poids (kg)',safe(d.poids?String(d.poids)+' kg':''),iX,gy,hfw);fld('N\u00b0 Identification / Puce',safe(d.identification||''),iX+hfw+6,gy,hfw)
    y+=phH+5

    // ── PROPRIETAIRE ────────────────────────────────────────────────────────
    chkPage(60)
    secBand('PROPRIETAIRE',M,y,CW);y+=7
    doc.setFillColor(248,250,252);doc.rect(M,y,CW,50,'F')
    const pw2=(CW-8)/2
    fld('Nom du proprietaire',safe(d.proprietaire_nom||''),M+4,y+4,pw2);fld('Telephone',safe(d.proprietaire_tel||''),M+pw2+8,y+4,pw2)
    fld('Email',safe(d.proprietaire_email||''),M+4,y+19,pw2);fld('Adresse',safe(d.proprietaire_adresse||''),M+pw2+8,y+19,pw2)
    fld('Ville',safe(d.proprietaire_ville||''),M+4,y+34,pw2);fld('Code postal',safe(d.proprietaire_cp||''),M+pw2+8,y+34,pw2)
    y+=55

    // ── CABINET VETERINAIRE ─────────────────────────────────────────────────
    chkPage(58)
    secBand('CABINET VETERINAIRE',M,y,CW);y+=7
    doc.setFillColor(248,250,252);doc.rect(M,y,CW,48,'F')
    fld('Nom du cabinet / Veterinaire traitant',safe((d.veterinaire_cabinet||'')+(d.attestation_veterinaire?' - Dr. '+d.attestation_veterinaire:'')+(d.attestation_ordre?' (Ordre N\u00b0 '+d.attestation_ordre+')':'')),M+4,y+4,CW-8)
    const vc2=(CW-8)/2
    fld('Telephone',safe(d.veterinaire_tel||''),M+4,y+19,vc2);fld('Email',safe(d.veterinaire_email||''),M+vc2+8,y+19,vc2)
    fld('Adresse',safe(d.veterinaire_adresse||''),M+4,y+34,CW-8)
    y+=52

    // ── CARNET DE VACCINATIONS ───────────────────────────────────────────────
    if(vaccins&&vaccins.length>0){
      chkPage(24+vaccins.length*8)
      const hdrs=['VACCIN','DATE','RAPPEL','N\u00b0 LOT','STATUT']
      const cols=[54,28,28,38,CW-148]
      const RH=8,THt=7,HH=6.5,totalH=THt+HH+vaccins.length*RH+2
      secBand('CARNET DE VACCINATIONS',M,y,CW)
      doc.setFillColor(255,255,255);doc.rect(M,y+THt,CW,totalH-THt,'F')
      let ty=y+THt
      doc.setFillColor(232,237,248);doc.rect(M,ty,CW,HH,'F')
      doc.setDrawColor(...BD);doc.setLineWidth(0.3);doc.line(M,ty+HH,M+CW,ty+HH)
      doc.setTextColor(...NV);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
      let cx=M+3;hdrs.forEach((h,i)=>{t(h,cx,ty+4.5);cx+=cols[i]});ty+=HH
      vaccins.forEach((v,ri)=>{
        doc.setFillColor(ri%2===0?255:248,ri%2===0?255:250,ri%2===0?255:252);doc.rect(M,ty,CW,RH,'F')
        if(ri<vaccins.length-1){doc.setDrawColor(...BD);doc.setLineWidth(0.15);doc.line(M,ty+RH,M+CW,ty+RH)}
        doc.setTextColor(30,41,59);doc.setFontSize(8);doc.setFont('helvetica','normal')
        const ok=v.rappel&&new Date(v.rappel)>new Date()
        const row=[safe(v.type||''),fmt(v.date),fmt(v.rappel),safe(v.lot||''),v.rappel?null:'']
        let cx2=M+3
        row.forEach((cell,ci)=>{
          if(hdrs[ci]==='STATUT'&&v.rappel){
            doc.setFillColor(ok?220:254,ok?252:230,ok?220:210);doc.rect(cx2,ty+1.5,cols[ci]-4,RH-3,'F')
            doc.setTextColor(ok?22:150,ok?120:30,ok?60:40);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
            t(ok?'A jour':'Rappel proche',cx2+(cols[ci]-4)/2,ty+5.5,{align:'center'})
            doc.setTextColor(30,41,59);doc.setFont('helvetica','normal');doc.setFontSize(8)
          }else{t(safe(String(cell||'')).slice(0,26),cx2,ty+5.5)}
          cx2+=cols[ci]
        });ty+=RH
      })
      y+=totalH+5
    }

    // ── TRAITEMENTS ANTIPARASITAIRES ─────────────────────────────────────────
    if(antiparas&&antiparas.length>0){
      chkPage(24+antiparas.length*8)
      const hdrs2=['TYPE','PRODUIT','DATE','PROCHAINE ECHEANCE']
      const cols2=[34,64,34,CW-132]
      const RH2=8,THt2=7,HH2=6.5,totalH2=THt2+HH2+antiparas.length*RH2+2
      secBand('TRAITEMENTS ANTIPARASITAIRES',M,y,CW)
      doc.setFillColor(255,255,255);doc.rect(M,y+THt2,CW,totalH2-THt2,'F')
      let ty2=y+THt2
      doc.setFillColor(232,237,248);doc.rect(M,ty2,CW,HH2,'F')
      doc.setDrawColor(...BD);doc.setLineWidth(0.3);doc.line(M,ty2+HH2,M+CW,ty2+HH2)
      doc.setTextColor(...NV);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
      let cx3=M+3;hdrs2.forEach((h,i)=>{t(h,cx3,ty2+4.5);cx3+=cols2[i]});ty2+=HH2
      antiparas.forEach((a,ri)=>{
        doc.setFillColor(ri%2===0?255:248,ri%2===0?255:250,ri%2===0?255:252);doc.rect(M,ty2,CW,RH2,'F')
        if(ri<antiparas.length-1){doc.setDrawColor(...BD);doc.setLineWidth(0.15);doc.line(M,ty2+RH2,M+CW,ty2+RH2)}
        doc.setTextColor(30,41,59);doc.setFontSize(8);doc.setFont('helvetica','normal')
        const row2=[safe(a.type||''),safe(a.produit||''),fmt(a.date),fmt(a.prochaine_date)]
        let cx4=M+3;row2.forEach((cell,ci)=>{t(safe(String(cell||'')).slice(0,30),cx4,ty2+5.5);cx4+=cols2[ci]});ty2+=RH2
      })
      y+=totalH2+5
    }

    // ── EXAMEN CLINIQUE - ATTESTATION VETERINAIRE ────────────────────────────
    chkPage(50)
    secBand('EXAMEN CLINIQUE - ATTESTATION VETERINAIRE',M,y,CW);y+=7
    const exH=36
    doc.setFillColor(255,255,255);doc.rect(M,y,CW,exH,'F')
    const ec1w=(CW-6)/3, ec2w=(CW-6)/3, ec3w=CW-6-ec1w-ec2w
    const ec1x=M+3, ec2x=ec1x+ec1w+3, ec3x=ec2x+ec2w+3
    doc.setDrawColor(...BD);doc.setLineWidth(0.2)
    doc.line(ec2x-1.5,y,ec2x-1.5,y+exH);doc.line(ec3x-1.5,y,ec3x-1.5,y+exH)
    fld('Date de l\'examen',fmt(d.attestation_date),ec1x,y+3,ec1w)
    fld('Validite',safe(d.attestation_validite||''),ec1x,y+18,ec1w)
    fld('Veterinaire',safe(d.attestation_veterinaire?'Dr. '+d.attestation_veterinaire:''),ec2x,y+3,ec2w)
    fld('N\u00b0 Ordre',safe(d.attestation_ordre||''),ec2x,y+18,ec2w)
    doc.setTextColor(...NV);doc.setFontSize(6.5);doc.setFont('helvetica','bold');t('Observations medicales',ec3x,y+5)
    doc.setTextColor(0,0,0);doc.setFontSize(8.5);doc.setFont('helvetica','italic')
    const obsLines=doc.splitTextToSize(safe(d.attestation_observations||''),ec3w);doc.text(obsLines.slice(0,3),ec3x,y+13)
    y+=exH+5

    // ── SIGNATURES & CACHET ──────────────────────────────────────────────────
    chkPage(50)
    secBand('SIGNATURES & CACHET',M,y,CW);y+=7
    const sigH=36, sigW=(CW-6)/2, cX=M+sigW+6
    doc.setFillColor(250,251,253);doc.rect(M,y,sigW,sigH,'F')
    doc.setFillColor(250,251,253);doc.rect(cX,y,sigW,sigH,'F')
    doc.setTextColor(...NV);doc.setFontSize(7);doc.setFont('helvetica','bold');t('SIGNATURE DU VETERINAIRE',M+sigW/2,y+6,{align:'center'})
    doc.setDrawColor(...GD);doc.setLineWidth(0.5);doc.setLineDashPattern([2,1.5],0);doc.rect(M+6,y+9,sigW-12,sigH-18);doc.setLineDashPattern([],0)
    if(sig){try{doc.addImage(sig,'PNG',M+8,y+11,sigW-16,sigH-22)}catch(e){}}
    doc.setDrawColor(...BD);doc.setLineWidth(0.3);doc.line(M+6,y+sigH-7,M+sigW-6,y+sigH-7)
    doc.setTextColor(100,116,139);doc.setFontSize(6.5);doc.setFont('helvetica','italic')
    t(safe(d.attestation_veterinaire?'Dr. '+d.attestation_veterinaire+' - Veterinaire':'Veterinaire'),M+sigW/2,y+sigH-3,{align:'center'})
    doc.setTextColor(...NV);doc.setFontSize(7);doc.setFont('helvetica','bold');t('CACHET OFFICIEL',cX+sigW/2,y+6,{align:'center'})
    const cachetS=24
    if(cachet){try{doc.addImage(cachet,'PNG',cX+sigW/2-cachetS/2,y+10,cachetS,cachetS)}catch(e){}}
    else{doc.setDrawColor(...GD);doc.setLineWidth(0.5);doc.setLineDashPattern([2,1.5],0);doc.circle(cX+sigW/2,y+20,12,'S');doc.setLineDashPattern([],0)}
    doc.setTextColor(150,165,185);doc.setFontSize(6.5);doc.setFont('helvetica','italic');t('Cachet du cabinet',cX+sigW/2,y+sigH-3,{align:'center'})
    y+=sigH+5

    drawFooterFn()
    doc.save('carnet-sante-'+safe(d.name||'animal').replace(/\s+/g,'-')+'.pdf')
  }


  const generateAttestationPDF = async (d, sig, stamp) => {
    const doc = new jsPDF({ unit:'mm', format:'a4' })
    const W=210,H=297,M=20,hH=38,N=[30,58,120],G=[217,174,92],WH=[255,255,255]
    const DARK=[44,62,80],GREY=[108,117,125],NAVY=N
    const tc=(...c)=>doc.setTextColor(...c);const txt=(s,x,y,o={})=>doc.text(String(s==null||s===''?'\u2014':s),x,y,o)
    await _pdfHeader(doc,W,M,N,G,WH,hH)
    let y=hH+18
    tc(...NAVY);doc.setFontSize(20);doc.setFont('helvetica','bold');txt('ATTESTATION V\u00c9T\u00c9RINAIRE',W/2,y,{align:'center'});y+=14
    // Intro
    tc(...DARK);doc.setFontSize(10);doc.setFont('helvetica','normal')
    const vetNom=d.vetNom||'[Nom et pr\u00e9nom]';const ordre=d.vetNumOrdre||'[N\u00b0 ordre]'
    txt('Je soussign\u00e9(e), Dr. ',M,y);const w1=doc.getTextWidth('Je soussign\u00e9(e), Dr. ')
    doc.setFont('helvetica','bold');txt(vetNom,M+w1,y);const w2=doc.getTextWidth(vetNom)
    doc.setFont('helvetica','normal');txt(', v\u00e9t\u00e9rinaire inscrit(e) \u00e0 l\u2019Ordre National des V\u00e9t\u00e9rinaires',M+w1+w2,y);y+=6
    txt('sous le num\u00e9ro ',M,y);const w3=doc.getTextWidth('sous le num\u00e9ro ')
    doc.setFont('helvetica','bold');txt(ordre+',',M+w3,y);const w4=doc.getTextWidth(ordre+',')
    doc.setFont('helvetica','normal');txt(' certifie avoir examin\u00e9 ce jour l\u2019animal ci-apr\u00e8s :',M+w3+w4,y);y+=12
    // Animal fields
    const aFields=[["Nom de l\u2019animal",d.animalNom||'[Nom]'],['Esp\u00e8ce',d.animalEspece||'[Esp\u00e8ce]'],['Race',d.animalRace||'[Race]'],['Sexe',d.animalSexe||'[Sexe]'],['Couleur',d.animalCouleur||'[Couleur]'],['Puc\u00e9',d.animalPuce||'[Oui/Non]'],["Propri\u00e9taire de l\u2019animal",d.proprietaireNom||'[Nom propri\u00e9taire]'],['Identification',d.animalId||'[N\u00b0 puce/tatouage]']]
    aFields.forEach(([label,val])=>{
      doc.setFont('helvetica','bold');tc(...NAVY);doc.setFontSize(10);const lbl=label+' : ';txt(lbl,M+8,y);const lw2=doc.getTextWidth(lbl)
      doc.setFont('helvetica','normal');tc(...DARK);txt(val,M+8+lw2,y);y+=7
    });y+=4
    // Attestations
    tc(...DARK);doc.setFontSize(10);doc.setFont('helvetica','normal');txt("Apr\u00e8s examen clinique, j\u2019atteste que l\u2019animal est :",M,y);y+=8
    ;['En bonne sant\u00e9 g\u00e9n\u00e9rale','\u00c0 jour de ses vaccinations obligatoires',"Ne pr\u00e9sente aucun signe de maladie contagieuse au moment de l\u2019examen",'Apte au transport auquel il est destin\u00e9'].forEach((label)=>{
      // Puce carrée remplie (fiable cross-version jsPDF)
      doc.setFillColor(30,58,120);doc.rect(M+8,y-2.8,2.2,2.2,'F')
      tc(...DARK);doc.setFontSize(10);doc.setFont('helvetica','normal')
      txt(label,M+13,y);y+=7
    });y+=8
    tc(...DARK);doc.setFontSize(10);doc.setFont('helvetica','normal');txt('Fait pour servir et valoir ce que de droit.',M,y);y+=16
    // ── Deux colonnes : gauche = lieu/cachet, droite = label signature/image ──
    const dateStr=d.date?new Date(d.date).toLocaleDateString('fr-FR'):new Date().toLocaleDateString('fr-FR')
    const halfW=(W-M*2)/2   // largeur de chaque colonne = 85mm
    const colL=M            // colonne gauche : M → M+halfW  (centre = M+halfW/2)
    const colR=M+halfW      // colonne droite : M+halfW → W-M (centre = M+halfW+halfW/2)
    const cxL=colL+halfW/2  // centre colonne gauche
    const cxR=colR+halfW/2  // centre colonne droite
    // Labels sur la même ligne
    tc(...NAVY);doc.setFont('helvetica','bold');doc.setFontSize(10)
    txt('Fait \u00e0 '+(d.ville||'[Ville]')+', le '+dateStr,cxL,y,{align:'center'})
    tc(...GREY);doc.setFont('helvetica','italic')
    txt('Signature du v\u00e9t\u00e9rinaire',cxR,y,{align:'center'});y+=8
    // Cachet centré sous "Fait à..." dans la colonne gauche
    const stampS=32
    if(stamp){try{doc.addImage(stamp,'PNG',cxL-stampS/2,y,stampS,stampS)}catch(e){}}
    // Signature centrée sous "Signature du vétérinaire" dans la colonne droite
    const sigW=55,sigH=24
    if(sig){try{doc.addImage(sig,'PNG',cxR-sigW/2,y+2,sigW,sigH)}catch(e){}}
    else{doc.setDrawColor(180,180,200);doc.setLineWidth(0.3);doc.line(cxR-sigW/2,y+sigH,cxR+sigW/2,y+sigH)}
    _pdfFooter(doc,H,W,N,G,WH,'Attestation v\u00e9t\u00e9rinaire \u2014 Animaux pour Adoption de la France','Document officiel \u2014 \u00e0 conserver avec l\u2019animal')
    doc.save('attestation-vet-'+(d.animalNom||'animal').replace(/\s+/g,'-')+'.pdf')
  }

  // ── PDF Document d'identification (style I-CAD) ────────────────────────────
  const generateIdentificationPDF = async (d) => {
    const doc = new jsPDF({ unit:'mm', format:'a4' })
    const W=210, H=297, M=13
    const CW=W-M*2  // 184mm

    const safe=s=>{
      if(s==null||s==='')return ''
      return String(s).replace(/[\u2018\u2019\u2032]/g,"'")
        .replace(/[\u201C\u201D]/g,'"').replace(/\u2014/g,'-')
        .replace(/\u2013/g,'-').replace(/[^\u0000-\u00FF]/g,'')
    }
    const t=(s,x,y2,o={})=>doc.text(safe(String(s==null?'':s))||' ',x,y2,o)
    const fmt=dt=>{if(!dt)return '';try{return new Date(dt).toLocaleDateString('fr-FR')}catch(e){return String(dt)}}

    let y=0

    // ── HEADER #1e3a7a fond plein ──────────────────────────────
    doc.setFillColor(30,58,122)
    doc.rect(0,0,W,32,'F')
    doc.setTextColor(255,255,255)
    doc.setFontSize(11);doc.setFont('helvetica','bold')
    t('SOCIETE D\'IDENTIFICATION DES CARNIVORES DOMESTIQUES',W/2,9,{align:'center'})
    doc.setFontSize(7.5);doc.setFont('helvetica','normal')
    t('112-114 Avenue Gabriel Peri  -  94246 L\'HAY LES ROSES Cedex',W/2,15,{align:'center'})
    doc.setFontSize(9.5);doc.setFont('helvetica','bold')
    t('0 810 778 778',W/2,21.5,{align:'center'})
    doc.setFontSize(7);doc.setFont('helvetica','normal')
    doc.setTextColor(180,200,235)
    const nowStr=new Date().toLocaleDateString('fr-FR')+' '+new Date().toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})
    t(nowStr,W/2,27.5,{align:'center'})
    y=32+7  // espace sous header (Madame section)

    // ── INTRO ─────────────────────────────────────────────────
    doc.setTextColor(71,85,105)
    doc.setFontSize(8.5);doc.setFont('helvetica','normal')
    t('Madame, Monsieur,',M,y);y+=4.5
    const intro='Nous avons le plaisir de vous adresser la carte d\'identification de votre animal suite a l\'enregistrement de son identification et de vos coordonnees dans le Fichier National des Carnivores Domestiques (chiens, chats, furets).'
    const introLines=doc.splitTextToSize(intro,CW)
    doc.text(introLines,M,y);y+=introLines.length*3.9+7

    // ── HELPER section — exactement comme le prototype HTML ────
    // .section   : border 0.5px #cbd5e1 tout autour, border-radius simule
    // .sec-title : fond #f1f5f9, border-left 3px #3478c8, texte fonce uppercase
    // .field     : label vert uppercase, valeur fond #f1f5f9 radius
    // .field:border-bottom entre champs
    const TH=8.5  // titre section
    const FH=11   // hauteur rangee champ

    const section=(title,rows,y0)=>{
      // Calculer hauteur totale
      let sH=TH
      let ri=0
      while(ri<rows.length){
        sH+=FH
        if(rows[ri][2]==='full'){ri++}else{ri+=2}
      }

      // Fond blanc de la section
      doc.setFillColor(255,255,255)
      doc.rect(M,y0,CW,sH,'F')
      // Bordure fine #cbd5e1 tout autour — simule border-radius avec coins arrondis
      doc.setDrawColor(203,213,225)
      doc.setLineWidth(0.35)
      doc.rect(M,y0,CW,sH)

      // Titre : fond #f1f5f9
      doc.setFillColor(241,245,249)
      doc.rect(M,y0,CW,TH,'F')
      // border-left 3px #3478c8 sur le titre uniquement
      doc.setFillColor(52,120,200)
      doc.rect(M,y0,3,TH,'F')
      // Separateur bas titre
      doc.setDrawColor(203,213,225)
      doc.setLineWidth(0.3)
      doc.line(M,y0+TH,M+CW,y0+TH)
      // Texte titre — couleur texte principale, uppercase
      doc.setTextColor(30,41,59)
      doc.setFontSize(7.5);doc.setFont('helvetica','bold')
      t(title.toUpperCase(),M+6,y0+6)

      // Champs
      const colW=CW/2
      const col2=M+colW
      let ry=y0+TH

      const field=(label,val,fx,fw,fy)=>{
        const PAD=3
        // Label vert uppercase 6.5px
        doc.setTextColor(22,163,74)
        doc.setFontSize(6.5);doc.setFont('helvetica','bold')
        t(label.toUpperCase(),fx+PAD,fy+3.5)
        // Valeur fond #f1f5f9, border-radius simule par rect, texte fonce 8.5px
        doc.setFillColor(241,245,249)
        doc.rect(fx+PAD,fy+4.5,fw-PAD*2,5.5,'F')
        doc.setTextColor(30,41,59)
        doc.setFontSize(8);doc.setFont('helvetica','normal')
        t(safe(val||''),fx+PAD+2,fy+9)
      }

      let ri2=0
      while(ri2<rows.length){
        const r=rows[ri2]
        if(r[2]==='full'){
          field(r[0],r[1],M,CW,ry)
          ry+=FH;ri2++
          // border-bottom entre champs (pas sur le dernier)
          if(ri2<rows.length){
            doc.setDrawColor(203,213,225);doc.setLineWidth(0.25)
            doc.line(M,ry,M+CW,ry)
          }
        }else{
          field(r[0],r[1],M,colW,ry)
          // Filet vertical central
          doc.setDrawColor(203,213,225);doc.setLineWidth(0.25)
          doc.line(col2,ry,col2,ry+FH)
          if(rows[ri2+1]){field(rows[ri2+1][0],rows[ri2+1][1],col2,colW,ry)}
          ry+=FH;ri2+=2
          // border-bottom
          if(ri2<rows.length){
            doc.setDrawColor(203,213,225);doc.setLineWidth(0.25)
            doc.line(M,ry,M+CW,ry)
          }
        }
      }
      return y0+sH+3
    }

    // ── 3 SECTIONS ────────────────────────────────────────────
    y=section('Identification de l\'animal',[
      ['Nom et prenom du proprietaire', d.ownerName||''],
      ['Adresse complete',              d.ownerAddress||''],
      ['Tel 1',                         d.phone1||''],
      ['E-mail',                        d.email||''],
      ['Veterinaire traitant',
       d.veterinaireNom?(safe(d.veterinaireNom)+(d.veterinaireContact?' - '+safe(d.veterinaireContact):'')):'',
       'full'],
    ],y)

    y=section('Identification - puce electronique',[
      ['No identification', d.animalId||''],
      ['Date',              fmt(d.idDate)],
      ['Emplacement',       d.idLocation||'', 'full'],
    ],y)

    y=section('Description du '+(d.animalEspece||'chat').toLowerCase(),[
      ['Date de naissance', fmt(d.birthDate)],
      ['Race',              d.breed||''],
      ['Couleur',           d.coat||''],
      ['Poil',              d.hairType||''],
      ['Sexe',              d.sex==='MALE'?'Male':(d.sex||'')],
      ['Sterilise',         d.sterilise||'Non'],
      ['Nom d\'usage',      d.animalName||''],
      ['Pays d\'origine',   d.paysOrigine||'France'],
    ],y)

    y+=5

    // ── FOOTER ───────────────────────────────────────────────
    doc.setFillColor(248,250,252)
    doc.rect(0,H-8,W,8,'F')
    doc.setDrawColor(203,213,225);doc.setLineWidth(0.3)
    doc.line(0,H-8,W,H-8)
    doc.setTextColor(148,163,184)
    doc.setFontSize(6.5);doc.setFont('helvetica','normal')
    t('Carte generee le '+new Date().toLocaleDateString('fr-FR')+' - Document informatif',W/2,H-3,{align:'center'})

    doc.save('identification-'+(d.animalName||'animal').replace(/\s+/g,'-').replace(/[^\u0000-\u00FF]/g,'')+'.pdf')
  }


  const generateCessionPDF = async (f, adminSig, userSig) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const W = 210, H = 297, M = 14, CW = W - M * 2

  const safe = s => { if (s == null || s === '') return '\u2014'; return String(s).replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"').replace(/\u2014/g, '-').replace(/[^\u0000-\u00FF]/g, '') }
  const t = (s, x, y2, o = {}) => doc.text(safe(String(s == null ? '' : s)) || ' ', x, y2, o)
  const fmt = dt => { if (!dt || dt === '\u2014') return '\u2014'; try { return new Date(dt).toLocaleDateString('fr-FR') } catch (e) { return String(dt) } }

  // Charger logo
  const logo = await _getLogoDataUrl()

  // Header
  const hH = 38, lZW = 50
  doc.setFillColor(255, 255, 255)
  doc.rect(0, 0, lZW, hH, 'F')
  doc.setFillColor(30, 58, 120)
  doc.rect(lZW, 0, W - lZW, hH, 'F')
  doc.setFillColor(217, 174, 92)
  doc.rect(lZW - 1, 0, 2, hH, 'F')
  doc.setFillColor(217, 174, 92)
  doc.rect(0, hH, W, 0.8, 'F')
  if (logo) { try { doc.addImage(logo, 'JPEG', (lZW - 28) / 2, (hH - 28) / 2, 28, 28) } catch (e) {} }
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(11)
  doc.setFont('helvetica', 'bold')
  t('ANIMAUX POUR ADOPTION DE LA FRANCE', W - M, 10, { align: 'right' })
  doc.setFontSize(8.5)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(180, 200, 230)
  t(safe(f.cedantAdresse || '123 Rue des Compagnons, 75001 Paris'), W - M, 17, { align: 'right' })
  t('Tel : ' + safe(f.cedantTel || '+33 07 57 75 00 92'), W - M, 23, { align: 'right' })
  t('Email : ' + safe(f.cedantEmail || 'animauxdecompagnie@animauxpouradoptiondelafrance.com'), W - M, 29, { align: 'right' })

  let y = hH + 10

  // Titre en NAVY
  doc.setTextColor(30, 58, 120)
  doc.setFontSize(20)
  doc.setFont('helvetica', 'bold')
  t('TRANSFERT DE DOCUMENTS', W / 2, y, { align: 'center' })
  y += 7
  doc.setFontSize(9)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(100, 116, 139)
  t('Date : ' + fmt(f.date || f.doc?.date), W / 2, y, { align: 'center' })
  y += 8

  // Sections
  const drawIS = (title, rows, y0) => {
    doc.setTextColor(217, 174, 92)
    doc.setFontSize(9)
    doc.setFont('helvetica', 'bold')
    t(title.toUpperCase(), M, y0)
    y0 += 2
    doc.setDrawColor(217, 174, 92)
    doc.setLineWidth(0.2)
    doc.line(M, y0, M + CW, y0)
    y0 += 5
    rows.forEach(([lbl, val]) => {
      doc.setTextColor(18, 34, 74)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      t(lbl + ' :', M, y0)
      doc.setTextColor(30, 41, 59)
      doc.setFont('helvetica', 'normal')
      t(safe(String(val || '\u2014')), 50, y0)
      y0 += 6.5
    })
    return y0 + 2
  }

  y = drawIS('INFORMATIONS DU CLIENT', [
    ['Nom', f.acquereurNom || f.clientNom],
    ['Email', f.acquereurEmail || f.clientEmail],
    ['T\u00e9l\u00e9phone', f.acquereurTel],
    ['Adresse', f.acquereurAdresse],
  ], y)

  y = drawIS('INFORMATIONS DU PRESTATAIRE', [
    ['Soci\u00e9t\u00e9', f.cedantNom || 'Animaux pour Adoption de la France'],
    ['T\u00e9l\u00e9phone', f.cedantTel || '\u2014'],
    ['Email', f.cedantEmail || 'animauxdecompagnie@animauxpouradoptiondelafrance.com'],
    ['Adresse', f.cedantAdresse || '123 Rue des Compagnons, 75001 Paris'],
  ], y)

  // Trait avant Objet — IDENTIQUE AU USER
  y -= 4
  doc.setDrawColor(217, 174, 92)
  doc.setLineWidth(0.2)
  doc.line(M, y, M + CW, y)
  y += 9

  // Corps
  doc.setTextColor(18, 34, 74)
  doc.setFontSize(10)
  doc.setFont('helvetica', 'bold')
  t('Objet : Confirmation de transfert de documents', M, y)
  y += 10
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(30, 41, 59)
  t('Madame, Monsieur ' + safe(f.acquereurNom || f.clientNom || '') + ',', M, y)
  y += 7
  const paras = [
    'Par la pr\u00e9sente, la soci\u00e9t\u00e9 ' + (f.cedantNom || 'Animaux pour Adoption de la France') + ' confirme le transfert des documents relatifs au dossier de cession de votre animal.',
    'Ce document atteste de la remise de l\'ensemble des pi\u00e8ces n\u00e9cessaires \u00e0 la constitution de votre dossier de cession animalier.',
    'Nous vous prions de bien vouloir v\u00e9rifier l\'exactitude des informations mentionn\u00e9es ci-dessus et de nous signaler toute anomalie dans les plus brefs d\u00e9lais.'
  ]
  paras.forEach(para => { const lines = doc.splitTextToSize(para, CW); doc.text(lines, M, y); y += lines.length * 4.0 + 3 })

  // Tableau
  doc.setTextColor(217, 174, 92)
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  t('LISTE DES DOCUMENTS TRANSFERES', M, y)
  y += 2
  doc.setDrawColor(217, 174, 92)
  doc.setLineWidth(0.2)
  doc.line(M, y, M + CW, y)
  y += 5
  const cols = [8, 72, 20, 42, 24]
  const hdrs = ['N°', 'Document', 'Nb', 'R\u00e9f\u00e9rence', 'Date']
  const RH = 8
  doc.setFillColor(30, 58, 120)
  doc.rect(M, y, CW, RH, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'bold')
  let cx = M + 2
  hdrs.forEach((h, i) => { t(h, cx, y + 5.5); cx += cols[i] })
  y += RH

  const docList = Array.isArray(f.documents) && f.documents.length > 0
    ? f.documents
    : [{ document: 'Attestation de cession', reference: f.animalId || '\u2014', nombre: '1', date: f.date || f.doc?.date }]
  const docRows = docList.map((row, ri) => [String(ri + 1), safe(row.document || ''), safe(row.nombre || '1'), safe(row.reference || ''), fmt(row.date)])

  // Afficher les lignes du tableau — IDENTIQUE AU USER (sans gestion de pages multiples)
  docRows.forEach((row, ri) => {
    const bg = ri % 2 === 0
    doc.setFillColor(bg ? 255 : 248, bg ? 255 : 250, bg ? 255 : 252)
    doc.rect(M, y, CW, RH, 'F')
    doc.setDrawColor(203, 213, 225)
    doc.setLineWidth(0.15)
    doc.rect(M, y, CW, RH)
    doc.setTextColor(30, 41, 59)
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'normal')
    let cx2 = M + 2
    row.forEach((cell, ci) => { t(safe(cell || '').slice(0, ci === 1 ? 45 : 18), cx2, y + 5.5); cx2 += cols[ci] })

    // Traits verticaux du tableau
    let xAcc = M
    cols.forEach((colWidth) => {
      xAcc += colWidth
      doc.setDrawColor(203, 213, 225)
      doc.setLineWidth(0.15)
      doc.line(xAcc, y, xAcc, y + RH)
    })

    y += RH
  })
  y += 6

  // Signatures — saut de page seulement si pas assez de place (55mm pour les blocs + footer 12mm)
  if (y + 55 > H - 12) {
    doc.addPage()
    // Mini-header page 2
    doc.setFillColor(255,255,255);doc.rect(0,0,50,38,'F')
    doc.setFillColor(18,34,74);doc.rect(50,0,W-50,38,'F')
    doc.setFillColor(217,174,92);doc.rect(0,38,W,0.8,'F')
    if(logo){try{doc.addImage(logo,'JPEG',11,5,28,28)}catch(e){}}
    doc.setTextColor(255,255,255);doc.setFontSize(11);doc.setFont('helvetica','bold')
    t('ANIMAUX POUR ADOPTION DE LA FRANCE',W-M,10,{align:'right'})
    // Footer page 2
    doc.setFillColor(18,34,74);doc.rect(0,H-10,W,10,'F')
    doc.setFillColor(217,174,92);doc.rect(0,H-10,W,0.8,'F')
    doc.setTextColor(180,200,230);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
    t('ANIMAUX POUR ADOPTION DE LA FRANCE - '+(f.cedantAdresse||'123 Rue des Compagnons, 75001 Paris'),W/2,H-4,{align:'center'})
    y = 52
  }

  // Signatures
  const sigW = (CW - 6) / 2
  const sigX2 = M + sigW + 6
  const sigH = 32
  doc.setFontSize(9)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(30, 41, 59)
  t('Signature du Prestataire', M + sigW / 2, y, { align: 'center' })
  t('Signature du Client', sigX2 + sigW / 2, y, { align: 'center' })
  y += 6
  doc.setFillColor(248, 250, 252)
  doc.rect(M, y, sigW, sigH, 'F')
  doc.setDrawColor(203, 213, 225)
  doc.setLineWidth(0.15)
  doc.rect(M, y, sigW, sigH)
  if (adminSig) { try { doc.addImage(adminSig, 'PNG', M + 3, y + 2, sigW - 6, sigH - 8) } catch (e) {} }
  doc.setDrawColor(100, 116, 139)
  doc.setLineWidth(0.15)
  doc.line(M + 3, y + sigH - 8, M + sigW - 3, y + sigH - 8)
  doc.setTextColor(30, 41, 59)
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'normal')
  t(safe(f.cedantNom || 'Animaux pour Adoption de la France'), M + 3, y + sigH - 4)

  doc.setFillColor(248, 250, 252)
  doc.rect(sigX2, y, sigW, sigH, 'F')
  doc.setDrawColor(203, 213, 225)
  doc.setLineWidth(0.15)
  doc.rect(sigX2, y, sigW, sigH)
  if (userSig) { try { doc.addImage(userSig, 'PNG', sigX2 + 3, y + 2, sigW - 6, sigH - 8) } catch (e) {} } else {
    doc.setTextColor(148, 163, 184)
    doc.setFontSize(7.5)
    doc.setFont('helvetica', 'italic')
    t('En attente de signature', sigX2 + sigW / 2, y + sigH / 2, { align: 'center' })
  }
  doc.setDrawColor(100, 116, 139)
  doc.setLineWidth(0.15)
  doc.line(sigX2 + 3, y + sigH - 8, sigX2 + sigW - 3, y + sigH - 8)
  doc.setTextColor(30, 41, 59)
  doc.setFontSize(7.5)
  doc.setFont('helvetica', 'normal')
  t(safe(f.acquereurNom || f.clientNom || 'Client'), sigX2 + 3, y + sigH - 4)
  y += sigH + 6

  // Footer (conservé identique)
  doc.setFillColor(18, 34, 74)
  doc.rect(0, H - 10, W, 10, 'F')
  doc.setFillColor(217, 174, 92)
  doc.rect(0, H - 10, W, 0.8, 'F')
  doc.setTextColor(180, 200, 230)
  doc.setFontSize(6.5)
  doc.setFont('helvetica', 'normal')
  t('ANIMAUX POUR ADOPTION DE LA FRANCE - ' + (f.cedantAdresse || '123 Rue des Compagnons, 75001 Paris'), W / 2, H - 4, { align: 'center' })

  doc.save('cession-' + (f.animalNom || f.animalEspece || 'animal').replace(/\s+/g, '-').replace(/[^\u0000-\u00FF]/g, '') + '.pdf')
}


  const generateLivraisonPDF = async (f) => {
    const doc = new jsPDF({ unit:'mm', format:'a4' })
    const W=210, H=297, M=14

    // ── Palette : bleu marine moyen (pas trop foncé), or, blanc ──
    const N  = [30,58,120]      // bleu marine (plus clair que [18,34,74])
    const G  = [217,174,92]     // gold
    const WH = [255,255,255]    // blanc pur
    const BD = [190,200,220]    // bordure gris-bleu légère

    const fc=(...c)=>doc.setFillColor(...c)
    const tc=(...c)=>doc.setTextColor(...c)
    const dc=(...c)=>doc.setDrawColor(...c)
    const lw=w=>doc.setLineWidth(w)
    const txt=(s,x,y,o={})=>doc.text(String(s==null||s===''?'\u2014':s),x,y,o)

    // ── Logo ─────────────────────────────────────────────────────
    const logo=await _getLogoDataUrl()

    // ═══════════════════════════════════════════════════════════════
    // HEADER — identique facture : zone blanche logo | zone navy textes
    // ═══════════════════════════════════════════════════════════════
    const hH=38
    const lZW=52
    fc(...WH);doc.rect(0,0,lZW,hH,'F')
    fc(...N);doc.rect(lZW,0,W-lZW,hH,'F')
    fc(...G);doc.rect(lZW-1.5,0,2,hH,'F')
    fc(...G);doc.rect(0,hH,W,1,'F')
    const lS=30
    if(logo){try{doc.addImage(logo,'JPEG',(lZW-lS)/2,(hH-lS)/2,lS,lS)}catch(e){}}
    tc(...WH);doc.setFontSize(12);doc.setFont('helvetica','bold')
    txt('ANIMAUX POUR ADOPTION',W-M,11,{align:'right'})
    tc(...G);doc.setFontSize(7.5);doc.setFont('helvetica','bold')
    txt('DE LA FRANCE',W-M,18,{align:'right'})
    tc(180,200,230);doc.setFontSize(6);doc.setFont('helvetica','normal')
    txt('123 Rue des Compagnons - 75001 Paris, France',W-M,25,{align:'right'})
    txt('T\u00e9l: +33 07 57 75 00 92  |  Email: animauxdecompagnie@animauxpouradoptiondelafrance.com',W-M,31,{align:'right'})

    // ── Titre BON DE LIVRAISON ────────────────────────────────────
    let y=hH+2
    fc(...N);doc.rect(M,y,W-M*2,10,'F')
    fc(...G);doc.rect(M,y,3,10,'F')
    tc(...WH);doc.setFontSize(12);doc.setFont('helvetica','bold')
    txt('BON DE LIVRAISON',W/2,y+6.8,{align:'center'})
    y+=13

    // ═══════════════════════════════════════════════════════════════
    // DOUBLE COLONNE ÉMETTEUR | CLIENT
    // Label gold + valeur navy sur 1 ligne, fond blanc, bordure fine
    // ═══════════════════════════════════════════════════════════════
    const colW=(W-M*2)/2-2
    const col1x=M, col2x=M+colW+4
    const ROW_C=5.8
    const BLK_TITLE=7
    const BLK_PAD=7
    // Hauteur basée sur le max des deux blocs (émetteur=4, client=5) → 5 rows
    const BLK_TOT=BLK_TITLE+5*ROW_C+BLK_PAD  // 7+29+7=43

    const drawInfoBlock=(bx,bw,by,title,rows)=>{
      fc(...N);doc.rect(bx,by,bw,BLK_TITLE,'F')
      tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
      txt(title,bx+bw/2,by+4.8,{align:'center'})
      fc(...WH);doc.rect(bx,by+BLK_TITLE,bw,BLK_TOT-BLK_TITLE,'F')
      dc(...BD);lw(0.3);doc.rect(bx,by+BLK_TITLE,bw,BLK_TOT-BLK_TITLE)
      dc(...N);lw(0.4);doc.rect(bx,by,bw,BLK_TOT)
      let ry=by+BLK_TITLE+5.5
      rows.forEach(([label,val])=>{
        // Label navy gras
        tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
        const lbl=label+' :'
        doc.text(lbl,bx+5,ry)
        const lblW=doc.getTextWidth(lbl)
        // Valeur navy normal
        tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
        const valStr=String(val||'\u2014').slice(0,28)
        txt(valStr,bx+5+lblW+2,ry)
        // Tirets pointillés jusqu'au bord droit
        const vw=doc.getTextWidth(valStr)
        const sx=bx+5+lblW+3+vw
        if(sx<bx+bw-4){
          dc(...BD);doc.setLineWidth(0.25)
          doc.setLineDashPattern([0.8,0.8],0)
          doc.line(sx,ry-0.5,bx+bw-4,ry-0.5)
          doc.setLineDashPattern([],0)
        }
        ry+=ROW_C
      })
    }

    drawInfoBlock(col1x,colW,y,'ÉMETTEUR',[
      ['Raison sociale', f.entreprise||'Animaux pour Adoption de la France'],
      ['Adresse',        f.localisation||'123 Rue des Compagnons, 75001 Paris'],
      ['Téléphone',      f.tel||'+33 07 57 75 00 92'],
      ['Email',          'animauxdecompagnie@animauxpouradoptiondelafrance.com'],
    ])
    drawInfoBlock(col2x,colW,y,'CLIENT / DESTINATAIRE',[
      ['Nom',       f.clientNom||'\u2014'],
      ['Adresse',   f.clientAdresse||'\u2014'],
      ['Téléphone', f.clientTel||'\u2014'],
      ['Email',     f.clientEmail||'\u2014'],
      ['Référence', f.formNumber||'\u2014'],
    ])
    y+=BLK_TOT+3

    // ═══════════════════════════════════════════════════════════════
    // INFORMATIONS DE LIVRAISON — 6 champs sur 1 colonne, compact
    // Pas de séparateur vertical, fond blanc, bordure fine
    // bandeau 7 + 6 champs × 4.5mm + pad 4 = 38mm
    // ═══════════════════════════════════════════════════════════════
    fc(...N);doc.rect(M,y,W-M*2,7,'F')
    fc(...G);doc.rect(M,y,3,7,'F')
    tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
    txt('INFORMATIONS DE LIVRAISON',W/2,y+4.8,{align:'center'})
    y+=7

    const infoFields=[
      ['N\u00b0 de livraison',   f.formNumber||'\u2014'],
      ['Date',              f.date||'\u2014'],
      ['Heure pr\u00e9vue', f.heure||'\u2014'],
      ['Date de commande',  f.dateCommande||f.date||'\u2014'],
      ['Mode de livraison', f.modeTransport||'Transport terrestre'],
      ['Transporteur',      f.responsable||'\u2014'],
    ]
    const infoLineH=4.5
    const infoBodyH=6*infoLineH+5  // 27+5=32mm
    fc(...WH);doc.rect(M,y,W-M*2,infoBodyH,'F')
    dc(...BD);lw(0.3);doc.rect(M,y,W-M*2,infoBodyH)

    let iy=y+5
    infoFields.forEach(([label,val])=>{
      // Label navy gras
      tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
      const lbl=label+' :'
      doc.text(lbl,M+4,iy)
      const lblW=doc.getTextWidth(lbl)
      // Valeur navy normal
      tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
      const iVal=String(val||'\u2014')
      txt(iVal,M+4+lblW+2,iy)
      // Tirets pointillés
      const ivw=doc.getTextWidth(iVal)
      const isx=M+4+lblW+3+ivw
      if(isx<M+W-M*2-4){
        dc(...BD);doc.setLineWidth(0.25)
        doc.setLineDashPattern([0.8,0.8],0)
        doc.line(isx,iy-0.5,M+W-M*2-4,iy-0.5)
        doc.setLineDashPattern([],0)
      }
      iy+=infoLineH
    })
    y+=infoBodyH+3

    // ═══════════════════════════════════════════════════════════════
    // TABLEAU ANIMAUX — fond blanc pur, bordure légère (7+5×7=42mm)
    // ═══════════════════════════════════════════════════════════════
    const cols=[
      {label:'N\u00b0',                   w:9},
      {label:'D\u00e9signation / Animal', w:38},
      {label:'Esp\u00e8ce',               w:23},
      {label:'Race',                       w:23},
      {label:'\u00c2ge',                   w:13},
      {label:'Poids',                      w:15},
      {label:'Couleur / Signe',            w:32},
      {label:'N\u00b0 Puce',              w:29},
    ]
    const tblW=W-M*2
    fc(...N);doc.rect(M,y,tblW,7,'F')
    tc(...WH);doc.setFontSize(5.5);doc.setFont('helvetica','bold')
    let cx=M
    cols.forEach(c=>{txt(c.label,cx+c.w/2,y+4.8,{align:'center'});cx+=c.w})
    y+=7

    const AROW_H=7
    const animalRows=[
      {num:1,animal:f.animal,espece:f.espece,race:f.race,age:f.ageAnimal,poids:f.poids,couleur:f.couleur,puce:f.puce},
      {},{},{},{},
    ]
    animalRows.forEach((row)=>{
      fc(...WH);doc.rect(M,y,tblW,AROW_H,'F')
      dc(...BD);lw(0.2);doc.rect(M,y,tblW,AROW_H)
      if(row.num){
        tc(...N);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
        const vals=[row.num,row.animal||'',row.espece||'',row.race||'',row.age||'',row.poids||'',row.couleur||'',row.puce||'']
        cx=M
        cols.forEach((c)=>{
          txt(String(vals[cols.indexOf(c)]||''),cx+c.w/2,y+4.8,{align:'center'})
          cx+=c.w
        })
      }
      cx=M;dc(...BD);lw(0.2)
      cols.slice(0,-1).forEach(c=>{cx+=c.w;doc.line(cx,y,cx,y+AROW_H)})
      y+=AROW_H
    })
    y+=3

    // ═══════════════════════════════════════════════════════════════
    // CONDITIONS DE LIVRAISON — fond blanc, bordure légère (7+41=48mm)
    // ═══════════════════════════════════════════════════════════════
    fc(...N);doc.rect(M,y,W-M*2,7,'F')
    fc(...G);doc.rect(M,y,3,7,'F')
    tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
    txt('CONDITIONS DE LIVRAISON ET OBSERVATIONS',W/2,y+4.8,{align:'center'})
    y+=7

    const condBodyH=7*5+6  // 41mm
    fc(...WH);doc.rect(M,y,W-M*2,condBodyH,'F')
    dc(...BD);lw(0.3);doc.rect(M,y,W-M*2,condBodyH)

    const conditions=[
      'Animal en bonne sant\u00e9 \u00e0 la livraison',
      'Carnet de vaccination remis',
      "Documents d'identification remis (passeport, puce)",
      'Kit de bienvenue remis au client',
      'Contrat de cession sign\u00e9',
      'Informations de suivi post-adoption fournies',
      'Animal conforme \u00e0 la demande du client',
    ]
    let cy=y+5
    conditions.forEach(cond=>{
      dc(...BD);lw(0.35);doc.rect(M+5,cy-2.8,3.5,3.5)
      dc(...G);lw(0.6)
      doc.line(M+5.3,cy-1.2,M+6.1,cy-0.4)
      doc.line(M+6.1,cy-0.4,M+8.2,cy-3.0)
      tc(...N);doc.setFontSize(7);doc.setFont('helvetica','normal')
      txt(cond,M+11,cy)
      cy+=5
    })
    y+=condBodyH+3

    // ═══════════════════════════════════════════════════════════════
    // SIGNATURES — 2 blocs séparés par gap de 4mm (7+28=35mm)
    // ═══════════════════════════════════════════════════════════════
    const sigGap=4
    const sigColW=(W-M*2-sigGap)/2
    const sigTH=7
    const sigBH=26
    ;[['ENTREPRISE', f.sigEntreprise],['CLIENT', f.sigClient]].forEach(([label,sigData],i)=>{
      const sx=M+i*(sigColW+sigGap)
      fc(...N);doc.rect(sx,y,sigColW,sigTH,'F')
      tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
      txt(label,sx+sigColW/2,y+4.8,{align:'center'})
      fc(...WH);doc.rect(sx,y+sigTH,sigColW,sigBH,'F')
      dc(...BD);lw(0.3);doc.rect(sx,y+sigTH,sigColW,sigBH)
      dc(...N);lw(0.4);doc.rect(sx,y,sigColW,sigTH)
      tc(...N);doc.setFontSize(7);doc.setFont('helvetica','normal')
      txt('Nom :',sx+5,y+sigTH+6)
      dc(...BD);lw(0.5)
      doc.line(sx+18,y+sigTH+6,sx+sigColW-5,y+sigTH+6)
      if(sigData){
        // Injecter l'image de signature dans la zone prévue
        try{doc.addImage(sigData,'PNG',sx+4,y+sigTH+9,sigColW-8,sigBH-13)}catch(e){}
      } else {
        tc(...N);doc.setFontSize(7);doc.setFont('helvetica','normal')
        txt('Signature et cachet :',sx+5,y+sigTH+16)
        dc(...BD);lw(0.5)
        doc.line(sx+5,y+sigTH+23,sx+sigColW-5,y+sigTH+23)
      }
    })
    y+=sigTH+sigBH+3

    // ═══════════════════════════════════════════════════════════════
    // FOOTER fixe en bas (22mm)
    // ═══════════════════════════════════════════════════════════════
    fc(...G);doc.rect(0,H-22,W,1,'F')
    fc(...N);doc.rect(0,H-21,W,21,'F')
    tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
    txt("Bon de livraison - Ce document atteste de la remise de l'animal au client",W/2,H-13,{align:'center'})
    tc(160,175,200);doc.setFontSize(6);doc.setFont('helvetica','normal')
    txt('Document confidentiel - Destinataire uniquement',W/2,H-8,{align:'center'})
    txt('Animaux pour Adoption de la France  |  Association loi 1901',W/2,H-3,{align:'center'})

    doc.save(`bon-de-livraison-${f.clientNom||f.formNumber||Date.now()}.pdf`)
  }

  const generateFacturePDF = async (f) => {
    const doc = new jsPDF({ unit:'mm', format:'a4' })
    const W=210, H=297, M=14
    const N=[30,58,120], G=[217,174,92], WH=[255,255,255]
    const BK=[30,58,120], SL=[80,100,140], BD=[190,200,220]

    const fc=(...c)=>doc.setFillColor(...c)
    const tc=(...c)=>doc.setTextColor(...c)
    const dc=(...c)=>doc.setDrawColor(...c)
    const lw=w=>doc.setLineWidth(w)
    const txt=(s,x,y,o={})=>doc.text(String(s==null||s===''?'':s),x,y,o)
    const fa=n=>Number(n||0).toLocaleString('fr-FR',{minimumFractionDigits:2,maximumFractionDigits:2})+' \u20AC'

    const logo=await _getLogoDataUrl()

    // ═══════════════════════════════════════
    // HEADER compact — zone blanche logo | zone navy textes
    // ═══════════════════════════════════════
    const hH=38
    const lZW=52   // zone blanche gauche
    fc(...WH);doc.rect(0,0,lZW,hH,'F')
    fc(...N);doc.rect(lZW,0,W-lZW,hH,'F')
    fc(...G);doc.rect(lZW-1.5,0,2,hH,'F')   // filet gold vertical
    fc(...G);doc.rect(0,hH,W,1,'F')          // filet gold bas
    // Logo centré verticalement et horizontalement dans zone blanche
    const lS=30
    if(logo){try{doc.addImage(logo,'JPEG',(lZW-lS)/2,(hH-lS)/2,lS,lS)}catch(e){}}
    // Textes zone navy alignés à droite, centrés verticalement
    tc(...WH);doc.setFontSize(12);doc.setFont('helvetica','bold')
    txt('ANIMAUX POUR ADOPTION',W-M,11,{align:'right'})
    tc(...G);doc.setFontSize(7.5);doc.setFont('helvetica','bold')
    txt('DE LA FRANCE',W-M,18,{align:'right'})
    tc(180,200,230);doc.setFontSize(6);doc.setFont('helvetica','normal')
    txt('123 Rue des Compagnons - 75001 Paris, France',W-M,25,{align:'right'})
    txt('T\u00e9l: +33 07 57 75 00 92  |  Email: animauxdecompagnie@animauxpouradoptiondelafrance.com',W-M,31,{align:'right'})

    // ── FACTURE ──────────────────────────
    let y=42
    fc(...N);doc.rect(M,y,W-M*2,9,'F')
    tc(...WH);doc.setFontSize(12);doc.setFont('helvetica','bold')
    txt('FACTURE',W/2,y+6.3,{align:'center'})
    y+=12

    // ── N° Facture + Date ─────────────────
    tc(...BK);doc.setFontSize(7);doc.setFont('helvetica','bold')
    txt('N\u00b0 Facture :',M,y+4.5)
    txt(f.invoiceNumber||f.ref||'',M+26,y+4.5)
    dc(...BD);lw(0.35);doc.line(M+26,y+5.2,W/2-4,y+5.2)
    txt('Date :',W/2+4,y+4.5)
    txt(f.date||'',W/2+16,y+4.5)
    doc.line(W/2+16,y+5.2,W-M,y+5.2)
    y+=9

    // ═══════════════════════════════════════
    // BLOCS ÉMETTEUR | CLIENT
    // bandeau navy, corps blanc, label noir + tirets pointillés
    // 6 champs × 6mm + titre 8 + pad 4 = 48mm (blk)
    // ═══════════════════════════════════════
    const colW=(W-M*2)/2-2, col2x=M+colW+4
    const FH=6, BLTIT=8

    const drawFBlock=(bx,bw,by,title,rows)=>{
      fc(...N);doc.rect(bx,by,bw,BLTIT,'F')
      tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
      txt(title,bx+bw/2,by+5.5,{align:'center'})
      const bodyH=rows.length*FH+3
      fc(...WH);doc.rect(bx,by+BLTIT,bw,bodyH,'F')
      dc(...BD);lw(0.3);doc.rect(bx,by,bw,BLTIT+bodyH)
      let ry=by+BLTIT+FH
      rows.forEach(([label,val])=>{
        tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
        const lbl=label+' :'
        txt(lbl,bx+4,ry)
        const lw2=doc.getTextWidth(lbl)
        tc(...SL);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
        const valStr=String(val||'').slice(0,26)
        txt(valStr,bx+4+lw2+2,ry)
        // Tirets pointillés
        dc(...BD);doc.setLineWidth(0.25)
        doc.setLineDashPattern([0.8,0.8],0)
        const vw=doc.getTextWidth(valStr)
        const sx=bx+4+lw2+3+vw
        if(sx<bx+bw-3) doc.line(sx,ry-0.5,bx+bw-3,ry-0.5)
        doc.setLineDashPattern([],0)
        ry+=FH
      })
    }

    drawFBlock(M,colW,y,'\u00c9METTEUR (PRESTATAIRE)',[
      ['Raison sociale', f.entreprise||'Animaux pour Adoption'],
      ['Adresse',        f.localisation||''],
      ['T\u00e9l\u00e9phone', f.tel||''],
      ['Email',          'animauxdecompagnie@animauxpouradoptiondelafrance.com'],
    ])
    drawFBlock(col2x,colW,y,'CLIENT (FACTUR\u00c9 \u00c0)',[
      ['Nom complet',    f.clientNom||''],
      ['Adresse',        f.clientAdresse||''],
      ['T\u00e9l\u00e9phone', f.clientTel||''],
      ['Email',          f.clientEmail||''],
    ])
    // 4 champs x 6mm + titre 8 + pad 3 = 35mm
    y+=BLTIT+4*FH+3+3

    // ═══════════════════════════════════════
    // TABLEAU — 5 lignes × 6mm = 30mm + header 7 + gap 2 = 39mm
    // ═══════════════════════════════════════
    const tC=[
      {l:'N\u00b0',w:10,a:'left'},
      {l:'D\u00e9signation',w:57,a:'left'},
      {l:'Esp\u00e8ce / Race',w:38,a:'left'},
      {l:'Qt\u00e9',w:15,a:'center'},
      {l:'P.U. (\u20AC)',w:30,a:'right'},
      {l:'Total (\u20AC)',w:32,a:'right'},
    ]
    const tW=W-M*2
    fc(...N);doc.rect(M,y,tW,7,'F')
    tc(...WH);doc.setFontSize(6);doc.setFont('helvetica','bold')
    let cx=M
    tC.forEach(c=>{
      const tx=c.a==='right'?cx+c.w-2:c.a==='center'?cx+c.w/2:cx+3
      txt(c.l,tx,y+4.8,{align:c.a})
      cx+=c.w
    })
    y+=7

    const pu=Number(f.montant||f.prixTransport||0)
    const anStr=[(f.espece||''),(f.race||'')].filter(Boolean).join(' / ').slice(0,18)
    const dr=['1',String(f.description||'Transport animalier').slice(0,24),anStr||'','1',fa(pu),fa(pu)]
    const RH=6
    for(let ri=0;ri<5;ri++){
      const rowY=y+ri*RH
      fc(ri%2===0?255:249,ri%2===0?255:250,ri%2===0?255:253)
      doc.rect(M,rowY,tW,RH,'F')
      dc(...BD);lw(0.2);doc.rect(M,rowY,tW,RH)
      if(ri===0){
        tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
        let rxC=M
        tC.forEach((col,ci)=>{
          const tx=col.a==='right'?rxC+col.w-2:col.a==='center'?rxC+col.w/2:rxC+3
          txt(dr[ci],tx,rowY+4.2,{align:col.a})
          rxC+=col.w
        })
      }
      let rxC2=M;dc(...BD);lw(0.18)
      tC.slice(0,-1).forEach(col=>{rxC2+=col.w;doc.line(rxC2,rowY,rxC2,rowY+RH)})
    }
    y+=5*RH
    y+=2

    // ═══════════════════════════════════════
    // TOTAUX — boîte droite 32mm
    // Sous-total supprimé : ______ €
    // TVA / Remise : ______ €
    // TOTAL PAYE : navy/gold
    // ═══════════════════════════════════════
    const totW=90, totX=M+tW-totW
    // TOTAL PAYE — bandeau navy
    fc(...N);doc.rect(totX,y+12,totW,11,'F')
    dc(...BD);lw(0.3);doc.rect(totX,y,totW,23)
    tc(...G);doc.setFontSize(7.5);doc.setFont('helvetica','bold')
    txt('TOTAL PAYE :',totX+4,y+19)
    tc(...WH);txt(fa(pu),totX+totW-4,y+19,{align:'right'})
    y+=23+3

    // ═══════════════════════════════════════
    // MODE DE PAIEMENT 22mm corps
    // ═══════════════════════════════════════
    const mpW=(W-M*2)*0.6
    fc(...N);doc.rect(M,y,mpW,7,'F')
    tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
    txt('MODE DE PAIEMENT',M+mpW/2,y+4.8,{align:'center'})
    y+=7
    fc(...WH);doc.rect(M,y,mpW,18,'F')
    dc(...BD);lw(0.3);doc.rect(M,y,mpW,18)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
    txt('IBAN :',M+4,y+5.5)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
    txt(f.iban||'',M+15,y+5.5)
    dc(...BD);lw(0.4);doc.line(M+15,y+6,M+mpW-4,y+6)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
    txt('BIC :',M+4,y+11)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
    txt(f.bic||'',M+13,y+11)
    dc(...BD);lw(0.4);doc.line(M+13,y+11.5,M+mpW/2-2,y+11.5)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
    txt('Banque :',M+mpW/2+2,y+11)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
    txt(f.banque||'',M+mpW/2+16,y+11)
    dc(...BD);lw(0.4);doc.line(M+mpW/2+16,y+11.5,M+mpW-4,y+11.5)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
    txt('Date d\u2019\u00e9ch\u00e9ance :',M+4,y+16.5)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
    txt(f.dateEcheance||'',M+28,y+16.5)
    dc(...BD);lw(0.4);doc.line(M+28,y+17,M+mpW/2-2,y+17)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
    txt('R\u00e9f\u00e9rences :',M+mpW/2+2,y+16.5)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
    txt(f.references||'',M+mpW/2+20,y+16.5)
    dc(...BD);lw(0.4);doc.line(M+mpW/2+20,y+17,M+mpW-4,y+17)
    y+=18+5

    // ═══════════════════════════════════════
    // CONDITIONS GÉNÉRALES 14mm corps
    // ═══════════════════════════════════════
    fc(...N);doc.rect(M,y,W-M*2,7,'F')
    tc(...WH);doc.setFontSize(7);doc.setFont('helvetica','bold')
    txt('CONDITIONS G\u00c9N\u00c9RALES',W/2,y+4.8,{align:'center'})
    y+=7
    fc(...WH);doc.rect(M,y,W-M*2,16,'F')
    dc(...BD);lw(0.3);doc.rect(M,y,W-M*2,16)
    tc(...BK);doc.setFontSize(6.5);doc.setFont('helvetica','normal')
    txt('Paiement \u00e0 r\u00e9ception de la facture. Retard de paiement : p\u00e9nalit\u00e9s selon le taux BCE + 10 points.',M+4,y+5)
    txt('En cas de litige, juridiction comp\u00e9tente : Tribunal comp\u00e9tent du si\u00e8ge social.',M+4,y+10)
    txt('TVA non applicable, art. 293 B du CGI (association loi 1901).',M+4,y+15)
    y+=16+5

    // ═══════════════════════════════════════
    // SIGNATURES — 2 blocs gap 4mm, 20mm corps
    // ═══════════════════════════════════════
    const sG=4,sCW=(W-M*2-sG)/2,sTH=7,sBH=20
    ;[['CACHET \u0026 SIGNATURE \u00c9METTEUR', f.sigEntreprise],['DATE ET SIGNATURE CLIENT', f.sigClient]].forEach(([title,sigData],i)=>{
      const sx=M+i*(sCW+sG)
      fc(...N);doc.rect(sx,y,sCW,sTH,'F')
      tc(...WH);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
      txt(title,sx+sCW/2,y+4.8,{align:'center'})
      fc(...WH);doc.rect(sx,y+sTH,sCW,sBH,'F')
      dc(...BD);lw(0.3);doc.rect(sx,y+sTH,sCW,sBH)
      dc(...N);lw(0.4);doc.rect(sx,y,sCW,sTH)
      if(sigData){
        try{doc.addImage(sigData,'PNG',sx+4,y+sTH+3,sCW-8,sBH-6)}catch(e){}
      } else {
        tc(...BK);doc.setFontSize(7);doc.setFont('helvetica','normal')
        txt('Signature et cachet',sx+sCW/2,y+sTH+12,{align:'center'})
      }
    })
    y+=sTH+sBH+3

    // ═══════════════════════════════════════
    // FOOTER 16mm
    // ═══════════════════════════════════════
    fc(...G);doc.rect(0,H-16,W,1.5,'F')
    fc(...N);doc.rect(0,H-14.5,W,14.5,'F')
    tc(...WH);doc.setFontSize(6.5);doc.setFont('helvetica','bold')
    txt('TVA non applicable - article 293 B du Code G\u00e9n\u00e9ral des Imp\u00f4ts',W/2,H-9,{align:'center'})
    tc(160,175,200);doc.setFontSize(5.5);doc.setFont('helvetica','normal')
    txt('Conservez ce justificatif.',W/2,H-5,{align:'center'})
    txt('Animaux pour Adoption de la France  |  Association loi 1901',W/2,H-1.5,{align:'center'})

    doc.save(`facture-${f.clientNom||f.invoiceNumber||Date.now()}.pdf`)
  }

  const loadAdoptionForms = async () => {
    setAdoptionLoading(true)
    try {
      const data = await adoptionAPI.getAllAdmin({ page: 1, limit: 50 })
      setAdoptionForms(data.forms || [])
    } catch (e) { showToast('Erreur chargement formulaires') }
    finally { setAdoptionLoading(false) }
  }

  // ── Synchronise docFormOpenRef avec les états des formulaires de documents ──
  // Permet au polling 15s de savoir si un formulaire est en cours de remplissage
  // sans causer de re-render (useRef, pas useState).
  useEffect(() => {
    docFormOpenRef.current = showLfForm || showFfForm || showCsForm || showAvForm || showDiForm || showAcForm
  }, [showLfForm, showFfForm, showCsForm, showAvForm, showDiForm, showAcForm])

  // ── Polling 15s pour adoption + livraison ──
  useEffect(() => {
    const poll = async () => {
      try {
        const data = await adoptionAPI.getAllAdmin({ page: 1, limit: 50 })
        setAdoptionForms(prev => {
          const hasChange = (data.forms || []).length !== prev.length ||
            (data.forms || []).some(f => { const p = prev.find(x => x._id === f._id); return !p || p.readByAdmin !== f.readByAdmin })
          return hasChange ? (data.forms || []) : prev
        })
        const countData = await adoptionAPI.getUnreadCountAdmin()
        setAdoptionBadge(countData.count || 0)
      } catch { /* silencieux */ }

      // ── Ne pas recharger les données livraison si un formulaire est ouvert ──
      // loadDeliveryData() met deliveryLoading=true ce qui démonte les formulaires
      // et détruit leur état interne (champs remplis par l'admin).
      if (docFormOpenRef.current) return

      try {
        const [fD, iD, ac] = await Promise.all([
          deliveryAPI.getForms(),
          deliveryAPI.getInvoices(),
          animalDocsAPI.getAll('cession'),
        ])
        const allSignedIds = [
          ...(fD.forms   || []).filter(x => x.signatureStatus === 'signed').map(x => x._id),
          ...(iD.invoices|| []).filter(x => x.signatureStatus === 'signed').map(x => x._id),
          ...(ac.docs    || []).filter(x => x.signatureStatus === 'signed').map(x => x._id),
        ]
        if (knownSignedIds.current === null) {
          knownSignedIds.current = new Set(allSignedIds)
        } else {
          const newlySigned = allSignedIds.filter(id => !knownSignedIds.current.has(id))
          if (newlySigned.length > 0) {
            setDocSignedBadge(n => n + newlySigned.length)
            newlySigned.forEach(id => knownSignedIds.current.add(id))
          }
        }
        await loadDeliveryData()
      } catch { /* silencieux */ }
    }
    const interval = setInterval(poll, 15000)
    return () => clearInterval(interval)
  }, []) // eslint-disable-line

  const openAdoptionDetail = async (form) => {
    setSelectedAdoption({ ...form, _loading: true })
    setAdoptionDetailLoading(true)
    try {
      const data = await adoptionAPI.getFormByIdAdmin(form._id)
      setSelectedAdoption(data.form)
      // Mettre à jour la liste locale (marquer comme lu)
      setAdoptionForms(fs => fs.map(f => f._id === form._id ? { ...f, readByAdmin: true } : f))
    } catch (e) { showToast('Erreur chargement formulaire') }
    finally { setAdoptionDetailLoading(false) }
  }

  const downloadAdoptionPDFAdmin = async (form) => {
    setDownloadingAdoptionId(form._id)
    try {
      // Récupérer le formulaire complet avec signature si pas déjà chargé
      let fullForm = form
      if (!form.signatureDataUrl) {
        const data = await adoptionAPI.getFormByIdAdmin(form._id)
        fullForm = data.form
      }
      // Charger le logo
      let logoDataUrl = null
      try {
        const logoResp = await fetch(require('../../assets/monlogo.jpeg'))
        const blob = await logoResp.blob()
        logoDataUrl = await new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(blob) })
      } catch (e) {}

      const doc = generateAdoptionPDF({
        data: { s1: fullForm.s1||{}, s2: fullForm.s2||{}, s3: fullForm.s3||{}, s4: fullForm.s4||{}, s5: fullForm.s5||{}, s6: fullForm.s6||{} },
        signatureDataUrl: fullForm.signatureDataUrl || null,
        user: fullForm.submittedBy || null,
        formNumber: fullForm.formNumber,
        logoDataUrl,
      })
      doc.save(`${fullForm.formNumber || 'formulaire-adoption'}.pdf`)
    } catch (e) { showToast('Erreur génération PDF') }
    finally { setDownloadingAdoptionId(null) }
  }

  // ── Rafraîchit uniquement la liste des utilisateurs (badges, aperçus) — panneau 1 ──
  const refreshConversations = async () => {
    try {
      const data = await messagesAPI.getAdminConversations()
      if (data?.conversations) setConversations(data.conversations)
    } catch {}
  }

  // ── Ouvre le fil d'une annonce précise pour l'utilisateur sélectionné — panneau 3 ──
  // Prend userId en paramètre explicite (jamais selectedConvId) pour éviter tout
  // problème de closure obsolète juste après un changement d'utilisateur.
  // Referme aussitôt le panneau "Annonces" pour laisser toute la place au fil.
  const openListingThread = async (userId, key) => {
    setSelectedListingKey(key)
    setThreadLoading(true)
    setListingPanelOpen(false)
    try {
      const data = await messagesAPI.getAdminUserListingThread(userId, key)
      setThreadMsgs(data.messages || [])
      // Cette annonce vient d'être marquée lue côté backend → on reflète localement
      setUserListingConvs(cs => cs.map(c => c.key === key ? { ...c, unreadCount: 0 } : c))
      refreshConversations() // met aussi à jour le badge global de cet utilisateur (panneau 1)
    } catch (err) {
      showToast(err.message || 'Erreur de chargement de la conversation')
    } finally {
      setThreadLoading(false)
    }
  }

  // ── Sélectionne un utilisateur : charge ses annonces discutées et ouvre le
  // panneau 2 pour les montrer — l'admin choisit ensuite explicitement laquelle
  // ouvrir (plus d'ouverture automatique de la plus récente) ──
  const openUserConversation = async (userId) => {
    setSelectedConvId(userId)
    setSelectedListingKey(null)
    setThreadMsgs([])
    setListingConvsLoading(true)
    setListingPanelOpen(true)
    try {
      const data = await messagesAPI.getAdminUserListingConversations(userId)
      setUserListingConvs(data.conversations || [])
    } catch (err) {
      showToast(err.message || 'Erreur de chargement des conversations')
    } finally {
      setListingConvsLoading(false)
    }
  }

  // ── Rafraîchit la liste des annonces de l'utilisateur actuellement sélectionné (polling) ──
  const refreshUserListingConvs = async () => {
    if (!selectedConvId) return
    try {
      const data = await messagesAPI.getAdminUserListingConversations(selectedConvId)
      setUserListingConvs(data.conversations || [])
    } catch {}
  }

  // ── Rafraîchit le thread actuellement ouvert (polling) ──
  const refreshThread = async () => {
    if (!selectedConvId || !selectedListingKey) return
    try {
      const data = await messagesAPI.getAdminUserListingThread(selectedConvId, selectedListingKey)
      setThreadMsgs(data.messages || [])
      setUserListingConvs(cs => cs.map(c => c.key === selectedListingKey ? { ...c, unreadCount: 0 } : c))
    } catch {}
  }

  // ── Envoie un message dans la conversation sélectionnée (texte et/ou plusieurs images) ──
  // Rattachée explicitement à l'annonce choisie dans le panneau 2 (plus d'inférence
  // automatique), garantissant que la réponse atterrit toujours au bon endroit côté user.
  const sendAdminChatMessage = async (text, imageFiles) => {
    if (!selectedConvId || !selectedListingKey) return
    setChatSending(true)
    try {
      const listingId = selectedListingKey !== 'general' ? selectedListingKey : null
      const res = await messagesAPI.sendAdminMessage(selectedConvId, { body: text, imageFiles, listingId })
      setThreadMsgs(m => [...m, res.message])
      const n = imageFiles?.length || 0
      const preview = text && text.trim() ? text : (n > 1 ? `📷 ${n} photos` : n === 1 ? '📷 Photo' : '')
      setUserListingConvs(cs => cs.map(c => c.key === selectedListingKey
        ? { ...c, lastMessage: preview, lastDate: new Date().toISOString() }
        : c
      ))
      setConversations(cs => cs.map(c => c.user._id === selectedConvId
        ? { ...c, lastMessage: preview, lastDate: new Date().toISOString() }
        : c
      ))
      // Notifie l'utilisateur en temps réel (autre onglet / autre fenêtre)
      try { new BroadcastChannel('app_sync_event').postMessage({ type: 'new_message' }) } catch {}
    } catch (err) {
      showToast(err.message || "Erreur lors de l'envoi")
    } finally {
      setChatSending(false)
    }
  }

  // ── Supprime un message — 'pour moi' (disparaît de ma vue) ou 'pour tout le
  // monde' (remplacé par un placeholder des deux côtés, réservé à l'expéditeur) ──
  const handleDeleteMessage = async (messageId, forEveryone) => {
    try {
      await messagesAPI.delete(messageId, forEveryone)
      if (forEveryone) {
        setThreadMsgs(m => m.map(x => x._id === messageId ? { ...x, deletedForEveryone: true, body: '', images: [] } : x))
      } else {
        setThreadMsgs(m => m.filter(x => x._id !== messageId))
      }
      refreshUserListingConvs()
      refreshConversations()
    } catch (err) {
      showToast(err.message || 'Erreur lors de la suppression')
    }
  }

  // ── Supprime la conversation actuellement ouverte avec l'utilisateur sélectionné (pour l'admin uniquement) ──
  const handleDeleteConversation = async () => {
    if (!selectedConvId || !selectedListingKey) return
    try {
      await messagesAPI.deleteAdminConversation(selectedConvId, selectedListingKey)
      setSelectedListingKey(null)
      setThreadMsgs([])
      await refreshUserListingConvs()
      await refreshConversations()
      showToast('Conversation supprimée')
    } catch (err) {
      showToast(err.message || 'Erreur lors de la suppression')
    }
  }

  // ── Polling : utilisateurs toutes les 10s, annonces de l'user ouvert toutes les
  // 10s, thread de l'annonce ouverte toutes les 2s ──
  useEffect(() => {
    const interval = setInterval(refreshConversations, 10000)
    return () => clearInterval(interval)
  }, []) // eslint-disable-line

  useEffect(() => {
    if (!selectedConvId || view !== 'messages') return
    const interval = setInterval(refreshUserListingConvs, 10000)
    return () => clearInterval(interval)
  }, [selectedConvId, view]) // eslint-disable-line

  // ── BroadcastChannel : rafraîchit instantanément le thread quand l'user envoie un message ──
  useEffect(() => {
    let ch
    try {
      ch = new BroadcastChannel('app_sync_event')
      ch.onmessage = () => {
        refreshThread()
        refreshConversations()
      }
    } catch {}
    return () => { try { ch && ch.close() } catch {} }
  }, []) // eslint-disable-line

  // ── Polling léger : statut en ligne/hors ligne de l'utilisateur sélectionné ──
  useEffect(() => {
    if (!selectedConvId || view !== 'messages') { setUserStatus(null); return }
    const pollStatus = async () => {
      try {
        const data = await messagesAPI.getUserStatus(selectedConvId)
        setUserStatus({ online: data.online, lastActive: data.lastActive })
      } catch {}
    }
    pollStatus()
    const interval = setInterval(pollStatus, 15000)
    return () => clearInterval(interval)
  }, [selectedConvId, view])

  useEffect(() => {
    if (!selectedConvId || !selectedListingKey || view !== 'messages') return
    const interval = setInterval(refreshThread, 2000)
    return () => clearInterval(interval)
  }, [selectedConvId, selectedListingKey, view]) // eslint-disable-line


  // Auto-ouvre le formulaire nouvelle annonce si demandé depuis la Navbar
  useEffect(() => {
    if (autoOpenListing) {
      setView('listings')
      setTimeout(() => {
        setEditItem(null)
        resetForm()
        setShowForm(true)
        onListingFormOpened()
      }, 80)
    }
  }, []) // eslint-disable-line

  // ── Charge toutes les annonces côté admin (tous statuts : pending, active, rejected, sold) ──
  // Indépendant du prop listings passé par Home.jsx qui ne contient que les 'active'.
  // Ce useEffect alimente setListings (le state de Home) via la prop callback,
  // ce qui garantit que le tableau admin et le catalogue public restent synchronisés.
  useEffect(() => {
    const fetchAllListings = async () => {
      try {
        const data = await listingsAPI.getAllAdmin({ limit: 200 })
        if (data?.listings) {
          setListings(data.listings.map(l => ({
            ...l,
            id:  l._id,
            img: l.images?.[0]?.url || '',
            imgs: (l.images || []).map(i => i.url),
          })))
        }
      } catch (err) { console.warn('Admin listings:', err.message) }
    }
    fetchAllListings()
    const interval = setInterval(fetchAllListings, 15000)
    return () => clearInterval(interval)
  }, []) // eslint-disable-line

  const resetForm = () => {
    setFTitle(''); setFBreed(''); setFCat('Chiens'); setFPrice(''); setFAge('')
    setFSex('Mâle'); setFCity(''); setFRegion(''); setFSeller('')
    setFSellerType('Pro'); setFPhone(''); setFDesc(''); setFStatus('active')
    setFVacc(false); setFMicro(false); setFPedi(false); setFFeat(false); setFImgs([])
    setFDelivery('Livraison incluse')
  }

  const openEdit = (item) => {
    setEditItem(item)
    setFTitle(item.title); setFBreed(item.breed); setFCat(item.category)
    setFPrice(item.price); setFAge(item.age); setFSex(item.sex)
    setFCity(item.city); setFRegion(item.region || '')
    setFSeller(item.seller || ''); setFSellerType(item.sellerType || 'Pro')
    setFPhone(item.phone || '')
    setFDesc(item.desc || ''); setFStatus(item.status)
    setFVacc(item.vaccinated); setFMicro(item.microchipped); setFPedi(item.pedigree); setFFeat(item.featured)
    setFDelivery(item.delivery || 'Livraison incluse')
    setFImgs([]); setShowForm(true)
  }

  // Stocke les vrais File objects pour FormData
  const handleFiles = async (files) => {
    const slots = 10 - fImgs.length
    if (slots <= 0) return
    const raw = Array.from(files).slice(0, slots)
    const compressed = await compressImages(raw, { maxDimension: 1600, targetMaxMB: 9 })
    const entries = compressed.map(f => ({ file: f, url: URL.createObjectURL(f), name: f.name }))
    setFImgs(p => [...p, ...entries].slice(0, 10))
  }

  // Appel API réel avec FormData
  const saveForm = async () => {
    if (!fTitle.trim())                                    { showToast('Le titre est requis');                return }
    if (!fBreed)                                           { showToast('La race est requise');                return }
    if (fSellerType !== 'Don' && !String(fPrice).trim())   { showToast('Le prix est requis');                return }
    if (!fAge.trim())                                      { showToast("L'âge est requis");                  return }
    if (!fCity.trim())                                     { showToast('La ville est requise');               return }
    if (!fRegion)                                          { showToast('La région est requise');              return }
    if (!fSeller.trim())                                   { showToast('Le nom vendeur/éleveur est requis');  return }
    if (!fPhone.trim())                                    { showToast('Le téléphone annonceur est requis');  return }
    if (fDesc.trim().length < 10)                          { showToast('La description doit contenir au moins 10 caractères'); return }
    if (fDesc.trim().length > 1000)                        { showToast('La description ne peut pas dépasser 1000 caractères'); return }

    setFSaving(true)
    try {
      const formData = new FormData()
      formData.append('title',        fTitle.trim())
      formData.append('breed',        fBreed.trim())
      formData.append('category',     fCat)
      formData.append('price',        fPrice)
      formData.append('age',          fAge.trim())
      formData.append('sex',          fSex)
      formData.append('city',         fCity.trim())
      formData.append('region',       fRegion)
      formData.append('seller',       fSeller.trim())
      formData.append('sellerType',   fSellerType || 'Pro')
      formData.append('phone',        fPhone.trim())
      formData.append('desc',         fDesc.trim())
      formData.append('status',       fStatus)
      formData.append('vaccinated',   fVacc)
      formData.append('microchipped', fMicro)
      formData.append('pedigree',     fPedi)
      formData.append('featured',     fFeat)
      formData.append('delivery',     fDelivery)

      // Ajouter les vrais fichiers image
      fImgs.forEach(entry => { if (entry.file) formData.append('images', entry.file) })

      let result
      if (editItem) {
        result = await listingsAPI.update(editItem._id || editItem.id, formData)
        const updated = result.listing
        const mainImg = updated.images?.[0]?.url || ''
        setListings(ls => ls.map(l =>
          (l._id === updated._id || l.id === updated._id)
            ? { ...l, ...updated, id: updated._id, img: mainImg, imgs: updated.images?.map(i => i.url) || [], createdAt: updated.createdAt }
            : l
        ))
        showToast('Annonce modifiée — visible sur le site')
      } else {
        result = await listingsAPI.create(formData)
        const newItem = result.listing
        const mainImg = newItem.images?.[0]?.url || ''
        setListings(ls => [{
          ...newItem,
          id:        newItem._id,
          img:       mainImg,
          imgs:      newItem.images?.map(i => i.url) || [],
          createdAt: newItem.createdAt,
        }, ...ls])
        showToast('Annonce publiée — visible sur le site')
      }
      setShowForm(false); setEditItem(null); resetForm(); setView('listings')
    } catch (err) {
      showToast(err.message || 'Erreur lors de la sauvegarde')
    } finally {
      setFSaving(false)
    }
  }

  // Stats
  const pendingCount    = listings.filter(l => l.status === 'pending').length
  const totalRevenue    = orders.reduce((s, o) => s + o.amount, 0)
  const unreadMsgs      = conversations.reduce((s, c) => s + (c.unreadCount || 0), 0)

  const catDist = CATEGORIES.map(c => ({ c, n: listings.filter(l => l.category === c).length })).filter(x => x.n > 0)
  const maxDist = Math.max(...catDist.map(x => x.n), 1)
  const DIST_COLORS = ['#1A3A8F','#F5C842','#4CAF50','#7c3aed','#ca8a04','#E63946','#0ea5e9','#f43f5e']

  // Activité mensuelle — données réelles backend ou fallback
  const realMonthlyRevenue = monthlyData
    ? MONTHS.map((_, i) => monthlyData[i] ?? 0)
    : FALLBACK_REVENUE
  const realMonthlyOrders = revenueStats?.monthlyOrders
    ? MONTHS.map((_, i) => revenueStats.monthlyOrders[i] ?? 0)
    : FALLBACK_ORDERS

  const chartData = chartMode === 'revenue' ? realMonthlyRevenue
                  : chartMode === 'views'   ? FALLBACK_VIEWS
                  : realMonthlyOrders
  const maxChart  = Math.max(...chartData, 1)

  const filteredListings = useMemo(() =>
    listings.filter(l => {
      if (statusFilter !== 'all' && l.status !== statusFilter) return false
      if (!searchQ) return true
      return l.title.toLowerCase().includes(searchQ.toLowerCase()) ||
             l.breed.toLowerCase().includes(searchQ.toLowerCase()) ||
             l.city.toLowerCase().includes(searchQ.toLowerCase())
    })
  , [listings, searchQ, statusFilter])

  const totalListingsPages = Math.ceil(filteredListings.length / LISTINGS_PER_PAGE)
  const paginatedListings  = filteredListings.slice((listingsPage-1)*LISTINGS_PER_PAGE, listingsPage*LISTINGS_PER_PAGE)

  const NAV = [
    { sec:'Tableau de bord', items:[
      { id:'dashboard', icon:<IcoDashboard />, label:'Vue d\'ensemble' },
      { id:'revenue',   icon:<IcoRevenue />,   label:'Revenus & finances' },
    ]},
    { sec:'Contenu', items:[
      { id:'listings',  icon:<IcoListings />,  label:'Annonces', badge: pendingCount > 0 ? pendingCount : null },
      { id:'publish',   icon:<IcoPublish />,   label:'Publier une annonce' },
    ]},
    { sec:'Communauté', items:[
      { id:'users',     icon:<IcoUsers />,     label:'Utilisateurs', badge: newUsersBadge > 0 ? newUsersBadge : null },
      { id:'orders',    icon:<IcoOrders />,    label:'Commandes', badge: newOrdersBadge > 0 ? newOrdersBadge : null },
      { id:'messages',  icon:<IcoMessages />,  label:'Messages', badge: unreadMsgs > 0 ? unreadMsgs : null },
      { id:'documents', icon:<IcoShield />,    label:"Mes documents", badge: (adoptionBadge + docSignedBadge) > 0 ? (adoptionBadge + docSignedBadge) : null },
      { id:'transport', icon:<IcoTruck />,     label:'Transport & Tracking' },
    ]},
    { sec:'Configuration', items:[
      { id:'settings',  icon:<IcoSettings />,  label:'Paramètres' },
    ]},
  ]

  const pageTitle = {
    dashboard:'Vue d\'ensemble', revenue:'Revenus & finances',
    listings:'Gestion des annonces', publish: editItem ? 'Modifier une annonce' : 'Publier une annonce',
    users:'Utilisateurs', orders:'Commandes', messages:'Messages', documents:"Mes documents", adoption:"Formulaires d’adoption", settings:'Paramètres',
  }[view] || 'Dashboard'

  return (
    <div className="adm-layout">

      {/* ── SIDEBAR DESKTOP ── */}
      <aside className="adm-sb">
        <div className="adm-sb-logo">
          <div className="adm-sb-logo-icon">
            <img src={require('../../assets/monlogo.jpeg')} alt="Logo" className="adm-sb-logo-img" onError={(e) => { e.target.style.display='none' }} />
          </div>
          <div className="adm-sb-logo-txt">Animaux pour Adoption <span className="adm-badge">Admin</span></div>
        </div>
        <nav className="adm-nav">
          {NAV.map(sec => (
            <div key={sec.sec}>
              <div className="adm-nav-sec">{sec.sec}</div>
              {sec.items.map(item => (
                <button
                  key={item.id}
                  data-id={item.id}
                  className={`adm-nav-item${view === item.id ? ' on' : ''}`}
                  onClick={() => {
                    if (item.id === 'publish')   { setEditItem(null); resetForm(); setShowForm(false) }
                    if (item.id === 'listings')  { setStatusFilter('all'); setSearchQ(''); setListingsPage(1) }
                    if (item.id === 'orders')    { setNewOrdersBadge(0); localStorage.setItem(ORDERS_SEEN_KEY, String(localOrders.length)) }
                    if (item.id === 'users')     setNewUsersBadge(0)
                    if (item.id === 'documents') { setAdoptionBadge(0); setDocSignedBadge(0); setSelectedAdoption(null); loadAdoptionForms(); loadDeliveryData(); setDocTab('adoption') }
                    if (item.id === 'transport') loadTrips()
                    setView(item.id)
                  }}
                >
                  <span className="adm-nav-icon">{item.icon}</span>
                  {item.label}
                  {item.badge && <span className="adm-nav-badge">{item.badge}</span>}
                </button>
              ))}
              <div className="adm-divider" />
            </div>
          ))}
        </nav>
        <div className="adm-sb-user">
          <div className="adm-sb-avatar">AD</div>
          <div>
            <div className="adm-sb-uname">Administrateur</div>
            <div className="adm-sb-uemail">animauxdecompagnie@animauxpouradoptiondelafrance.com</div>
          </div>
          <button className="adm-sb-logout" title="Déconnexion" onClick={onLogout}><IcoLogout /></button>
        </div>
      </aside>

      {/* ── TOPBAR (hamburger mobile + titre + retour desktop) ── */}
      <div className="adm-main">
        <div className="adm-topbar">
          <button className="adm-burger" onClick={() => setSbOpen(true)} aria-label="Menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            {notifBadge > 0 && <span className="adm-burger-dot" />}
          </button>
          <button className="adm-back-btn adm-back-btn-topbar" onClick={onBack}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Retour au site
          </button>
          <div className="adm-page-title">{pageTitle}</div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            {notifBadge > 0 && (
              <span style={{ background:'#E63946', color:'#fff', borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700 }}>
                🔔 {notifBadge} nouvelle{notifBadge > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Drawer mobile — identique au user */}
        {sbOpen && (
          <div className="ud-drawer-overlay" onClick={() => setSbOpen(false)}>
            <div className="ud-drawer" onClick={e => e.stopPropagation()}>

              {/* En-tête */}
              <div className="ud-drawer-head">
                <div className="ud-drawer-user">
                  <div className="adm-sb-avatar">AD</div>
                  <div>
                    <div className="ud-sb-uname">Bonjour, Admin 👋</div>
                    <div className="ud-sb-uemail">animauxdecompagnie@animauxpouradoptiondelafrance.com</div>
                  </div>
                </div>
                <button className="ud-drawer-close" onClick={() => setSbOpen(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>
              </div>

              {/* Navigation */}
              <div className="ud-drawer-body">
                {NAV.map(sec => sec.items.map(item => (
                  <button
                    key={item.id}
                    className={`ud-drawer-item${view === item.id ? ' active' : ''}`}
                    onClick={() => {
                      if (item.id === 'publish')   { setEditItem(null); resetForm(); setShowForm(false) }
                      if (item.id === 'listings')  { setStatusFilter('all'); setSearchQ(''); setListingsPage(1) }
                      if (item.id === 'orders')    { setNewOrdersBadge(0); localStorage.setItem(ORDERS_SEEN_KEY, String(localOrders.length)) }
                      if (item.id === 'users')     setNewUsersBadge(0)
                      if (item.id === 'documents') { setAdoptionBadge(0); setDocSignedBadge(0); setSelectedAdoption(null); loadAdoptionForms(); loadDeliveryData(); setDocTab('adoption') }
                      if (item.id === 'transport') loadTrips()
                      setView(item.id); setSbOpen(false)
                    }}
                  >
                    <span className="ud-drawer-icon">{item.icon}</span>
                    <span className="ud-drawer-label">{item.label}</span>
                    {item.badge && <span className="ud-nav-badge">{item.badge > 9 ? '9+' : item.badge}</span>}
                  </button>
                )))}
              </div>

              {/* Pied */}
              <div className="ud-drawer-footer">
                <button className="ud-drawer-back" onClick={() => { setSbOpen(false); onBack() }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Retour au site
                </button>
                <button className="ud-drawer-logout" onClick={onLogout}>
                  <IcoLogout /> Quitter
                </button>
              </div>

            </div>
          </div>
        )}

        <div className="adm-content">

          {/* ══ VUE D'ENSEMBLE ══ */}
          {view === 'dashboard' && (
            <>
              <div className="adm-page-hd">
                <div>
                  <div className="adm-hd-title">Bonjour, Administrateur 👋</div>
                  <div className="adm-hd-sub">Résumé de l'activité de la plateforme</div>
                </div>
                <button className="adm-btn adm-btn-primary" onClick={() => { setView('publish'); setEditItem(null); resetForm() }}>+ Publier</button>
              </div>

              {/* KPIs */}
              <div className="adm-stats-row">
                {[
                  { label:'Revenus totaux',    val:fmt(revenueStats?.totalRevenue ?? totalRevenue), delta:'Commandes payées', up:true,  color:'c-green',  icon:<IcoRevenue /> },
                  { label:'Messages non lus',  val:unreadMsgs,           delta:'À traiter',       up:false, color:'c-blue',   icon:<IcoMessages /> },
                  { label:'Commandes',         val:revenueStats?.total   ?? orders.length,         delta:'Total payées', up:true, color:'c-gold',   icon:<IcoOrders /> },
                  { label:'Total des annonces',val:listings.length,       delta:'Sur la plateforme',up:true, color:'c-purple', icon:<IcoListings /> },
                  { label:'Utilisateurs',      val:users.length,          delta:'Inscrits',         up:true, color:'c-blue',   icon:<IcoUsers /> },
                  { label:'Commandes livrées', val:revenueStats?.delivered ?? orders.filter(o=>o.status==='delivered').length, delta:`${revenueStats?.deliveryRate ?? 0}% du total`, up:true, color:'c-green', icon:<IcoCheck /> },
                ].map((s,i) => (
                  <div key={i} className={`adm-stat-card ${s.color}`}>
                    <div className="adm-stat-label">{s.label}</div>
                    <div className="adm-stat-val">{s.val}</div>
                    <div className={`adm-stat-delta ${s.up ? 'd-up' : 'd-dn'}`}>{s.up ? '▲' : '▼'} {s.delta}</div>
                    <div className="adm-stat-icon">{s.icon}</div>
                  </div>
                ))}
              </div>

              {/* Charts */}
              <div className="adm-charts-row">
                <div className="adm-chart-card">
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                    <div>
                      <div className="adm-chart-title"><IcoTrend /> Activité mensuelle</div>
                      <div className="adm-chart-sub">
                        {revenueStats ? `${new Date().getFullYear()} — données réelles` : '12 derniers mois — chargement…'}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:5 }}>
                      {[['revenue',<IcoRevenue />],['views',<IcoEye />],['orders',<IcoOrders />]].map(([m,ico]) => (
                        <button key={m} className={`adm-chart-tab${chartMode===m?' on':''}`} onClick={() => setChartMode(m)}>{ico}</button>
                      ))}
                    </div>
                  </div>
                  <div className="adm-bar-chart">
                    {chartData.map((v,i) => (
                      <div key={i} className="adm-bar-col">
                        <div className="adm-bar-amt">{chartMode==='revenue' ? (v>=1000?`${(v/1000).toFixed(1)}k`:v) : v}</div>
                        <div className="adm-bar" style={{ height:`${(v/maxChart)*110}px`, background: i===new Date().getMonth() ? 'var(--gold)' : 'var(--blue-lt)' }} />
                        <div className="adm-bar-month">{MONTHS[i]}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="adm-chart-card">
                  <div className="adm-chart-title"><IcoPie /> Par espèce</div>
                  <div className="adm-chart-sub" style={{ marginBottom:14 }}>{listings.length} annonces</div>
                  {catDist.map((x,i) => (
                    <div key={x.c} className="adm-dist-item">
                      <div className="adm-dist-dot" style={{ background:DIST_COLORS[i%DIST_COLORS.length] }} />
                      <div className="adm-dist-lbl">{x.c}</div>
                      <div className="adm-dist-bar-bg"><div className="adm-dist-bar-fg" style={{ width:`${(x.n/maxDist)*100}%`, background:DIST_COLORS[i%DIST_COLORS.length] }} /></div>
                      <div className="adm-dist-val">{x.n}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent listings */}
              <div className="adm-tbl-wrap">
                <div className="adm-tbl-head">
                  <div className="adm-tbl-title"><IcoListings /> Dernières annonces</div>
                  <button className="adm-btn adm-btn-ghost" onClick={() => { setView('listings'); setListingsPage(1) }}>Voir toutes →</button>
                </div>
                <div className="adm-tbl-scroll">
                  <table className="adm-tbl">
                    <thead><tr><th>Annonce</th><th>Catégorie</th><th>Prix</th><th>Statut</th><th>Vues</th><th>Action</th></tr></thead>
                    <tbody>
                      {listings.slice(0,5).map(l => (
                        <tr key={l.id || l._id}>
                          <td>
                            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                              {(l.images?.[0]?.url || l.img) && <img className="adm-tbl-img" src={l.images?.[0]?.url || l.img} alt="" loading="lazy" />}
                              <div><div className="adm-tbl-name">{l.title}</div><div className="adm-tbl-sub">{l.breed} · {l.city}</div></div>
                            </div>
                          </td>
                          <td>{l.category}</td>
                          <td><strong>{fmt(l.price)}</strong></td>
                          <td><span className={`adm-sp adm-sp-${l.status}`}>{l.status==='active'?'Actif':l.status==='pending'?'En attente':'Vendu'}</span></td>
                          <td>{l.views}</td>
                          <td><button className="adm-ta-btn adm-ta-edit" onClick={() => { openEdit(l); setView('listings') }}><IcoEdit /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ══ REVENUS ══ */}
          {view === 'revenue' && (() => {
            const CAT_COLORS = {
              'Chiens':'#f59e0b','Chats':'#8b5cf6','Oiseaux':'#10b981',
              'Reptiles':'#22c55e','Lapins':'#ec4899','Rongeurs':'#f97316',
              'Poissons':'#0ea5e9','Chevaux':'#a16207',
            }

            // Ventes par espèce : backend uniquement — pas de données fictives
            const catSales = CATEGORIES.map(cat => {
              const bc = revenueStats?.byCategory?.[cat]
              if (bc) return { cat, count: bc.count, revenue: bc.revenue }
              const sl = listings.filter(l => l.category === cat && l.status === 'sold')
              return { cat, count: sl.length, revenue: sl.reduce((s,l) => s + l.price, 0) }
            }).filter(x => x.count > 0 || x.revenue > 0)

            const noSalesData     = catSales.length === 0
            const totalCatRevenue = catSales.reduce((s,x) => s + x.revenue, 0)

            // KPIs financiers depuis le backend
            const realRevenue    = revenueStats?.totalRevenue  ?? totalRevenue
            const realCommission = revenueStats?.totalCommission ?? Math.round(realRevenue * 0.1)
            const realTotal      = revenueStats?.total         ?? orders.length
            const realDelivered  = revenueStats?.delivered     ?? orders.filter(o => o.status === 'delivered').length
            const realAvgBasket  = revenueStats?.avgBasket     ?? (realTotal > 0 ? Math.round(realRevenue / realTotal) : 0)
            const realDelivRate  = revenueStats?.deliveryRate  ?? (realTotal > 0 ? Math.round((realDelivered / realTotal) * 100) : 0)

            return (
              <>
                <div className="adm-page-hd"><div className="adm-hd-title"><IcoRevenue /> Revenus & finances</div></div>
                <div className="adm-revenue-grid">
                  {[
                    { title:'Revenus totaux',   val:fmt(realRevenue),    sub:'Commandes payées',      pct:Math.min(100,Math.round(realRevenue/500)), color:'var(--blue)'  },
                    { title:'Commissions (10%)',val:fmt(realCommission), sub:'Revenus plateforme',    pct:10,           color:'var(--green)'  },
                    { title:'Panier moyen',     val:fmt(realAvgBasket),  sub:`${realTotal} commandes`,pct:Math.min(100,Math.round(realAvgBasket/20)), color:'var(--blue)' },
                    { title:'Tx. livraison',    val:`${realDelivRate}%`, sub:`${realDelivered} livrées`, pct:realDelivRate, color:'var(--green)' },
                  ].map((c,i) => (
                    <div key={i} className="adm-revenue-card">
                      <div className="adm-rc-title">{c.title}</div>
                      <div className="adm-rc-val">{c.val}</div>
                      <div className="adm-rc-sub">{c.sub}</div>
                      <div className="adm-rc-bar-bg"><div className="adm-rc-bar-fg" style={{ width:`${c.pct}%`, background:c.color }} /></div>
                    </div>
                  ))}
                </div>
                <div className="adm-charts-row" style={{ marginBottom:0 }}>
                  <div className="adm-chart-card">
                    <div className="adm-chart-title"><IcoPie /> Ventes par espèce</div>
                    <div className="adm-chart-sub" style={{ marginBottom:18 }}>Répartition du chiffre d'affaires par catégorie</div>
                    {noSalesData ? (
                      <div style={{ textAlign:'center', padding:'32px 0', color:'var(--slate-lt)' }}>
                        <div style={{ fontSize:32, marginBottom:10 }}>📊</div>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--slate)', marginBottom:4 }}>0 vente enregistrée</div>
                        <div style={{ fontSize:12 }}>Les données s'afficheront dès la première commande livrée.</div>
                      </div>
                    ) : (
                      <>
                        <div className="rev-cat-list">
                          {catSales.map((x) => {
                            const pct = Math.round((x.revenue / totalCatRevenue) * 100) || 0
                            const color = CAT_COLORS[x.cat] || '#6366f1'
                            return (
                              <div key={x.cat} className="rev-cat-row">
                                <div className="rev-cat-left">
                                  <div className="rev-cat-dot" style={{ background:color }} />
                                  <div className="rev-cat-name">{x.cat}</div>
                                </div>
                                <div className="rev-cat-bar-wrap">
                                  <div className="rev-cat-bar-bg">
                                    <div className="rev-cat-bar-fg" style={{ width:`${pct}%`, background:color }} />
                                  </div>
                                  <div className="rev-cat-pct">{pct}%</div>
                                </div>
                                <div className="rev-cat-right">
                                  <div className="rev-cat-rev">{fmt(x.revenue)}</div>
                                  <div className="rev-cat-count">{x.count} vente{x.count>1?'s':''}</div>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                        <div className="rev-cat-total">
                          <span>Total des revenus</span>
                          <strong>{fmt(totalCatRevenue)}</strong>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="adm-chart-card">
                    <div className="adm-chart-title"><IcoTrend /> Répartition visuelle</div>
                    <div className="adm-chart-sub" style={{ marginBottom:18 }}>Part de chaque espèce</div>
                    {noSalesData ? (
                      <div style={{ textAlign:'center', padding:'32px 0', color:'var(--slate-lt)' }}>
                        <div style={{ fontSize:32, marginBottom:10 }}>🐾</div>
                        <div style={{ fontSize:13, fontWeight:600, color:'var(--slate)', marginBottom:4 }}>0 vente enregistrée</div>
                        <div style={{ fontSize:12 }}>La répartition s'affichera dès que des animaux auront été vendus.</div>
                      </div>
                    ) : (
                      <>
                        <div className="rev-stacked-bar">
                          {catSales.map(x => {
                            const pct = (x.revenue / totalCatRevenue) * 100
                            return pct > 0 ? (
                              <div key={x.cat} className="rev-stacked-seg" style={{ width:`${pct}%`, background: CAT_COLORS[x.cat] || '#6366f1' }} title={`${x.cat} — ${Math.round(pct)}%`} />
                            ) : null
                          })}
                        </div>
                        <div className="rev-legend">
                          {catSales.map(x => (
                            <div key={x.cat} className="rev-legend-item">
                              <div className="rev-cat-dot" style={{ background: CAT_COLORS[x.cat] || '#6366f1' }} />
                              <span className="rev-legend-label">{x.cat}</span>
                              <span className="rev-legend-pct">{Math.round((x.revenue/totalCatRevenue)*100)}%</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </>
            )
          })()}

          {/* ══ ANNONCES ══ */}
          {view === 'listings' && !showForm && (
            <>
              <div className="adm-page-hd">
                <div><div className="adm-hd-title"><IcoListings /> Annonces ({listings.length})</div></div>
                <div className="adm-hd-actions">
                  {pendingCount > 0 && <span className="adm-sp adm-sp-pending" style={{ padding:'6px 12px' }}><IcoClock /> {pendingCount} en attente</span>}
                  <button className="adm-btn adm-btn-primary" onClick={() => { setView('publish'); setEditItem(null); resetForm() }}>+ Nouvelle</button>
                </div>
              </div>
              <div className="adm-tbl-wrap">
                {/* Onglets filtre statut */}
                <div className="adm-status-tabs">
                  {[
                    { key:'all',      label:'Toutes',       count: listings.length },
                    { key:'pending',  label:'En attente',   count: listings.filter(l=>l.status==='pending').length },
                    { key:'active',   label:'Approuvées',   count: listings.filter(l=>l.status==='active').length },
                    { key:'rejected', label:'Rejetées',     count: listings.filter(l=>l.status==='rejected').length },
                    { key:'sold',     label:'Vendues',      count: listings.filter(l=>l.status==='sold').length },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      className={`adm-status-tab${statusFilter===tab.key?' active':''} adm-stab-${tab.key}`}
                      onClick={() => { setStatusFilter(tab.key); setListingsPage(1) }}
                    >
                      {tab.label}
                      <span className="adm-stab-count">{tab.count}</span>
                    </button>
                  ))}
                </div>
                <div className="adm-tbl-head">
                  <div className="adm-tbl-title">
                    {statusFilter==='all' ? 'Toutes les annonces'
                      : statusFilter==='pending' ? 'Annonces en attente'
                      : statusFilter==='active'  ? 'Annonces approuvées'
                      : statusFilter==='rejected'? 'Annonces rejetées'
                      : 'Annonces vendues'}
                    {' '}({filteredListings.length})
                  </div>
                  <div className="adm-search-wrap">
                    <IcoSearch />
                    <input className="adm-search" placeholder="Rechercher…" value={searchQ} onChange={e => { setSearchQ(e.target.value); setListingsPage(1) }} />
                  </div>
                </div>
                <div className="adm-tbl-scroll">
                  <table className="adm-tbl">
                    <thead><tr><th>Photo</th><th>Titre</th><th>Catégorie</th><th>Prix</th><th>Ville</th><th>Vendeur</th><th>Date</th><th>Statut</th><th>Vues</th><th>Actions</th></tr></thead>
                    <tbody>{paginatedListings.map(l => {
                      const mainImg = l.images?.[0]?.url || l.img || ''
                      return (
                        <tr key={l.id || l._id}>
                          <td>
                            {mainImg
                              ? <img className="adm-tbl-img" src={mainImg} alt="" loading="lazy" />
                              : <div className="adm-tbl-img" style={{ background:'var(--border)', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--slate-lt)', fontSize:10 }}>—</div>
                            }
                          </td>
                          <td><div className="adm-tbl-name">{l.title}</div><div className="adm-tbl-sub">{l.breed}</div></td>
                          <td>{l.category}</td>
                          <td><strong>{fmt(l.price)}</strong></td>
                          <td>{l.city}</td>
                          <td style={{ fontSize:12, color:'var(--slate)' }}>
                            {l.seller || '—'}
                            {l.sellerType && <div className="adm-tbl-sub">{l.sellerType === 'Pro' ? 'Éleveur Pro' : l.sellerType === 'Don' ? 'Don' : 'Particulier'}</div>}
                          </td>
                          <td style={{ fontSize:11, color:'var(--slate-lt)', whiteSpace:'nowrap' }}>{fmtDate(l.createdAt || l.date)}</td>
                          <td>
                            {(l.status === 'active' || l.status === 'pending') ? (
                              <span
                                className={`adm-sp adm-sp-${l.status}`}
                                style={{ cursor:'pointer' }}
                                onClick={() => handleListingStatusChange(l, l.status === 'active' ? 'pending' : 'active')}
                                title="Cliquer pour changer le statut"
                              >
                                {l.status === 'active' ? 'Actif' : 'En attente'}
                              </span>
                            ) : (
                              <span className={`adm-sp adm-sp-${l.status}`}>
                                {l.status === 'rejected' ? 'Rejetée' : 'Vendu'}
                              </span>
                            )}
                          </td>
                          <td>{l.views}</td>
                          <td>
                            <div style={{ display:'flex', gap:4 }}>
                              {l.status==='pending' && (
                                <>
                                  <button className="adm-ta-btn adm-ta-approve" onClick={() => handleListingStatusChange(l, 'active')} title="Approuver"><IcoCheck /></button>
                                  <button className="adm-ta-btn adm-ta-reject" onClick={() => openRejectModal(l)} title="Rejeter"><IcoBan /></button>
                                </>
                              )}
                              <button className="adm-ta-btn adm-ta-edit" onClick={() => openEdit(l)}><IcoEdit /></button>
                              <button className="adm-ta-btn adm-ta-del" onClick={() => setConfirmDel({type:'listing',id:l.id||l._id,label:l.title})}><IcoTrash /></button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}</tbody>
                  </table>
                </div>
                {totalListingsPages > 1 && (
                  <div className="adm-pagination">
                    <button className="adm-page-btn" disabled={listingsPage===1} onClick={() => setListingsPage(p=>p-1)}>←</button>
                    {Array.from({length:totalListingsPages},(_,i)=>i+1).map(p => (
                      <button key={p} className={`adm-page-btn${listingsPage===p?' active':''}`} onClick={() => setListingsPage(p)}>{p}</button>
                    ))}
                    <button className="adm-page-btn" disabled={listingsPage===totalListingsPages} onClick={() => setListingsPage(p=>p+1)}>→</button>
                    <span className="adm-page-info">{(listingsPage-1)*LISTINGS_PER_PAGE+1}–{Math.min(listingsPage*LISTINGS_PER_PAGE,filteredListings.length)} / {filteredListings.length}</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ══ FORMULAIRE ANNONCE ══ */}
          {(view === 'publish' || (view === 'listings' && showForm)) && (
            <div className="adm-form-card">
              <div className="adm-form-title">{editItem ? <><IcoEdit /> Modifier l'annonce</> : <><IcoPublish /> Nouvelle annonce</>}</div>

              {/* Photos */}
              <div className="adm-fg">
                <label className="adm-label">Photos (max 10)</label>
                <div
                  className={`adm-upload${drag ? ' drag' : ''}`}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDrag(true) }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={e => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files) }}
                >
                  <div style={{ marginBottom:6 }}><IcoUpload /></div>
                  <div style={{ fontSize:13, fontWeight:600, color:'var(--blue)', marginBottom:3 }}>Glissez vos photos ici ou cliquez</div>
                  <div style={{ fontSize:11, color:'var(--slate)' }}>JPG, PNG, WebP — max 10 photos — 10 Mo max par photo</div>
                  <input ref={fileRef} type="file" multiple accept="image/*" style={{ display:'none' }} onChange={e => handleFiles(e.target.files)} />
                </div>
                {fImgs.length > 0 && (
                  <div className="adm-img-preview">
                    {fImgs.map((img,i) => (
                      <div key={i} className="adm-img-item">
                        <img src={img.url} alt="" />
                        <button className="adm-img-del" onClick={() => setFImgs(p => p.filter((_,idx) => idx!==i))}>✕</button>
                        {i===0 && <div className="adm-img-main">Principale</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="adm-fg"><label className="adm-label">Titre <Req /></label><input className="adm-input" placeholder="Ex: Chiot Berger Allemand LOF" value={fTitle} onChange={e => setFTitle(e.target.value)} /></div>

              <div className="adm-fr2">
                <div className="adm-fg">
                  <label className="adm-label">Espèce <Req /></label>
                  <BreedSelect options={CATEGORIES} value={fCat} onChange={(v) => { setFCat(v); setFBreed('') }} placeholder="Sélectionner une espèce…" />
                </div>
                <div className="adm-fg">
                  <label className="adm-label">Race <Req /></label>
                  <BreedSelect category={fCat} value={fBreed} onChange={setFBreed} placeholder="Sélectionner ou taper une race…" />
                </div>
              </div>

              <div className="adm-fr3">
                <div className="adm-fg">
                  <label className="adm-label">
                    Prix (€) {fSellerType !== 'Don' && '*'}
                    {fSellerType === 'Don' && <span style={{marginLeft:6, fontSize:11, fontWeight:500, color:'var(--green-dk)', textTransform:'none'}}>Don gratuit</span>}
                  </label>
                  <input
                    className="adm-input"
                    type="number"
                    placeholder="850"
                    value={fSellerType === 'Don' ? '0' : fPrice}
                    onChange={e => { if (fSellerType !== 'Don') setFPrice(e.target.value) }}
                    disabled={fSellerType === 'Don'}
                    style={fSellerType === 'Don' ? {background:'var(--bg2)', color:'var(--slate-lt)', cursor:'not-allowed'} : {}}
                  />
                </div>
                <div className="adm-fg"><label className="adm-label">Âge <Req /></label><input className="adm-input" placeholder="2 mois" value={fAge} onChange={e => setFAge(e.target.value)} /></div>
                <div className="adm-fg">
                  <label className="adm-label">Sexe <Req /></label>
                  <BreedSelect options={['Mâle','Femelle']} value={fSex} onChange={setFSex} placeholder="Sélectionner…" />
                </div>
              </div>

              {/* Localisation */}
              <div className="adm-section-title"><IcoPin /> Localisation</div>
              <div className="adm-fr2">
                <div className="adm-fg">
                  <label className="adm-label">Ville <Req /></label>
                  <input className="adm-input" placeholder="Paris" value={fCity} onChange={e => setFCity(e.target.value)} />
                </div>
                <div className="adm-fg">
                  <label className="adm-label">Région <Req /></label>
                  <BreedSelect options={REGIONS} value={fRegion} onChange={setFRegion} placeholder="Sélectionner ou taper une région…" />
                </div>
              </div>

              {/* Annonceur — 3 colonnes : Vendeur / Téléphone / Type */}
              <div className="adm-fr3">
                <div className="adm-fg">
                  <label className="adm-label">Vendeur / Éleveur <Req /></label>
                  <input className="adm-input" placeholder="Élevage du Chêne Vert" value={fSeller} onChange={e => setFSeller(e.target.value)} />
                </div>
                <div className="adm-fg">
                  <label className="adm-label">Téléphone annonceur <Req /></label>
                  <input className="adm-input" type="tel" placeholder="+330612345678" value={fPhone} onChange={e => setFPhone(e.target.value)} />
                </div>
                <div className="adm-fg">
                  <label className="adm-label">Type</label>
                  <BreedSelect
                    options={['Éleveur Pro','Particulier','Don']}
                    value={fSellerType === 'Pro' ? 'Éleveur Pro' : fSellerType === 'Part' ? 'Particulier' : fSellerType === 'Don' ? 'Don' : fSellerType}
                    onChange={v => {
                      const t = v === 'Éleveur Pro' ? 'Pro' : v === 'Particulier' ? 'Part' : v
                      setFSellerType(t)
                      if (t === 'Don') setFPrice('0')
                    }}
                    placeholder="Sélectionner le type…"
                  />
                </div>
              </div>

              <div className="adm-fg">
                <label className="adm-label">
                  Description <Req />
                  <span style={{
                    marginLeft: 8,
                    fontSize: 11,
                    fontWeight: 400,
                    textTransform: 'none',
                    color: fDesc.length === 0 ? 'var(--slate-lt)'
                         : fDesc.length < 10  ? 'var(--red)'
                         : fDesc.length > 1000 ? 'var(--red)'
                         : 'var(--green-dk)',
                  }}>
                    {fDesc.length}/1000
                    {fDesc.length > 0 && fDesc.length < 10 && ' (min. 10)'}
                    {fDesc.length > 1000 && ' (max. 1000 dépassé)'}
                    {fDesc.length >= 10 && fDesc.length <= 1000 && ' ✓'}
                  </span>
                </label>
                <textarea
                  className="adm-input adm-textarea"
                  rows={6}
                  placeholder="Décrivez l'animal : caractère, santé, conditions d'élevage, documents fournis… (10 à 1000 caractères)"
                  value={fDesc}
                  maxLength={1010}
                  onChange={e => setFDesc(e.target.value)}
                  style={{ borderColor: fDesc.length > 1000 ? '#E63946' : fDesc.length > 0 && fDesc.length < 10 ? '#E63946' : undefined }}
                />
              </div>

              <div className="adm-fr2">
                <div className="adm-fg">
                  <label className="adm-label">Statut</label>
                  <BreedSelect
                    options={['Actif','En attente','Vendu']}
                    value={fStatus === 'active' ? 'Actif' : fStatus === 'pending' ? 'En attente' : 'Vendu'}
                    onChange={v => setFStatus(v === 'Actif' ? 'active' : v === 'En attente' ? 'pending' : 'sold')}
                    placeholder="Sélectionner le statut…"
                  />
                </div>
                <div className="adm-fg">
                  <label className="adm-label">Livraison <Req /></label>
                  <BreedSelect
                    options={DELIVERY_OPTIONS}
                    value={fDelivery}
                    onChange={setFDelivery}
                    placeholder="Sélectionner…"
                  />
                </div>
              </div>

              <div style={{ display:'flex', flexWrap:'wrap', gap:18, marginBottom:18 }}>
                <label className="adm-cbx"><input type="checkbox" checked={fVacc} onChange={e => setFVacc(e.target.checked)} /><IcoSyringe /> Vacciné</label>
                <label className="adm-cbx"><input type="checkbox" checked={fMicro} onChange={e => setFMicro(e.target.checked)} />
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="8" strokeDasharray="2 3"/></svg>
                  Pucé
                </label>
                <label className="adm-cbx"><input type="checkbox" checked={fPedi} onChange={e => setFPedi(e.target.checked)} /><IcoListings /> Pédigrée</label>
                <label className="adm-cbx"><input type="checkbox" checked={fFeat} onChange={e => setFFeat(e.target.checked)} />⭐ À la une</label>
              </div>

              <div style={{ display:'flex', gap:10 }}>
                <button
                  className="adm-btn adm-btn-primary"
                  onClick={saveForm}
                  disabled={fSaving}
                  style={{ opacity: fSaving ? 0.7 : 1 }}
                >
                  {fSaving ? '⏳ Envoi en cours…' : editItem ? <><IcoSave /> Enregistrer</> : <><IcoRocket /> Publier l'annonce</>}
                </button>
                <button className="adm-btn adm-btn-ghost" disabled={fSaving} onClick={() => { setShowForm(false); setEditItem(null); resetForm(); setView('listings') }}>Annuler</button>
              </div>
            </div>
          )}

          {/* ══ UTILISATEURS ══ */}
          {view === 'users' && (() => {
            const U_PER = 10
            const totalUpg = Math.max(1, Math.ceil(users.length / U_PER))
            const pagedUsers = users.slice((usersPage-1)*U_PER, usersPage*U_PER)
            return (
              <>
                <div className="adm-page-hd"><div className="adm-hd-title"><IcoUsers /> Utilisateurs ({users.length})</div></div>
                <div className="adm-tbl-wrap">
                  <div className="adm-tbl-head"><div className="adm-tbl-title">Gestion des utilisateurs</div></div>
                  <div className="adm-tbl-scroll">
                    <table className="adm-tbl">
                      <thead><tr><th>Utilisateur</th><th>Email</th><th>Ville</th><th>Achats</th><th>Inscrit le</th><th>Statut</th><th>Actions</th></tr></thead>
                      <tbody>{pagedUsers.map(u => (
                        <tr key={u.id}>
                          <td>
                            <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                              <div className="adm-user-av">{u.name[0]}</div>
                              <div><div className="adm-tbl-name">{u.name}</div><div className="adm-tbl-sub">{u.type==='Pro'?'Éleveur Pro':'Particulier'}</div></div>
                            </div>
                          </td>
                          <td style={{ color:'var(--slate)', fontSize:12 }}>{u.email}</td>
                          <td style={{ fontSize:12, color:'var(--slate)' }}>{u.city || '—'}</td>
                          <td style={{ fontWeight:700, color:'var(--blue)', textAlign:'center' }}>{u.purchases ?? 0}</td>
                          <td style={{ fontSize:11, color:'var(--slate-lt)' }}>{u.joined}</td>
                          <td>
                            <span className={`adm-sp adm-sp-${u.status}`} style={{ cursor:'pointer' }} onClick={() => {
                              setUsers(us => us.map(x => x.id===u.id?{...x,status:x.status==='active'?'banned':'active'}:x))
                              showToast('Statut modifié')
                            }}>
                              {u.status==='active'?'Actif':'Banni'}
                            </span>
                          </td>
                          <td>
                            <div style={{ display:'flex', gap:4 }}>
                              <button className="adm-ta-btn adm-ta-ban" onClick={async () => {
                                const newActive = u.status === 'banned'
                                try { await usersAPI.updateStatus(u.id, newActive) } catch {}
                                setUsers(us => us.map(x => x.id===u.id?{...x,status:newActive?'active':'banned'}:x))
                                showToast('Statut modifié')
                              }}>
                                {u.status==='banned'?<IcoCheck />:<IcoBan />}
                              </button>
                              <button className="adm-ta-btn adm-ta-del" onClick={() => setConfirmDel({type:'user',id:u.id,label:u.name})}><IcoTrash /></button>
                            </div>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  {totalUpg > 1 && (
                    <div className="adm-pagination">
                      <button className="adm-page-btn" disabled={usersPage===1} onClick={() => setUsersPage(p=>p-1)}>←</button>
                      {Array.from({length:totalUpg},(_,i)=>i+1).map(p => (
                        <button key={p} className={`adm-page-btn${usersPage===p?' active':''}`} onClick={() => setUsersPage(p)}>{p}</button>
                      ))}
                      <button className="adm-page-btn" disabled={usersPage===totalUpg} onClick={() => setUsersPage(p=>p+1)}>→</button>
                      <span className="adm-page-info">{(usersPage-1)*U_PER+1}–{Math.min(usersPage*U_PER,users.length)} / {users.length}</span>
                    </div>
                  )}
                </div>
              </>
            )
          })()}

          {/* ══ COMMANDES ══ */}
          {view === 'orders' && (() => {
            const O_PER = 10
            const totalOpg = Math.max(1, Math.ceil(orders.length / O_PER))
            const pagedOrders = orders.slice((ordersAdmPage-1)*O_PER, ordersAdmPage*O_PER)
            const fmtPayMethod = (method, brand, last4) => {
              if (method === 'free')   return 'Don gratuit'
              if (method === 'wire')   return 'Virement bancaire'
              if (method === 'paypal') return 'PayPal'
              if (method === 'link')   return 'Stripe Link'
              if (method === 'card' && brand && last4) {
                const b = brand.charAt(0).toUpperCase() + brand.slice(1)
                return `${b} \u2022\u2022\u2022\u2022${last4}`
              }
              return 'Carte bancaire'
            }
            return (
              <>
                <div className="adm-page-hd"><div className="adm-hd-title"><IcoOrders /> Commandes</div></div>
                <div className="adm-tbl-wrap">
                  <div className="adm-tbl-head"><div className="adm-tbl-title">Toutes les commandes</div></div>
                  <div className="adm-tbl-scroll">
                    <table className="adm-tbl">
                      <thead><tr><th>Référence</th><th>Acheteur</th><th>Animal</th><th>Montant</th><th>Moyen de paiement</th><th>Date</th><th>Paiement</th><th>Statut</th></tr></thead>
                      <tbody>{pagedOrders.map(o => (
                        <tr key={o.id}>
                          <td>
                            <span style={{fontFamily:"'Sora',sans-serif",fontSize:11,fontWeight:700,color:'var(--gold-dk)',background:'var(--gold-lt)',padding:'3px 8px',borderRadius:6,whiteSpace:'nowrap'}}>{o.id}</span>
                            {o.wireReference && <div style={{fontSize:10,color:'var(--slate-lt)',marginTop:2,fontFamily:'monospace'}}>{o.wireReference}</div>}
                          </td>
                          <td>{o.buyer}</td>
                          <td style={{ fontSize:12 }}>{o.animal}</td>
                          <td><strong style={{color:'var(--gold-dk)',fontSize:14}}>{fmt(o.amount)}</strong></td>
                          <td>
                            <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontSize:12 }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                              {fmtPayMethod(o.payMethod, o.cardBrand, o.cardLast4)}
                            </span>
                          </td>
                          <td style={{ fontSize:11, color:'var(--slate-lt)' }}>{o.date}</td>
                          <td>
                            {o.paymentStatus === 'pending_wire' ? (
                              <button
                                onClick={async () => {
                                  if (!window.confirm(`Confirmer la réception du virement pour la commande ${o.id} ?`)) return
                                  try {
                                    const mongoId = o._mongoId || o.id
                                    await ordersAPI.confirmWire(mongoId)
                                    setLocalOrders(lo => lo.map(x => x.id === o.id
                                      ? { ...x, paymentStatus: 'paid', status: 'confirmed' }
                                      : x
                                    ))
                                  } catch (e) { alert('Erreur : ' + e.message) }
                                }}
                                style={{
                                  fontSize:11, fontWeight:700, padding:'5px 10px',
                                  borderRadius:6, border:'1.5px solid #d9ae5c',
                                  background:'#fffbef', color:'#a87c2e',
                                  cursor:'pointer', whiteSpace:'nowrap',
                                }}
                              >
                                ✓ Confirmer réception
                              </button>
                            ) : o.paymentStatus === 'paid' ? (
                              <span style={{fontSize:11,fontWeight:700,color:'var(--green-dk)',background:'var(--green-lt)',padding:'3px 10px',borderRadius:6}}>
                                ✓ Reçu
                              </span>
                            ) : (
                              <span style={{fontSize:11,color:'var(--slate-lt)'}}>—</span>
                            )}
                          </td>
                          <td>
                            <select
                              value={o.status}
                              onChange={e => updateOrderStatus(o.id, e.target.value)}
                              style={{
                                fontSize:11, fontWeight:700, padding:'4px 8px',
                                borderRadius:6, border:'1px solid var(--border)',
                                background: o.status==='delivered'?'var(--green-lt)': o.status==='shipped'?'#FFF8E1': o.status==='confirmed'?'#EEF2FF':'var(--bg)',
                                color: o.status==='delivered'?'var(--green-dk)': o.status==='shipped'?'#E65100': o.status==='confirmed'?'var(--blue)':'var(--slate)',
                                cursor:'pointer', outline:'none',
                              }}
                            >
                              <option value="pending">En attente</option>
                              <option value="confirmed">Confirmé</option>
                              <option value="shipped">Expédié</option>
                              <option value="delivered">Livré</option>
                            </select>
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                  {totalOpg > 1 && (
                    <div className="adm-pagination">
                      <button className="adm-page-btn" disabled={ordersAdmPage===1} onClick={() => setOrdersAdmPage(p=>p-1)}>←</button>
                      {Array.from({length:totalOpg},(_,i)=>i+1).map(p => (
                        <button key={p} className={`adm-page-btn${ordersAdmPage===p?' active':''}`} onClick={() => setOrdersAdmPage(p)}>{p}</button>
                      ))}
                      <button className="adm-page-btn" disabled={ordersAdmPage===totalOpg} onClick={() => setOrdersAdmPage(p=>p+1)}>→</button>
                      <span className="adm-page-info">{(ordersAdmPage-1)*O_PER+1}–{Math.min(ordersAdmPage*O_PER,orders.length)} / {orders.length}</span>
                    </div>
                  )}
                </div>
              </>
            )
          })()}

          {/* ══ MESSAGES ══ */}
          {view === 'messages' && (() => {
            const selectedConv = conversations.find(c => c.user._id === selectedConvId)

            const listConvTitle = (c) => c.listing ? c.listing.title : 'Questions générales'
            const listConvBreed = (c) => c.listing ? c.listing.breed : 'Support'
            const listConvImg   = (c) => c.listing?.images?.[0]?.url

            return (
              <>
                <div className="adm-page-hd">
                  <div className="adm-hd-title"><IcoMessages /> Messages</div>
                  {unreadMsgs > 0 && (
                    <span className="adm-sp adm-sp-pending" style={{ padding:'6px 12px' }}>
                      {unreadMsgs} non lu{unreadMsgs > 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                <div className="adm-tbl-wrap adm-msg-split-3">

                  {/* ── Panneau 1 : utilisateurs ── */}
                  <div className="adm-conv-list">
                    <div className="adm-tbl-head">
                      <div className="adm-tbl-title">Utilisateurs</div>
                      <span className="adm-page-info" style={{ margin:0 }}>{conversations.length}</span>
                    </div>
                    <div className="adm-conv-scroll">
                      {conversations.length === 0 ? (
                        <div className="adm-conv-empty-msg">Aucune conversation pour l'instant.</div>
                      ) : conversations.map(c => {
                        const initials = (c.user.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                        return (
                          <div
                            key={c.user._id}
                            className={`adm-conv-item${selectedConvId === c.user._id ? ' selected' : ''}`}
                            onClick={() => openUserConversation(c.user._id)}
                          >
                            <div className="adm-user-av" style={{ flexShrink:0 }}>{initials}</div>
                            <div style={{ flex:1, minWidth:0 }}>
                              <div className="adm-conv-row">
                                <span className={`adm-conv-name${c.unreadCount > 0 ? ' unread' : ''}`}>{c.user.name}</span>
                                <span className="adm-conv-date">{fmtDate(c.lastDate)}</span>
                              </div>
                              <div className="adm-conv-row">
                                <span className="adm-conv-preview">{c.lastMessage}</span>
                                {c.unreadCount > 0 && <span className="adm-nav-badge" style={{ marginLeft:6 }}>{c.unreadCount}</span>}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* ── Panneau 2 : annonces discutées avec l'utilisateur sélectionné (tiroir) ── */}
                  <div className={`adm-listing-conv-list${listingPanelOpen ? ' open' : ''}`}>
                    <div className="adm-tbl-head">
                      <div className="adm-tbl-title">Annonces</div>
                      {userListingConvs.length > 0 && <span className="adm-page-info" style={{ margin:0 }}>{userListingConvs.length}</span>}
                    </div>
                    {!selectedConvId ? (
                      <div className="adm-conv-empty-msg">Sélectionnez un utilisateur.</div>
                    ) : listingConvsLoading ? (
                      <div className="adm-conv-empty-msg">Chargement…</div>
                    ) : userListingConvs.length === 0 ? (
                      <div className="adm-conv-empty-msg">Aucune annonce discutée.</div>
                    ) : (
                      <div className="adm-conv-scroll">
                        {userListingConvs.map(c => (
                          <div
                            key={c.key}
                            className={`adm-conv-item${selectedListingKey === c.key ? ' selected' : ''}`}
                            onClick={() => openListingThread(selectedConvId, c.key)}
                          >
                            {listConvImg(c) ? (
                              <img className="adm-conv-item-img" src={listConvImg(c)} alt="" loading="lazy" />
                            ) : (
                              <div className="adm-conv-item-icon"><IcoMessages /></div>
                            )}
                            <div style={{ flex:1, minWidth:0 }}>
                              <div className="adm-conv-breed">{listConvBreed(c)}</div>
                              <div className="adm-conv-row">
                                <span className={`adm-conv-name${c.unreadCount > 0 ? ' unread' : ''}`}>{listConvTitle(c)}</span>
                                <span className="adm-conv-date">{fmtDate(c.lastDate)}</span>
                              </div>
                              <div className="adm-conv-row">
                                <span className="adm-conv-preview">{c.lastMessage}</span>
                                {c.unreadCount > 0 && <span className="adm-nav-badge" style={{ marginLeft:6 }}>{c.unreadCount}</span>}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* ── Panneau 3 : fil de la conversation sélectionnée ── */}
                  <div className="adm-conv-thread">
                    {selectedConv && selectedListingKey ? (
                      <>
                        <div className="adm-tbl-head">
                          <div style={{ display:'flex', alignItems:'center', gap:9 }}>
                            <button
                              className="adm-back-to-listings"
                              onClick={() => { setListingPanelOpen(true); refreshUserListingConvs() }}
                              title="Voir les annonces discutées"
                              type="button"
                            >
                              <IcoChevronLeft />
                            </button>
                            <div className="adm-user-av">
                              {(selectedConv.user.name || '?').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <div>
                              <div className="adm-tbl-name">{selectedConv.user.name}</div>
                              <div className="adm-tbl-sub">{selectedConv.user.email}</div>
                            </div>
                          </div>
                        </div>
                        <ChatThread
                          key={`${selectedConvId}-${selectedListingKey}`}
                          messages={threadMsgs}
                          loading={threadLoading}
                          isMine={(m) => m.sender?.role === 'admin'}
                          onSend={sendAdminChatMessage}
                          sending={chatSending}
                          placeholder={`Répondre à ${selectedConv.user.name.split(' ')[0]}…`}
                          emptyTitle="Aucun message"
                          emptyHint="Cette conversation ne contient aucun message pour le moment."
                          banner={threadBanner}
                          onDeleteMessage={handleDeleteMessage}
                          onDeleteConversation={handleDeleteConversation}
                          peerStatus={userStatus}
                        />
                      </>
                    ) : (
                      <div className="adm-conv-empty">
                        <IcoMessages />
                        <span>{selectedConvId ? 'Sélectionnez une annonce' : 'Sélectionnez une conversation'}</span>
                      </div>
                    )}
                  </div>

                </div>
              </>
            )
          })()}


          {/* ══ MES DOCUMENTS ══ */}
          {view === 'documents' && (
            <>
              <div className="adm-page-hd">
                <div>
                  <div className="adm-hd-title"><IcoShield /> Mes documents</div>
                  <div className="adm-hd-sub">Adoption · Livraison · Factures · Carnet · Attestation · Identification · Cession</div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  {docTab==='adoption' && selectedAdoption && <button className="adm-btn adm-btn-ghost" onClick={()=>setSelectedAdoption(null)}>← Retour</button>}
                  {docTab==='adoption' && adoptionForms.some(f=>!f.readByAdmin) && <button className="adm-btn adm-btn-primary" onClick={async()=>{await adoptionAPI.markAllReadAdmin();loadAdoptionForms();showToast('Tout marqué comme lu')}}>Tout marquer lu</button>}
                  {docTab==='livraison'     && !showLfForm && <button className="adm-btn adm-btn-primary" onClick={()=>setShowLfForm(true)}>+ Nouveau formulaire</button>}
                  {docTab==='facture'       && !showFfForm && <button className="adm-btn adm-btn-primary" onClick={()=>setShowFfForm(true)}>+ Nouvelle facture</button>}
                  {docTab==='carnet'        && !showCsForm && <button className="adm-btn adm-btn-primary" onClick={()=>setShowCsForm(true)}>+ Nouveau carnet</button>}
                  {docTab==='attestation'   && !showAvForm && <button className="adm-btn adm-btn-primary" onClick={()=>setShowAvForm(true)}>+ Nouvelle attestation</button>}
                  {docTab==='identification'&& !showDiForm && <button className="adm-btn adm-btn-primary" onClick={()=>setShowDiForm(true)}>+ Nouveau document</button>}
                  {docTab==='cession'       && !showAcForm && <button className="adm-btn adm-btn-primary" onClick={()=>setShowAcForm(true)}>+ Nouvelle cession</button>}
                </div>
              </div>
              <div className="adm-doc-tabs">
                {[
                  {id:'adoption',       label:"Formulaires d'adoption",   count:adoptionForms.length,  badge:adoptionBadge},
                  {id:'livraison',      label:'Formulaires de livraison', count:livraisonForms.length},
                  {id:'facture',        label:'Factures de livraison',    count:facturesForms.length},
                  {id:'carnet',         label:'Carnet de santé',          count:carnetForms.length},
                  {id:'attestation',    label:'Attestation vétérinaire',  count:attestForms.length},
                  {id:'identification', label:"Document d'identification",count:identifForms.length},
                  {id:'cession',        label:'Attestation de cession',   count:cessionForms.length},
                ].map(t=>(
                  <button key={t.id} className={`adm-doc-tab${docTab===t.id?' active':''}`} onClick={()=>{setDocTab(t.id);setShowLfForm(false);setShowFfForm(false);setShowCsForm(false);setShowAvForm(false);setShowDiForm(false);setShowAcForm(false);setCarnetPage(1);setAttestPage(1);setIdentifPage(1);setCessionPage(1);if(t.id==='adoption'){loadAdoptionForms()}else{loadDeliveryData()}}}>
                    {t.label}{t.count>0&&<span className="adm-doc-tab-badge">{t.count}</span>}{t.badge>0&&<span className="adm-doc-tab-badge" style={{background:'#FEE2E2',color:'#DC2626'}}>{t.badge}</span>}
                  </button>
                ))}
              </div>
              {docTab==='adoption' && (adoptionLoading ? (
                <div className="adm-empty"><div className="catalog-spinner" /><p>Chargement…</p></div>
              ) : selectedAdoption ? (
                /* ── Vue détail d'un formulaire ── */
                <div className="adm-card" style={{ maxWidth:800 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
                    <div>
                      <div style={{ fontFamily:"'Syne',sans-serif", fontSize:18, fontWeight:800, color:'var(--blue)' }}>{selectedAdoption.formNumber}</div>
                      <div style={{ fontSize:12, color:'var(--slate)', marginTop:2 }}>
                        Soumis le {new Date(selectedAdoption.createdAt).toLocaleDateString('fr-FR', { day:'2-digit', month:'long', year:'numeric' })}
                        {selectedAdoption.submittedBy && ` par ${selectedAdoption.submittedBy.name || selectedAdoption.submittedBy.email}`}
                      </div>
                    </div>
                    <button
                      className="adm-btn adm-btn-primary"
                      disabled={downloadingAdoptionId === selectedAdoption._id}
                      onClick={() => downloadAdoptionPDFAdmin(selectedAdoption)}
                    >
                      {downloadingAdoptionId === selectedAdoption._id ? '…' : '⬇ Télécharger PDF'}
                    </button>
                  </div>

                  {adoptionDetailLoading ? (
                    <div style={{ textAlign:'center', padding:40 }}><div className="catalog-spinner" /></div>
                  ) : (
                    <div style={{ display:'grid', gap:12 }}>
                      {/* Section 1 */}
                      {selectedAdoption.s1 && (
                        <div className="adm-settings-card" style={{ padding:'14px 18px' }}>
                          <div className="adm-settings-title" style={{ marginBottom:10 }}>1. Informations personnelles</div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
                            {[['Nom et prénom', selectedAdoption.s1.nomPrenom], ['Date de naissance', selectedAdoption.s1.dateNaissance], ['Adresse', selectedAdoption.s1.adresse], ['Téléphone', selectedAdoption.s1.telephone], ['Email', selectedAdoption.s1.email], ['Profession', selectedAdoption.s1.profession], ['Situation familiale', selectedAdoption.s1.situationFamil], ['Adultes au foyer', selectedAdoption.s1.nbAdultes], ['Enfants', selectedAdoption.s1.nbEnfants]].map(([l, v]) => v ? (
                              <div key={l}><div style={{ fontSize:10, fontWeight:700, color:'var(--gold)', textTransform:'uppercase' }}>{l}</div><div style={{ fontSize:13, color:'var(--blue)' }}>{v}</div></div>
                            ) : null)}
                          </div>
                        </div>
                      )}
                      {/* Section 2 */}
                      {selectedAdoption.s2 && (
                        <div className="adm-settings-card" style={{ padding:'14px 18px' }}>
                          <div className="adm-settings-title" style={{ marginBottom:10 }}>2. Logement</div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
                            {[['Type', selectedAdoption.s2.typeLogement], ['Propriétaire/Loc.', selectedAdoption.s2.proprietaire], ['Superficie', selectedAdoption.s2.superficie], ['Jardin/Balcon', selectedAdoption.s2.jardinBalcon], ['Accès extérieur', selectedAdoption.s2.accesExterieur], ['Temps seul', selectedAdoption.s2.tempsSeul], ['Lieu couchage', selectedAdoption.s2.lieuDodo]].map(([l, v]) => v ? (
                              <div key={l}><div style={{ fontSize:10, fontWeight:700, color:'var(--gold)', textTransform:'uppercase' }}>{l}</div><div style={{ fontSize:13, color:'var(--blue)' }}>{v}</div></div>
                            ) : null)}
                          </div>
                        </div>
                      )}
                      {/* Section 3 */}
                      {selectedAdoption.s3 && (
                        <div className="adm-settings-card" style={{ padding:'14px 18px' }}>
                          <div className="adm-settings-title" style={{ marginBottom:10 }}>3. Expérience & attentes</div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
                            {[['Animaux passés', selectedAdoption.s3.animalsPasses], ['Raison', selectedAdoption.s3.raisonAdoption], ['Type animal', selectedAdoption.s3.typeAnimal], ['Préférences', selectedAdoption.s3.preferences], ['Coûts', selectedAdoption.s3.conscientCouts], ['Comportement', selectedAdoption.s3.problComportement], ['Absence', selectedAdoption.s3.absenceProlongee]].map(([l, v]) => v ? (
                              <div key={l}><div style={{ fontSize:10, fontWeight:700, color:'var(--gold)', textTransform:'uppercase' }}>{l}</div><div style={{ fontSize:13, color:'var(--blue)' }}>{v}</div></div>
                            ) : null)}
                          </div>
                        </div>
                      )}
                      {/* Section 4 */}
                      {selectedAdoption.s4 && (
                        <div className="adm-settings-card" style={{ padding:'14px 18px' }}>
                          <div className="adm-settings-title" style={{ marginBottom:10 }}>4. Animaux actuels</div>
                          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 20px' }}>
                            {[['Animaux actuels', selectedAdoption.s4.animauxActuels], ['Réaction', selectedAdoption.s4.reactionAnimaux], ['Vaccinés/Id.', selectedAdoption.s4.vaccinesId]].map(([l, v]) => v ? (
                              <div key={l}><div style={{ fontSize:10, fontWeight:700, color:'var(--gold)', textTransform:'uppercase' }}>{l}</div><div style={{ fontSize:13, color:'var(--blue)' }}>{v}</div></div>
                            ) : null)}
                          </div>
                        </div>
                      )}
                      {/* Section 5 */}
                      {selectedAdoption.s5 && (
                        <div className="adm-settings-card" style={{ padding:'14px 18px' }}>
                          <div className="adm-settings-title" style={{ marginBottom:10 }}>5. Engagements</div>
                          <div style={{ display:'grid', gap:6 }}>
                            {[['Engagement long terme', selectedAdoption.s5.engagementLongTerme], ['Accepte les visites', selectedAdoption.s5.accepteVisites], ['Soins vétérinaires', selectedAdoption.s5.soinVeteriaire], ['Contrat d\'adoption', selectedAdoption.s5.accepteContrat]].map(([l, v]) => (
                              <div key={l} style={{ display:'flex', gap:8, alignItems:'center' }}>
                                <span style={{ color: v ? 'var(--green-dk)' : 'var(--slate-lt)', fontWeight:700, fontSize:14 }}>{v ? '✓' : '✗'}</span>
                                <span style={{ fontSize:13, color:'var(--blue)' }}>{l}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Section 6 */}
                      {selectedAdoption.s6?.autresInfos && (
                        <div className="adm-settings-card" style={{ padding:'14px 18px' }}>
                          <div className="adm-settings-title" style={{ marginBottom:10 }}>6. Informations additionnelles</div>
                          <div style={{ fontSize:13, color:'var(--blue)', whiteSpace:'pre-wrap' }}>{selectedAdoption.s6.autresInfos}</div>
                        </div>
                      )}
                      {/* Signature */}
                      {selectedAdoption.signatureDataUrl && (
                        <div className="adm-settings-card" style={{ padding:'14px 18px' }}>
                          <div className="adm-settings-title" style={{ marginBottom:10 }}>Signature</div>
                          <img src={selectedAdoption.signatureDataUrl} alt="Signature" style={{ maxWidth:300, border:'1px solid var(--border)', borderRadius:6, background:'#fff', padding:4 }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* ── Liste des formulaires ── */
                adoptionForms.length === 0 ? (
                  <div className="adm-empty">
                    <div className="adm-empty-icon"><IcoShield /></div>
                    <h3>Aucun formulaire d'adoption</h3>
                    <p>Aucun formulaire d'adoption soumis pour le moment.</p>
                  </div>
                ) : (
                  <div className="adm-tbl-wrap">
                    <div className="adm-tbl-head">
                      <div className="adm-tbl-title"><IcoShield /> Formulaires d'adoption <span style={{fontFamily:"'Sora',sans-serif",fontSize:12,fontWeight:400,color:'var(--slate)',marginLeft:6}}>({adoptionForms.length})</span></div>
                    </div>
                    <div className="adm-tbl-scroll"><table className="adm-tbl">
                      <thead>
                        <tr>
                          <th>Référence</th>
                          <th>Adoptant</th>
                          <th>Email</th>
                          <th>Date</th>
                          <th>Statut</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adoptionForms.map(f => (
                          <tr key={f._id} style={{ background: !f.readByAdmin ? 'rgba(217,174,92,0.08)' : undefined }}>
                            <td><span className="adm-adopt-ref">{f.formNumber || '—'}</span></td>
                            <td>{f.submittedBy?.name || f.s1?.nomPrenom || '—'}</td>
                            <td style={{ fontSize:12, color:'var(--slate)' }}>{f.submittedBy?.email || '—'}</td>
                            <td style={{ fontSize:12 }}>{fmtDate(f.createdAt)}</td>
                            <td>
                              {!f.readByAdmin
                                ? <span className="adm-sp adm-sp-pending">Nouveau</span>
                                : <span className="adm-sp adm-sp-active">Lu</span>}
                            </td>
                            <td>
                              <div style={{ display:'flex', gap:6 }}>
                                <button className="adm-btn adm-btn-ghost" style={{ padding:'4px 10px', fontSize:12 }} onClick={() => openAdoptionDetail(f)}>Voir</button>
                                <button
                                  className="adm-dl-btn"
                                  disabled={downloadingAdoptionId === f._id}
                                  onClick={() => downloadAdoptionPDFAdmin(f)}
                                >
                                  {downloadingAdoptionId === f._id ? '…' : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> PDF</>}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table></div>
                  </div>
                )
              ))}

              {deliveryLoading && docTab!=='adoption' && (<div className="adm-empty"><div className="catalog-spinner" /><p>Chargement…</p></div>)}
              {!deliveryLoading && docTab==='livraison' && (showLfForm ? (
                <div className="adm-card" style={{maxWidth:700}}>
                  <h3 style={{fontFamily:"'Syne',sans-serif",fontSize:17,fontWeight:800,color:'var(--blue)',marginBottom:20}}>📋 Nouveau formulaire de livraison</h3>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 20px'}}>
                    {/* Section Émetteur */}
                    <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginBottom:2}}>Émetteur</div>
                    {[["Nom de l'entreprise",'entreprise'],['Localisation / Adresse','localisation'],['Téléphone','tel'],['Responsable / Livreur','responsable'],["ID entreprise (SIRET)",'idEntreprise']].map(([label,key])=>(
                      <div key={key} style={{display:'flex',flexDirection:'column',gap:4}}>
                        <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>
                        <input className="adm-input" value={lf[key]||''} onChange={e=>setLf(p=>({...p,[key]:e.target.value}))} placeholder={label}/>
                      </div>
                    ))}
                    {/* Section Client */}
                    <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginTop:8,marginBottom:2}}>Client / Destinataire</div>
                    <div style={{gridColumn:'1/-1',background:'var(--gold-lt)',border:'1.5px solid var(--gold)',borderRadius:10,padding:'12px 14px'}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:6}}>👤 Rechercher un utilisateur</label>
                      {lfUserResolved?(
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <div style={{flex:1}}><div style={{fontWeight:700,color:'var(--blue)',fontSize:14}}>{lfUserResolved.name}</div><div style={{fontSize:12,color:'var(--slate)'}}>{lfUserResolved.email}</div></div>
                          <span style={{fontSize:11,fontWeight:700,color:'var(--green-dk)',background:'var(--green-lt)',padding:'3px 10px',borderRadius:100}}>✓ Lié</span>
                          <button className="adm-ta-btn adm-ta-del" style={{padding:'4px 8px'}} onClick={()=>{setLfUserResolved(null);setLfUserSearch('');setLfUserMatches([]);setLf(p=>({...p,clientEmail:'',clientNom:''}));}}>✕</button>
                        </div>
                      ):(
                        <div style={{position:'relative'}}>
                          <input className="adm-input" style={{marginBottom:0}} placeholder="Rechercher par nom ou email…" value={lfUserSearch}
                            onChange={e=>{const v=e.target.value;setLfUserSearch(v);setLf(p=>({...p,clientEmail:v}));_searchUsers(v,setLfUserMatches)}}/>
                          {lfUserMatches.length>0&&(<div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:60,background:'var(--white)',border:'1.5px solid var(--gold)',borderRadius:9,boxShadow:'0 8px 24px rgba(0,0,0,.13)',overflow:'hidden',marginTop:2}}>
                            {lfUserMatches.map(u=>(<div key={u.id||u.email} style={{padding:'9px 14px',cursor:'pointer',borderBottom:'1px solid var(--border)'}}
                              onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'} onMouseLeave={e=>e.currentTarget.style.background=''}
                              onClick={()=>{setLfUserResolved({name:u.name,email:u.email});setLf(p=>({...p,clientEmail:u.email,clientNom:u.name}));setLfUserSearch(u.email);setLfUserMatches([])}}>
                              <div style={{fontWeight:700,color:'var(--blue)',fontSize:13}}>{u.name}</div><div style={{fontSize:12,color:'var(--slate)'}}>{u.email}</div>
                            </div>))}
                          </div>)}
                          {lfUserSearch.trim()&&!lfUserResolved&&lfUserMatches.length===0&&(<div style={{marginTop:6,fontSize:12,color:'var(--slate)'}}>⚠ Aucun compte trouvé.</div>)}
                        </div>
                      )}
                    </div>
                    {[['Nom du client','clientNom'],['Téléphone client','clientTel'],['Adresse client','clientAdresse']].map(([label,key])=>(
                      <div key={key} style={{display:'flex',flexDirection:'column',gap:4}}>
                        <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>
                        <input className="adm-input" value={lf[key]||''} onChange={e=>setLf(p=>({...p,[key]:e.target.value}))} placeholder={label}/>
                      </div>
                    ))}
                    {/* Section Livraison */}
                    <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginTop:8,marginBottom:2}}>Informations de livraison</div>
                    {/* Date de livraison */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Date de livraison</label>
                      <input className="adm-input" type="date" value={lf.date||''} onChange={e=>setLf(p=>({...p,date:e.target.value}))}/>
                    </div>
                    {/* Date de commande */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Date de commande</label>
                      <input className="adm-input" type="date" value={lf.dateCommande||''} onChange={e=>setLf(p=>({...p,dateCommande:e.target.value}))}/>
                    </div>
                    {/* Heure prévue */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Heure prévue</label>
                      <input className="adm-input" type="time" value={lf.heure||''} onChange={e=>setLf(p=>({...p,heure:e.target.value}))}/>
                    </div>
                    {/* Mode de livraison */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Mode de livraison</label>
                      <input className="adm-input" value={lf.modeTransport||''} onChange={e=>setLf(p=>({...p,modeTransport:e.target.value}))} placeholder="Ex: Avion, Camion…"/>
                    </div>
                    {/* Départ */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Départ</label>
                      <input className="adm-input" value={lf.depart||''} onChange={e=>setLf(p=>({...p,depart:e.target.value}))} placeholder="Ville de départ"/>
                    </div>
                    {/* Arrivée */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Arrivée</label>
                      <input className="adm-input" value={lf.arrivee||''} onChange={e=>setLf(p=>({...p,arrivee:e.target.value}))} placeholder="Ville d'arrivée"/>
                    </div>
                    {/* Prix transport */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Prix transport (€)</label>
                      <input className="adm-input" type="number" min="0" step="0.01" value={lf.prixTransport||''} onChange={e=>setLf(p=>({...p,prixTransport:e.target.value}))} placeholder="0.00"/>
                    </div>
                    {/* Section Animal */}
                    <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginTop:8,marginBottom:2}}>Animal</div>
                    {/* Nom animal */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Désignation / Nom animal</label>
                      <input className="adm-input" value={lf.animal||''} onChange={e=>setLf(p=>({...p,animal:e.target.value}))} placeholder="Ex: Rex"/>
                    </div>
                    {/* Espèce */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Espèce</label>
                      <input className="adm-input" value={lf.espece||''} onChange={e=>setLf(p=>({...p,espece:e.target.value}))} placeholder="Ex: Chien"/>
                    </div>
                    {/* Race */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Race</label>
                      <input className="adm-input" value={lf.race||''} onChange={e=>setLf(p=>({...p,race:e.target.value}))} placeholder="Ex: Labrador"/>
                    </div>
                    {/* Âge */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Âge</label>
                      <input className="adm-input" type="number" min="0" value={lf.ageAnimal||''} onChange={e=>setLf(p=>({...p,ageAnimal:e.target.value}))} placeholder="Ex: 3"/>
                    </div>
                    {/* Poids */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Poids</label>
                      <input className="adm-input" type="number" min="0" step="0.1" value={lf.poids||''} onChange={e=>setLf(p=>({...p,poids:e.target.value}))} placeholder="Ex: 12.5"/>
                    </div>
                    {/* Couleur */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Couleur / Signe distinctif</label>
                      <input className="adm-input" value={lf.couleur||''} onChange={e=>setLf(p=>({...p,couleur:e.target.value}))} placeholder="Ex: Robe dorée"/>
                    </div>
                    {/* Puce */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>N° Puce électronique</label>
                      <input className="adm-input" value={lf.puce||''} onChange={e=>setLf(p=>({...p,puce:e.target.value}))} placeholder="Ex: 250269612345678"/>
                    </div>
                  </div>
                  {/* Section Signature admin uniquement — le client signe depuis son espace */}
                  <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginTop:8,marginBottom:8}}>Votre signature</div>
                  <div style={{maxWidth:360}}>
                    <SignatureCanvas canvasRef={lfSigEntRef} label="Signature Entreprise" onSign={setLfSigEntreprise} />
                  </div>
                  <div style={{display:'flex',gap:10,marginTop:20}}>
                    <button className="adm-btn adm-btn-primary" onClick={async()=>{try{const res=await deliveryAPI.createForm({...lf,adminSignature:lfSigEntreprise||null});const nf=res.form;setLivraisonForms(p=>[nf,...p]);generateLivraisonPDF({...lf,formNumber:nf.formNumber,sigEntreprise:lfSigEntreprise,sigClient:null});setShowLfForm(false);setLfSigEntreprise(null);setLfSigClient(null);setLfUserResolved(null);setLfUserSearch('');setLfUserMatches([]);showToast(lfSigEntreprise?'Formulaire créé & envoyé pour co-signature ✓':'Formulaire créé ✓')}catch(e){showToast('Erreur : '+(e.message||'impossible'))}}}>📄 Créer & Télécharger PDF</button>
                    <button className="adm-btn adm-btn-ghost" onClick={()=>setShowLfForm(false)}>Annuler</button>
                  </div>
                </div>
              ):livraisonForms.length===0?(
                <div style={{textAlign:'center',padding:'52px 20px'}}>
                  <div className="adm-empty">
                    <div className="adm-empty-icon"><IcoTruck /></div>
                    <h3>Aucun formulaire de livraison</h3>
                    <p>Utilisez le bouton <strong>+ Nouveau formulaire</strong> en haut à droite pour créer votre premier formulaire.</p>
                  </div>
                </div>
              ):(
                <div className="adm-tbl-wrap">
                  <div className="adm-tbl-head">
                    <div className="adm-tbl-title"><IcoTruck /> Formulaires de livraison <span style={{fontFamily:"'Sora',sans-serif",fontSize:12,fontWeight:400,color:'var(--slate)',marginLeft:6}}>({livraisonForms.length})</span></div>
                  </div>
                  <div className="adm-tbl-scroll"><table className="adm-tbl">
                    <thead><tr><th>Référence</th><th>Client</th><th>Animal</th><th>Trajet</th><th>Prix</th><th>Date</th><th></th></tr></thead>
                    <tbody>{livraisonForms.map(f=>(
                      <tr key={f._id||f.id}>
                        <td><span style={{fontFamily:"'Sora',sans-serif",fontSize:11,fontWeight:700,color:'var(--gold-dk)',background:'var(--gold-lt)',padding:'3px 8px',borderRadius:6}}>{f.formNumber||'—'}</span></td>
                        <td>
                          <div className="adm-tbl-name">{f.clientNom||'—'}</div>
                          <div className="adm-tbl-sub">{f.clientEmail||f.clientTel}</div>
                        </td>
                        <td>
                          <div style={{fontWeight:600,color:'var(--blue)'}}>{f.animal||'—'}</div>
                          <div className="adm-tbl-sub">{f.espece}</div>
                        </td>
                        <td>
                          <div style={{fontSize:12,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                            <span style={{color:'var(--green-dk)',fontWeight:600}}>🚩 {f.depart||'—'}</span>
                            <span style={{color:'var(--slate-lt)'}}>→</span>
                            <span style={{color:'#dc2626',fontWeight:600}}>🏁 {f.arrivee||'—'}</span>
                          </div>
                        </td>
                        <td><strong style={{color:'var(--gold-dk)'}}>{f.prixTransport?f.prixTransport+' €':'—'}</strong></td>
                        <td style={{fontSize:12,color:'var(--slate)',whiteSpace:'nowrap'}}>{f.date}</td>
                        <td>
                          <div style={{display:'flex',gap:6}}>
                            <button className="adm-dl-btn" onClick={()=>generateLivraisonPDF({...f,sigEntreprise:f.adminSignature||f.sigEntreprise||null,sigClient:f.userSignature||f.sigClient||null})} title="Télécharger PDF">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                              PDF
                            </button>
                            <button className="adm-ta-btn adm-ta-del" onClick={async()=>{try{await deliveryAPI.deleteForm(f._id||f.id);setLivraisonForms(p=>p.filter(x=>(x._id||x.id)!==(f._id||f.id)))}catch(e){showToast('Erreur')}}} title="Supprimer"><IcoTrash/></button>
                          </div>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                </div>
              ))}

              {!deliveryLoading && docTab==='facture' && (showFfForm ? (
                <div className="adm-card" style={{maxWidth:700}}>
                  <h3 style={{fontFamily:"'Syne',sans-serif",fontSize:17,fontWeight:800,color:'var(--blue)',marginBottom:20}}>🧾 Nouvelle facture de livraison</h3>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 20px'}}>
                    {/* Émetteur */}
                    <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginBottom:2}}>Émetteur</div>
                    {[["Nom de l'entreprise",'entreprise'],['Localisation / Adresse','localisation'],['Téléphone','tel'],['Email entreprise','emailEntreprise']].map(([label,key])=>(
                      <div key={key} style={{display:'flex',flexDirection:'column',gap:4}}>
                        <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>
                        <input className="adm-input" value={ff[key]||''} onChange={e=>setFf(p=>({...p,[key]:e.target.value}))} placeholder={label}/>
                      </div>
                    ))}
                    {/* Facture */}
                    <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginTop:8,marginBottom:2}}>Informations facture</div>
                    {/* N° facture */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>N° facture</label>
                      <input className="adm-input" value={ff.ref||''} onChange={e=>setFf(p=>({...p,ref:e.target.value}))} placeholder="Ex: FACT-2025-001"/>
                    </div>
                    {/* Date */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Date</label>
                      <input className="adm-input" type="date" value={ff.date||''} onChange={e=>setFf(p=>({...p,date:e.target.value}))}/>
                    </div>
                    {/* Description */}
                    <div style={{display:'flex',flexDirection:'column',gap:4,gridColumn:'1/-1'}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Description</label>
                      <input className="adm-input" value={ff.description||''} onChange={e=>setFf(p=>({...p,description:e.target.value}))} placeholder="Ex: Transport animalier"/>
                    </div>
                    {/* Montant */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Montant (€)</label>
                      <input className="adm-input" type="number" min="0" step="0.01" value={ff.montant||''} onChange={e=>setFf(p=>({...p,montant:e.target.value}))} placeholder="0.00"/>
                    </div>
                    {/* Animal */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Animal</label>
                      <input className="adm-input" value={ff.animal||''} onChange={e=>setFf(p=>({...p,animal:e.target.value}))} placeholder="Ex: Rex"/>
                    </div>
                    {/* Espèce */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Espèce</label>
                      <input className="adm-input" value={ff.espece||''} onChange={e=>setFf(p=>({...p,espece:e.target.value}))} placeholder="Ex: Chien"/>
                    </div>
                    {/* Client */}
                    <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginTop:8,marginBottom:2}}>Client</div>
                    <div style={{gridColumn:'1/-1',background:'var(--gold-lt)',border:'1.5px solid var(--gold)',borderRadius:10,padding:'12px 14px'}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:6}}>👤 Rechercher un utilisateur</label>
                      {ffUserResolved?(
                        <div style={{display:'flex',alignItems:'center',gap:10}}>
                          <div style={{flex:1}}><div style={{fontWeight:700,color:'var(--blue)',fontSize:14}}>{ffUserResolved.name}</div><div style={{fontSize:12,color:'var(--slate)'}}>{ffUserResolved.email}</div></div>
                          <span style={{fontSize:11,fontWeight:700,color:'var(--green-dk)',background:'var(--green-lt)',padding:'3px 10px',borderRadius:100}}>✓ Lié</span>
                          <button className="adm-ta-btn adm-ta-del" style={{padding:'4px 8px'}} onClick={()=>{setFfUserResolved(null);setFfUserSearch('');setFfUserMatches([]);setFf(p=>({...p,clientEmail:'',clientNom:''}));}}>✕</button>
                        </div>
                      ):(
                        <div style={{position:'relative'}}>
                          <input className="adm-input" style={{marginBottom:0}} placeholder="Rechercher par nom ou email…" value={ffUserSearch}
                            onChange={e=>{const v=e.target.value;setFfUserSearch(v);setFf(p=>({...p,clientEmail:v}));_searchUsers(v,setFfUserMatches)}}/>
                          {ffUserMatches.length>0&&(<div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:60,background:'var(--white)',border:'1.5px solid var(--gold)',borderRadius:9,boxShadow:'0 8px 24px rgba(0,0,0,.13)',overflow:'hidden',marginTop:2}}>
                            {ffUserMatches.map(u=>(<div key={u.id||u.email} style={{padding:'9px 14px',cursor:'pointer',borderBottom:'1px solid var(--border)'}}
                              onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'} onMouseLeave={e=>e.currentTarget.style.background=''}
                              onClick={()=>{setFfUserResolved({name:u.name,email:u.email});setFf(p=>({...p,clientEmail:u.email,clientNom:u.name}));setFfUserSearch(u.email);setFfUserMatches([])}}>
                              <div style={{fontWeight:700,color:'var(--blue)',fontSize:13}}>{u.name}</div><div style={{fontSize:12,color:'var(--slate)'}}>{u.email}</div>
                            </div>))}
                          </div>)}
                          {ffUserSearch.trim()&&!ffUserResolved&&ffUserMatches.length===0&&(<div style={{marginTop:6,fontSize:12,color:'var(--slate)'}}>⚠ Aucun compte trouvé.</div>)}
                        </div>
                      )}
                    </div>
                    {[['Nom du client','clientNom'],['Tél. client','clientTel'],['Adresse client','clientAdresse']].map(([label,key])=>(
                      <div key={key} style={{display:'flex',flexDirection:'column',gap:4}}>
                        <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>{label}</label>
                        <input className="adm-input" value={ff[key]||''} onChange={e=>setFf(p=>({...p,[key]:e.target.value}))} placeholder={label}/>
                      </div>
                    ))}
                    {/* Mode de paiement */}
                    <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginTop:8,marginBottom:2}}>Mode de paiement</div>
                    {/* IBAN */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>IBAN</label>
                      <input className="adm-input" value={ff.iban||''} onChange={e=>setFf(p=>({...p,iban:e.target.value}))} placeholder="FR76 …"/>
                    </div>
                    {/* BIC */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>BIC</label>
                      <input className="adm-input" value={ff.bic||''} onChange={e=>setFf(p=>({...p,bic:e.target.value}))} placeholder="Ex: BNPAFRPP"/>
                    </div>
                    {/* Banque */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Banque</label>
                      <input className="adm-input" value={ff.banque||''} onChange={e=>setFf(p=>({...p,banque:e.target.value}))} placeholder="Ex: BNP Paribas"/>
                    </div>
                    {/* Date d'échéance */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Date d'échéance</label>
                      <input className="adm-input" type="date" value={ff.dateEcheance||''} onChange={e=>setFf(p=>({...p,dateEcheance:e.target.value}))}/>
                    </div>
                    {/* Références */}
                    <div style={{display:'flex',flexDirection:'column',gap:4}}>
                      <label style={{fontSize:11,fontWeight:700,color:'var(--slate)',textTransform:'uppercase',letterSpacing:'0.06em'}}>Références</label>
                      <input className="adm-input" value={ff.references||''} onChange={e=>setFf(p=>({...p,references:e.target.value}))} placeholder="Ex: CMD-2025-001"/>
                    </div>
                  </div>
                  {/* Signature admin uniquement */}
                  <div style={{gridColumn:'1/-1',fontSize:11,fontWeight:700,color:'var(--gold-dk)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'2px solid var(--gold)',paddingBottom:4,marginTop:8,marginBottom:8}}>Votre signature</div>
                  <div style={{maxWidth:360}}>
                    <SignatureCanvas canvasRef={ffSigEntRef} label="Signature Entreprise" onSign={setFfSigEntreprise} />
                  </div>
                  <div style={{display:'flex',gap:10,marginTop:20}}>
                    <button className="adm-btn adm-btn-primary" onClick={async()=>{try{const res=await deliveryAPI.createInvoice({...ff,montant:Number(ff.montant)||0,adminSignature:ffSigEntreprise||null});const nf=res.invoice;setFacturesForms(p=>[nf,...p]);generateFacturePDF({...ff,ref:nf.invoiceNumber,invoiceNumber:nf.invoiceNumber,sigEntreprise:ffSigEntreprise,sigClient:null});setShowFfForm(false);setFfSigEntreprise(null);setFfSigClient(null);setFfUserResolved(null);setFfUserSearch('');setFfUserMatches([]);showToast(ffSigEntreprise?'Facture créée & envoyée pour co-signature ✓':'Facture créée ✓')}catch(e){showToast('Erreur : '+(e.message||'impossible'))}}}>🧾 Créer & Télécharger PDF</button>
                    <button className="adm-btn adm-btn-ghost" onClick={()=>setShowFfForm(false)}>Annuler</button>
                  </div>
                </div>
              ):facturesForms.length===0?(
                <div style={{textAlign:'center',padding:'52px 20px'}}>
                  <div className="adm-empty">
                    <div className="adm-empty-icon"><IcoInvoice /></div>
                    <h3>Aucune facture de livraison</h3>
                    <p>Utilisez le bouton <strong>+ Nouvelle facture</strong> en haut à droite pour créer votre première facture.</p>
                  </div>
                </div>
              ):(
                <div className="adm-tbl-wrap">
                  <div className="adm-tbl-head">
                    <div className="adm-tbl-title"><IcoListings /> Factures de livraison <span style={{fontFamily:"'Sora',sans-serif",fontSize:12,fontWeight:400,color:'var(--slate)',marginLeft:6}}>({facturesForms.length})</span></div>
                  </div>
                  <div className="adm-tbl-scroll"><table className="adm-tbl">
                    <thead><tr><th>Référence</th><th>Client</th><th>Animal</th><th>Description</th><th>Montant</th><th>Date</th><th></th></tr></thead>
                    <tbody>{facturesForms.map(f=>(
                      <tr key={f._id||f.id}>
                        <td><span style={{fontFamily:"'Sora',sans-serif",fontSize:11,fontWeight:700,color:'var(--gold-dk)',background:'var(--gold-lt)',padding:'3px 8px',borderRadius:6}}>{f.invoiceNumber||'—'}</span></td>
                        <td>
                          <div className="adm-tbl-name">{f.clientNom||'—'}</div>
                          <div className="adm-tbl-sub">{f.clientEmail||f.clientTel}</div>
                        </td>
                        <td>
                          <div style={{fontWeight:600,color:'var(--blue)'}}>{f.animal||'—'}</div>
                          <div className="adm-tbl-sub">{f.espece}</div>
                        </td>
                        <td style={{fontSize:12,color:'var(--slate)'}}>{f.description||'Transport animalier'}</td>
                        <td><strong style={{color:'var(--gold-dk)',fontSize:14}}>{f.montant?Number(f.montant).toLocaleString('fr-FR')+' €':'—'}</strong></td>
                        <td style={{fontSize:12,color:'var(--slate)',whiteSpace:'nowrap'}}>{f.date}</td>
                        <td>
                          <div style={{display:'flex',gap:6}}>
                            <button className="adm-dl-btn" onClick={()=>generateFacturePDF({...f,ref:f.invoiceNumber,sigEntreprise:f.adminSignature||f.sigEntreprise||null,sigClient:f.userSignature||f.sigClient||null})} title="Télécharger PDF"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> PDF</button>
                            <button className="adm-ta-btn adm-ta-del" onClick={async()=>{try{await deliveryAPI.deleteInvoice(f._id||f.id);setFacturesForms(p=>p.filter(x=>(x._id||x.id)!==(f._id||f.id)))}catch(e){showToast('Erreur')}}} title="Supprimer">
                              <IcoTrash/>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                </div>
              ))}


              {/* ── CARNET DE SANTÉ ── */}
              {!deliveryLoading && docTab==='carnet' && (showCsForm ? (
                <CarnetSanteForm show={showCsForm} users={users}
                  onClose={()=>setShowCsForm(false)}
                  onGenerate={async(d,vaccins,antiparas,sig,photo,cachet,userResolved)=>{
                    const payload={docType:'carnet',animalNom:d.name,animalEspece:d.species,clientNom:userResolved?.name||d.proprietaire_nom,clientEmail:userResolved?.email||d.proprietaire_email,date:d.attestation_date,payload:JSON.stringify({d,vaccins,antiparas,photo:photo||null,cachet:cachet||null}),adminSignature:sig||null}
                    try{
                      const res=await animalDocsAPI.create(payload)
                      setCarnetForms(p=>[res.doc,...p])
                      await generateCarnetSantePDF(d,vaccins,antiparas,sig,photo,cachet)
                      setShowCsForm(false);showToast('Carnet créé & PDF généré ✓')
                      try{new BroadcastChannel('app_sync_event').postMessage({type:'new_doc'})}catch{}
                    }catch(e){showToast('Erreur: '+(e.message||'impossible'))}
                  }}/>
              ) : carnetForms.length===0 ? (
                <div className="adm-empty"><div className="adm-empty-icon">📋</div><h3>Aucun carnet de santé</h3><p>Créez un carnet de santé pour un animal.</p></div>
              ) : (
                <div className="adm-tbl-wrap">
                  <div className="adm-tbl-head"><div className="adm-tbl-title">📋 Carnets de santé <span style={{fontSize:12,fontWeight:400,color:'var(--slate)',marginLeft:6}}>({carnetForms.length})</span></div><button className="adm-btn adm-btn-primary" onClick={()=>setShowCsForm(true)}>+ Nouveau carnet</button></div>
                  <div className="adm-tbl-scroll"><table className="adm-tbl"><thead><tr><th>Référence</th><th>Animal</th><th>Client</th><th>Date</th><th>Statut</th><th></th></tr></thead>
                  <tbody>{carnetForms.slice((carnetPage-1)*ADOC_PER,carnetPage*ADOC_PER).map(f=>(
                    <tr key={f._id}>
                      <td><span style={{fontFamily:"'Sora',sans-serif",fontSize:11,fontWeight:700,color:'var(--gold-dk)',background:'var(--gold-lt)',padding:'3px 8px',borderRadius:6,whiteSpace:'nowrap'}}>{f.docNumber||'—'}</span></td>
                      <td><div className="adm-tbl-name">{f.animalNom||'—'}</div><div className="adm-tbl-sub">{f.animalEspece}</div></td>
                      <td><div className="adm-tbl-name">{f.clientNom||'—'}</div><div className="adm-tbl-sub">{f.clientEmail}</div></td>
                      <td style={{fontSize:12,color:'var(--slate)'}}>{f.date||'—'}</td>
                      <td><span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:'var(--green-lt)',color:'var(--green-dk)'}}>📄 Généré</span></td>
                      <td style={{display:'flex',gap:6,alignItems:'center'}}>
                        <button className="adm-dl-btn" onClick={async()=>{try{const full=await animalDocsAPI.getOne(f._id);const fd=full.doc||f;const p=fd.payload?JSON.parse(fd.payload):{};await generateCarnetSantePDF(p.d||{},p.vaccins||[],p.antiparas||[],fd.adminSignature||null,p.photo||null,p.cachet||null)}catch(e){showToast('Erreur PDF')}}} title="PDF"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> PDF</button>
                        <button className="adm-ta-btn adm-ta-del" onClick={async()=>{try{await animalDocsAPI.delete(f._id);setCarnetForms(p=>p.filter(x=>x._id!==f._id));showToast('Supprimé')}catch(e){showToast('Erreur')}}} title="Supprimer"><IcoTrash/></button>
                      </td>
                    </tr>
                  ))}</tbody></table></div>
                {Math.ceil(carnetForms.length/ADOC_PER)>1&&(<div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,padding:'10px 16px',borderTop:'1px solid var(--border)'}}><button style={{width:30,height:30,borderRadius:7,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:17,fontWeight:700,cursor:'pointer',opacity:carnetPage===1?.35:1,display:'flex',alignItems:'center',justifyContent:'center'}} disabled={carnetPage===1} onClick={()=>setCarnetPage(p=>p-1)}>‹</button><span style={{fontSize:13,fontWeight:600,color:'var(--slate)',minWidth:52,textAlign:'center'}}>{carnetPage}/{Math.ceil(carnetForms.length/ADOC_PER)}</span><button style={{width:30,height:30,borderRadius:7,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:17,fontWeight:700,cursor:'pointer',opacity:carnetPage===Math.ceil(carnetForms.length/ADOC_PER)?.35:1,display:'flex',alignItems:'center',justifyContent:'center'}} disabled={carnetPage===Math.ceil(carnetForms.length/ADOC_PER)} onClick={()=>setCarnetPage(p=>p+1)}>›</button></div>)}
                </div>
              ))}

              {/* ── ATTESTATION VÉTÉRINAIRE ── */}
              {!deliveryLoading && docTab==='attestation' && (showAvForm ? (
                <AttestationVetForm show={showAvForm} users={users}
                  onClose={()=>setShowAvForm(false)}
                  onGenerate={async(d,sig,stamp,userResolved)=>{
                    const payload={docType:'attestation',animalNom:d.animalNom,animalEspece:d.animalEspece,clientNom:userResolved?.name||d.proprietaireNom,clientEmail:userResolved?.email||'',date:d.date,payload:JSON.stringify({d,stamp:stamp||null}),adminSignature:sig||null}
                    try{
                      const res=await animalDocsAPI.create(payload)
                      setAttestForms(p=>[res.doc,...p])
                      await generateAttestationPDF(d,sig,stamp)
                      setShowAvForm(false);showToast('Attestation créée & PDF généré ✓')
                      try{new BroadcastChannel('app_sync_event').postMessage({type:'new_doc'})}catch{}
                    }catch(e){showToast('Erreur: '+(e.message||'impossible'))}
                  }}/>
              ) : attestForms.length===0 ? (
                <div className="adm-empty"><div className="adm-empty-icon">🩺</div><h3>Aucune attestation vétérinaire</h3><p>Créez une attestation vétérinaire officielle.</p></div>
              ) : (
                <div className="adm-tbl-wrap">
                  <div className="adm-tbl-head"><div className="adm-tbl-title">🩺 Attestations vétérinaires <span style={{fontSize:12,fontWeight:400,color:'var(--slate)',marginLeft:6}}>({attestForms.length})</span></div><button className="adm-btn adm-btn-primary" onClick={()=>setShowAvForm(true)}>+ Nouvelle attestation</button></div>
                  <div className="adm-tbl-scroll"><table className="adm-tbl"><thead><tr><th>Référence</th><th>Animal</th><th>Client</th><th>Date</th><th>Statut</th><th></th></tr></thead>
                  <tbody>{attestForms.slice((attestPage-1)*ADOC_PER,attestPage*ADOC_PER).map(f=>(
                    <tr key={f._id}>
                      <td><span style={{fontFamily:"'Sora',sans-serif",fontSize:11,fontWeight:700,color:'var(--gold-dk)',background:'var(--gold-lt)',padding:'3px 8px',borderRadius:6,whiteSpace:'nowrap'}}>{f.docNumber||'—'}</span></td>
                      <td><div className="adm-tbl-name">{f.animalNom||'—'}</div><div className="adm-tbl-sub">{f.animalEspece}</div></td>
                      <td><div className="adm-tbl-name">{f.clientNom||'—'}</div><div className="adm-tbl-sub">{f.clientEmail}</div></td>
                      <td style={{fontSize:12,color:'var(--slate)'}}>{f.date||'—'}</td>
                      <td><span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:'var(--green-lt)',color:'var(--green-dk)'}}>📄 Généré</span></td>
                      <td style={{display:'flex',gap:6,alignItems:'center'}}>
                        <button className="adm-dl-btn" onClick={async()=>{try{const full=await animalDocsAPI.getOne(f._id);const fd=full.doc||f;const parsed=fd.payload?JSON.parse(fd.payload):{};await generateAttestationPDF(parsed.d||{},fd.adminSignature||null,parsed.stamp||null)}catch(e){showToast('Erreur PDF')}}} title="PDF"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> PDF</button>
                        <button className="adm-ta-btn adm-ta-del" onClick={async()=>{try{await animalDocsAPI.delete(f._id);setAttestForms(p=>p.filter(x=>x._id!==f._id));showToast('Supprimé')}catch(e){showToast('Erreur')}}} title="Supprimer"><IcoTrash/></button>
                      </td>
                    </tr>
                  ))}</tbody></table></div>
                {Math.ceil(attestForms.length/ADOC_PER)>1&&(<div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,padding:'10px 16px',borderTop:'1px solid var(--border)'}}><button style={{width:30,height:30,borderRadius:7,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:17,fontWeight:700,cursor:'pointer',opacity:attestPage===1?.35:1,display:'flex',alignItems:'center',justifyContent:'center'}} disabled={attestPage===1} onClick={()=>setAttestPage(p=>p-1)}>‹</button><span style={{fontSize:13,fontWeight:600,color:'var(--slate)',minWidth:52,textAlign:'center'}}>{attestPage}/{Math.ceil(attestForms.length/ADOC_PER)}</span><button style={{width:30,height:30,borderRadius:7,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:17,fontWeight:700,cursor:'pointer',opacity:attestPage===Math.ceil(attestForms.length/ADOC_PER)?.35:1,display:'flex',alignItems:'center',justifyContent:'center'}} disabled={attestPage===Math.ceil(attestForms.length/ADOC_PER)} onClick={()=>setAttestPage(p=>p+1)}>›</button></div>)}
                </div>
              ))}

              {/* ── DOCUMENT D'IDENTIFICATION ── */}
              {!deliveryLoading && docTab==='identification' && (showDiForm ? (
                <IdentificationForm show={showDiForm} users={users}
                  onClose={()=>setShowDiForm(false)}
                  onGenerate={async(d,userResolved)=>{
                    const payload={docType:'identification',animalNom:d.animalName,animalEspece:d.animalEspece,clientNom:userResolved?.name||d.ownerName,clientEmail:userResolved?.email||d.email,date:d.idDate,payload:JSON.stringify({d}),adminSignature:null}
                    try{
                      const res=await animalDocsAPI.create(payload)
                      setIdentifForms(p=>[res.doc,...p])
                      setShowDiForm(false)
                      await generateIdentificationPDF(d)
                      showToast("Identification créée & PDF généré ✓")
                      try{new BroadcastChannel('app_sync_event').postMessage({type:'new_doc'})}catch{}
                    }catch(e){showToast('Erreur: '+(e.message||'impossible'));setShowDiForm(false)}
                  }}/>
              ) : identifForms.length===0 ? (
                <div className="adm-empty"><div className="adm-empty-icon">🪪</div><h3>Aucun document d&apos;identification</h3><p>Créez un document d'identification animale.</p></div>
              ) : (
                <div className="adm-tbl-wrap">
                  <div className="adm-tbl-head"><div className="adm-tbl-title">🪪 Documents d&apos;identification <span style={{fontSize:12,fontWeight:400,color:'var(--slate)',marginLeft:6}}>({identifForms.length})</span></div><button className="adm-btn adm-btn-primary" onClick={()=>setShowDiForm(true)}>+ Nouveau document</button></div>
                  <div className="adm-tbl-scroll"><table className="adm-tbl"><thead><tr><th>Référence</th><th>Animal</th><th>Client</th><th>Date</th><th></th></tr></thead>
                  <tbody>{identifForms.slice((identifPage-1)*ADOC_PER,identifPage*ADOC_PER).map(f=>(
                    <tr key={f._id}>
                      <td><span style={{fontFamily:"'Sora',sans-serif",fontSize:11,fontWeight:700,color:'var(--gold-dk)',background:'var(--gold-lt)',padding:'3px 8px',borderRadius:6,whiteSpace:'nowrap'}}>{f.docNumber||'—'}</span></td>
                      <td><div className="adm-tbl-name">{f.animalNom||'—'}</div><div className="adm-tbl-sub">{f.animalEspece}</div></td>
                      <td><div className="adm-tbl-name">{f.clientNom||'—'}</div><div className="adm-tbl-sub">{f.clientEmail}</div></td>
                      <td style={{fontSize:12,color:'var(--slate)'}}>{f.date||'—'}</td>
                      <td style={{display:'flex',gap:6,alignItems:'center'}}>
                        <button className="adm-dl-btn" onClick={async()=>{try{const full=await animalDocsAPI.getOne(f._id);const fd=full.doc||f;const p=fd.payload?JSON.parse(fd.payload):{};await generateIdentificationPDF(p.d||{})}catch(e){showToast('Erreur PDF')}}} title="PDF"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> PDF</button>
                        <button className="adm-ta-btn adm-ta-del" onClick={async()=>{try{await animalDocsAPI.delete(f._id);setIdentifForms(p=>p.filter(x=>x._id!==f._id));showToast('Supprimé')}catch(e){showToast('Erreur')}}} title="Supprimer"><IcoTrash/></button>
                      </td>
                    </tr>
                  ))}</tbody></table></div>
                {Math.ceil(identifForms.length/ADOC_PER)>1&&(<div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,padding:'10px 16px',borderTop:'1px solid var(--border)'}}><button style={{width:30,height:30,borderRadius:7,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:17,fontWeight:700,cursor:'pointer',opacity:identifPage===1?.35:1,display:'flex',alignItems:'center',justifyContent:'center'}} disabled={identifPage===1} onClick={()=>setIdentifPage(p=>p-1)}>‹</button><span style={{fontSize:13,fontWeight:600,color:'var(--slate)',minWidth:52,textAlign:'center'}}>{identifPage}/{Math.ceil(identifForms.length/ADOC_PER)}</span><button style={{width:30,height:30,borderRadius:7,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:17,fontWeight:700,cursor:'pointer',opacity:identifPage===Math.ceil(identifForms.length/ADOC_PER)?.35:1,display:'flex',alignItems:'center',justifyContent:'center'}} disabled={identifPage===Math.ceil(identifForms.length/ADOC_PER)} onClick={()=>setIdentifPage(p=>p+1)}>›</button></div>)}
                </div>
              ))}

              {/* ── ATTESTATION DE CESSION — MÊME LOGIQUE QUE LIVRAISON ── */}
              {!deliveryLoading && docTab==='cession' && (showAcForm ? (
                <CessionForm show={showAcForm} users={users}
                  onClose={()=>setShowAcForm(false)}
                  onGenerate={async(d,sig,userResolved)=>{
                    const payload={docType:'cession',animalNom:d.animalNom,animalEspece:d.animalEspece,clientNom:userResolved?.name||d.acquereurNom,clientEmail:userResolved?.email||d.acquereurEmail,date:d.date,payload:JSON.stringify({d}),adminSignature:sig||null}
                    try{
                      const res=await animalDocsAPI.create(payload)
                      setCessionForms(p=>[res.doc,...p])
                      await generateCessionPDF({...d,...res.doc},sig,null)
                      setShowAcForm(false);showToast(sig?'Cession créée & envoyée pour co-signature ✓':'Cession créée ✓')
                      try{new BroadcastChannel('app_sync_event').postMessage({type:'new_doc'})}catch{}
                    }catch(e){showToast('Erreur: '+(e.message||'impossible'))}
                  }}/>
              ) : cessionForms.length===0 ? (
                <div className="adm-empty"><div className="adm-empty-icon">📜</div><h3>Aucune attestation de cession</h3><p>Créez une attestation de cession pour formaliser le transfert d&apos;un animal.</p></div>
              ) : (
                <div className="adm-tbl-wrap">
                  <div className="adm-tbl-head"><div className="adm-tbl-title">📜 Attestations de cession <span style={{fontSize:12,fontWeight:400,color:'var(--slate)',marginLeft:6}}>({cessionForms.length})</span></div><button className="adm-btn adm-btn-primary" onClick={()=>setShowAcForm(true)}>+ Nouvelle cession</button></div>
                  <div className="adm-tbl-scroll"><table className="adm-tbl"><thead><tr><th>Référence</th><th>Animal</th><th>Acquéreur</th><th>Date</th><th>Statut</th><th></th></tr></thead>
                  <tbody>{cessionForms.slice((cessionPage-1)*ADOC_PER,cessionPage*ADOC_PER).map(f=>(
                    <tr key={f._id}>
                      <td><span style={{fontFamily:"'Sora',sans-serif",fontSize:11,fontWeight:700,color:'var(--gold-dk)',background:'var(--gold-lt)',padding:'3px 8px',borderRadius:6,whiteSpace:'nowrap'}}>{f.docNumber||'—'}</span></td>
                      <td><div className="adm-tbl-name">{f.animalNom||'—'}</div><div className="adm-tbl-sub">{f.animalEspece}</div></td>
                      <td><div className="adm-tbl-name">{f.clientNom||'—'}</div><div className="adm-tbl-sub">{f.clientEmail}</div></td>
                      <td style={{fontSize:12,color:'var(--slate)'}}>{f.date||'—'}</td>
                      <td><span style={{fontSize:11,fontWeight:700,padding:'3px 10px',borderRadius:100,background:f.signatureStatus==='signed'?'var(--green-lt)':f.signatureStatus==='awaiting_user'?'#FEF3C7':'var(--bg)',color:f.signatureStatus==='signed'?'var(--green-dk)':f.signatureStatus==='awaiting_user'?'#92400E':'var(--slate)'}}>{f.signatureStatus==='signed'?'✅ Signé des deux parties':f.signatureStatus==='awaiting_user'?'⏳ En attente client':'📄 Brouillon'}</span></td>
                      <td style={{display:'flex',gap:6,alignItems:'center'}}>
                        <button className="adm-dl-btn" onClick={async()=>{try{const full=await animalDocsAPI.getOne(f._id);const fd=full.doc||f;const p=fd.payload?JSON.parse(fd.payload):{};await generateCessionPDF({...p.d||{},...fd},fd.adminSignature||null,fd.userSignature||null)}catch(e){showToast('Erreur PDF')}}} title="PDF"><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg> PDF</button>
                        <button className="adm-ta-btn adm-ta-del" onClick={async()=>{try{await animalDocsAPI.delete(f._id);setCessionForms(p=>p.filter(x=>x._id!==f._id));showToast('Supprimé')}catch(e){showToast('Erreur')}}} title="Supprimer"><IcoTrash/></button>
                      </td>
                    </tr>
                  ))}</tbody></table></div>
                {Math.ceil(cessionForms.length/ADOC_PER)>1&&(<div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,padding:'10px 16px',borderTop:'1px solid var(--border)'}}><button style={{width:30,height:30,borderRadius:7,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:17,fontWeight:700,cursor:'pointer',opacity:cessionPage===1?.35:1,display:'flex',alignItems:'center',justifyContent:'center'}} disabled={cessionPage===1} onClick={()=>setCessionPage(p=>p-1)}>‹</button><span style={{fontSize:13,fontWeight:600,color:'var(--slate)',minWidth:52,textAlign:'center'}}>{cessionPage}/{Math.ceil(cessionForms.length/ADOC_PER)}</span><button style={{width:30,height:30,borderRadius:7,border:'1.5px solid var(--border)',background:'var(--white)',color:'var(--blue)',fontSize:17,fontWeight:700,cursor:'pointer',opacity:cessionPage===Math.ceil(cessionForms.length/ADOC_PER)?.35:1,display:'flex',alignItems:'center',justifyContent:'center'}} disabled={cessionPage===Math.ceil(cessionForms.length/ADOC_PER)} onClick={()=>setCessionPage(p=>p+1)}>›</button></div>)}
                </div>
              ))}

            </>
          )}


          {/* ══ TRANSPORT & TRACKING ══ */}
          {view === 'transport' && (
            <>
              <div className="adm-page-hd">
                <div>
                  <div className="adm-hd-title"><IcoTruck /> Transport & Tracking</div>
                  <div className="adm-hd-sub">{Object.keys(trips).length} trajet{Object.keys(trips).length !== 1 ? 's' : ''} enregistré{Object.keys(trips).length !== 1 ? 's' : ''}</div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  {isSyncing && <span style={{ fontSize:12, color:'var(--slate)' }}>🔄 Synchro…</span>}
                </div>
              </div>

              <div className="tracking-operator-view" style={{ padding: '0 0 24px' }}>

                {/* Résultat recherche opérateur */}
                {opSearchResult && (
                  <div className="operator-search-result" style={{ marginBottom: 20 }}>
                    <div className="tracking-card">
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                        <h3 style={{ fontSize:15, color:'var(--blue)', fontFamily:"'Syne',sans-serif" }}>
                          🐕 {escHtml(opSearchResult.animal)} — <span className="tracking-ship-id">{opSearchResult.id}</span>
                        </h3>
                        <button className="tracking-btn tracking-btn-sm tracking-delete-btn" onClick={() => deleteTripAdmin(opSearchResult.id)}>🗑️ Supprimer</button>
                      </div>
                      <div className="tracking-info-grid">
                        <div className="tracking-metric"><div className="tracking-ml">Espèce</div><div className="tracking-mv">{opSearchResult.species}</div></div>
                        <div className="tracking-metric"><div className="tracking-ml">Propriétaire</div><div className="tracking-mv">{escHtml(opSearchResult.owner)}</div></div>
                        <div className="tracking-metric"><div className="tracking-ml">Date</div><div className="tracking-mv">{opSearchResult.date}</div></div>
                        <div className="tracking-metric"><div className="tracking-ml">Durée</div><div className="tracking-mv">{formatDurAdmin(opSearchResult.totalDurationMinutes)}</div></div>
                      </div>
                      <div className="tracking-map-wrap"><canvas ref={canvasOpSearchRef} className="tracking-canvas" /></div>
                      <div className="tracking-anim-bar">
                        <button className={`tracking-btn tracking-btn-sm ${isOpSearchPlaying ? '' : 'tracking-btn-green'}`} onClick={toggleOpSearchPlay}>{isOpSearchPlaying ? '⏸ Pause' : '▶ Animer'}</button>
                        <button className="tracking-btn tracking-btn-sm" onClick={resetOpSearchAnim}>⟳ Réinitialiser</button>
                        <button className="tracking-btn tracking-btn-sm" onClick={() => loadShipAdmin(opSearchResult)}>📍 Panneau principal</button>
                        <span className="tracking-speed-info">⏱️ {formatDurAdmin(opSearchResult.totalDurationMinutes)}</span>
                      </div>
                      <div className="tracking-prog-wrap"><div className="tracking-prog-bar" style={{ width: `${Math.round(opSearchProgress * 100)}%` }} /></div>
                      <div className="tracking-prog-row">
                        <span>{(adjOpSearchWps || opSearchResult.waypoints)?.[0]?.name}</span>
                        <span>{Math.round(opSearchProgress * 100)}%</span>
                        <span>{(adjOpSearchWps || opSearchResult.waypoints)?.[(adjOpSearchWps || opSearchResult.waypoints).length - 1]?.name}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="tracking-op-grid">
                  {/* ── Colonne gauche : formulaire création ── */}
                  <div>
                    <div className="tracking-card">
                      <h3>🐾 Informations animal</h3>
                      <div className="tracking-two-col">
                        <div className="tracking-field"><label>Nom</label><input type="text" placeholder="Rex" value={opForm.name} onChange={e => setOpForm(f => ({ ...f, name: e.target.value }))} /></div>
                        <div className="tracking-field"><label>Espèce</label>
                          <select value={opForm.species} onChange={e => setOpForm(f => ({ ...f, species: e.target.value }))}>
                            <option>Chien</option><option>Chat</option><option>Lapin</option><option>Oiseau</option><option>Reptile</option><option>Autre</option>
                          </select>
                        </div>
                      </div>
                      <div className="tracking-two-col">
                        <div className="tracking-field"><label>Propriétaire</label><input type="text" placeholder="Nom du propriétaire" value={opForm.owner} onChange={e => setOpForm(f => ({ ...f, owner: e.target.value }))} /></div>
                        <div className="tracking-field"><label>Date</label><input type="date" value={opForm.date} onChange={e => setOpForm(f => ({ ...f, date: e.target.value }))} /></div>
                      </div>
                      <div className="tracking-field" style={{ marginTop: 4 }}>
                        <label>📧 Email du client (assigner ce ticket)</label>
                        <input type="email" placeholder="client@email.fr — laisser vide si non assigné" value={opForm.userEmail} onChange={e => setOpForm(f => ({ ...f, userEmail: e.target.value }))} />
                        <small style={{ fontSize: 11, color: 'var(--slate-lt)', marginTop: 3, display: 'block' }}>
                          Le client verra ce ticket dans son espace "Mes transports"
                        </small>
                      </div>
                    </div>

                    <div className="tracking-card" style={{ marginTop:12 }}>
                      <h3>🗺️ Itinéraire avec étapes</h3>
                      <div className="tracking-two-col">
                        <div className="tracking-field"><label>📍 Départ</label><input type="text" placeholder="Paris" value={opForm.start} onChange={e => setOpForm(f => ({ ...f, start: e.target.value }))} /></div>
                        <div className="tracking-field"><label>⏰ Heure</label><input type="time" value={opForm.startTime} onChange={e => setOpForm(f => ({ ...f, startTime: e.target.value }))} /></div>
                      </div>

                      <div className="tracking-stops-section">
                        <label>🔄 Étapes intermédiaires</label>
                        <div className="tracking-stops-list">
                          {stopsArray.map((stop, i) => (
                            <div key={i} className="tracking-stop-item">
                              <span>📍</span>
                              <span className="tracking-stop-name">{escHtml(stop.name)}</span>
                              <span className="tracking-stop-time">{stop.time}</span>
                              <button className="tracking-btn-ico" onClick={() => removeStop(i)}>✕</button>
                            </div>
                          ))}
                          {stopsArray.length === 0 && <div className="tracking-stops-empty">Aucune étape intermédiaire</div>}
                        </div>
                        <div className="tracking-stop-row">
                          <input type="text" placeholder="Nom de l'étape (ex: Lyon)" value={stopName} onChange={e => setStopName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addStop()} />
                          <input type="time" value={stopTime} onChange={e => setStopTime(e.target.value)} />
                          <button className="tracking-btn tracking-btn-green tracking-btn-sm" onClick={addStop}>+</button>
                        </div>
                      </div>

                      <div className="tracking-two-col">
                        <div className="tracking-field"><label>🏁 Arrivée</label><input type="text" placeholder="Marseille" value={opForm.end} onChange={e => setOpForm(f => ({ ...f, end: e.target.value }))} /></div>
                        <div className="tracking-field"><label>⏰ Heure</label><input type="time" value={opForm.endTime} onChange={e => setOpForm(f => ({ ...f, endTime: e.target.value }))} /></div>
                      </div>

                      <button className="tracking-btn tracking-btn-green tracking-btn-full" onClick={generateTripAdmin}>✅ Générer le numéro de suivi</button>

                      {showGenBox && (
                        <div className="tracking-gen-box">
                          <div>Numéro de suivi généré</div>
                          <div className="tracking-gen-num">{generatedCode}</div>
                          <div className="tracking-gen-sub">{opForm.name || (activeShipAdmin?.animal)} · {opForm.start || activeShipAdmin?.waypoints?.[0]?.name} → {opForm.end || activeShipAdmin?.waypoints?.[activeShipAdmin?.waypoints?.length - 1]?.name}</div>
                          <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12 }}>
                            <button className="tracking-btn tracking-btn-sm" onClick={() => { navigator.clipboard.writeText(generatedCode); showToast('Numéro copié : ' + generatedCode) }}>📋 Copier</button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ── Colonne droite : liste + suivi en direct ── */}
                  <div>
                    <div className="tracking-card">
                      <h3>📋 Tous les trajets</h3>
                      <div className="tracking-search-row tracking-list-search">
                        <input type="text" placeholder="ANI-XXXX" value={opSearchCode} onChange={e => setOpSearchCode(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && doOpSearch()} />
                        <button className="tracking-btn tracking-btn-green tracking-btn-sm" onClick={doOpSearch}>🔍 Rechercher</button>
                      </div>
                      {opSearchError && <div className="tracking-err">{opSearchError}</div>}
                      <div className="tracking-ship-list">
                        {Object.values(trips).length === 0 && <div className="tracking-ship-empty">Aucun trajet créé</div>}
                        {Object.values(trips).map(ship => {
                          const meta = tripsProgress[ship.id] || {}
                          const pct  = Math.round((meta.progressAtStart || 0) * 100)
                          const playing = isTripPlayingAdmin(ship.id)
                          const isAct = activeShipAdmin?.id === ship.id
                          return (
                            <div key={ship.id} className={`tracking-ship-row ${isAct ? 'tracking-ship-row-active' : ''}`}>
                              <div onClick={() => loadShipAdmin(ship)} style={{ display:'flex', alignItems:'center', gap:12, flex:1, cursor:'pointer' }}>
                                <div>{playing ? '▶️' : '🐕'}</div>
                                <div className="tracking-ship-info">
                                  <strong>{escHtml(ship.animal)}</strong> · {ship.species}
                                  <small>{escHtml(ship.waypoints?.[0]?.name)} → {escHtml(ship.waypoints?.[ship.waypoints.length - 1]?.name)}</small>
                                  <small className="tracking-ship-duration">⏱️ {formatDurAdmin(ship.totalDurationMinutes)}</small>
                                  {pct > 0 && pct < 100 && (
                                    <div className="tracking-ship-progress">
                                      <div className="tracking-ship-progress-bar" style={{ width:`${pct}%` }} />
                                      <span>{pct}% {playing ? '🔴 En direct' : ''}</span>
                                    </div>
                                  )}
                                  {pct >= 100 && <span className="tracking-ship-done">✅ Terminé</span>}
                                </div>
                                <div className="tracking-ship-id">{ship.id}</div>
                              </div>
                              <button className="tracking-btn-ico tracking-delete-btn" onClick={() => deleteTripAdmin(ship.id)} title="Supprimer">🗑️</button>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    {activeShipAdmin && (
                      <div className="tracking-card" style={{ marginTop:12 }}>
                        <h3>📍 Suivi en direct — <span className="tracking-ship-label">{activeShipAdmin.animal} — {activeShipAdmin.id}</span></h3>
                        <div className="tracking-map-wrap"><canvas ref={canvasAdminRef} className="tracking-canvas" /></div>
                        <div className="tracking-anim-bar">
                          <button className={`tracking-btn tracking-btn-sm ${isPlayingAdmin ? '' : 'tracking-btn-green'}`} onClick={togglePlayAdmin}>{isPlayingAdmin ? '⏸ Pause' : '▶ Animer'}</button>
                          <button className="tracking-btn tracking-btn-sm" onClick={resetAnimAdmin}>⟳ Réinitialiser</button>
                          <span className="tracking-speed-info">⏱️ Durée : {formatDurAdmin(activeShipAdmin.totalDurationMinutes)}</span>
                        </div>
                        <div className="tracking-prog-wrap"><div className="tracking-prog-bar" style={{ width:`${Math.round(sharedProgressAdmin * 100)}%` }} /></div>
                        <div className="tracking-prog-row">
                          <span>{activeShipAdmin.waypoints?.[0]?.name}</span>
                          <span>{Math.round(sharedProgressAdmin * 100)}%</span>
                          <span>{activeShipAdmin.waypoints?.[activeShipAdmin.waypoints.length - 1]?.name}</span>
                        </div>
                        <div className="tracking-saved-info">
                          💾 Progression sauvegardée : {Math.round((tripsProgress[activeShipAdmin.id]?.progressAtStart || 0) * 100)}%
                          {isTripPlayingAdmin(activeShipAdmin.id) && !isPlayingAdmin && <span className="tracking-live-badge"> — 🔴 Animation en cours</span>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}


          {/* ══ PARAMÈTRES ══ */}
          {view === 'settings' && (
            <>
              <div className="adm-page-hd"><div className="adm-hd-title"><IcoSettings /> Paramètres</div></div>
              <div className="adm-settings-grid">
                {[
                  { title:'Informations générales', icon:<IcoGlobe />, fields:[
                    {label:'Nom du site', val:'Animaux pour Adoption de la France', type:'text'},
                    {label:'Email de contact', val:'animauxdecompagnie@animauxpouradoptiondelafrance.com', type:'email'},
                    {label:'Téléphone support', val:'+33 07 57 75 00 92', type:'text'},
                  ]},
                  { title:'Finances', icon:<IcoRevenue />, fields:[
                    {label:'Taux de commission (%)', val:'10', type:'number'},
                    {label:'Prix min. annonce (€)', val:'0', type:'number'},
                  ]},
                  { title:'Sécurité admin', icon:<IcoSettings />, fields:[
                    {label:'Changer l\'email admin', val:'animauxdecompagnie@animauxpouradoptiondelafrance.com', type:'email'},
                    {label:'Nouveau mot de passe', val:'', type:'password', placeholder:'••••••••'},
                    {label:'Confirmer mot de passe', val:'', type:'password', placeholder:'••••••••'},
                  ]},
                  { title:'SEO', icon:<IcoTrend />, fields:[
                    {label:'Meta titre', val:'Animaux pour Adoption de la France', type:'text'},
                    {label:'Google Analytics ID', val:'', type:'text', placeholder:'G-XXXXXXXXXX'},
                  ]},
                ].map((card,i) => (
                  <div key={i} className="adm-settings-card">
                    <div className="adm-settings-title">{card.icon} {card.title}</div>
                    {card.fields.map(f => (
                      <div key={f.label} className="adm-fg">
                        <label className="adm-label">{f.label}</label>
                        <input className="adm-input" type={f.type} defaultValue={f.val} placeholder={f.placeholder||''} />
                      </div>
                    ))}
                    <button className="adm-btn adm-btn-primary" style={{ marginTop:8 }} onClick={() => showToast('Paramètres sauvegardés')}><IcoSave /> Sauvegarder</button>
                  </div>
                ))}
              </div>
            </>
          )}

        </div>
      </div>

      {/* Confirm delete */}
      {confirmDel && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box">
            <div style={{ marginBottom:10, color:'var(--red)' }}><IcoTrash /></div>
            <div style={{ fontFamily:'\'Syne\',sans-serif', fontSize:18, fontWeight:800, color:'var(--blue)', marginBottom:6 }}>Supprimer ?</div>
            <div style={{ fontSize:13, color:'var(--slate)', marginBottom:6 }}>Cette action est irréversible.</div>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--blue)', marginBottom:20 }}>{confirmDel.label}</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button className="adm-btn adm-btn-danger" onClick={async () => {
                try {
                  if (confirmDel.type === 'listing') {
                    await listingsAPI.delete(confirmDel.id)
                    setListings(l => l.filter(i => (i.id||i._id) !== confirmDel.id))
                    showToast('Annonce supprimée')
                  }
                  if (confirmDel.type === 'user') {
                    await usersAPI.deleteUser(confirmDel.id)
                    setUsers(u => u.filter(i => i.id !== confirmDel.id))
                    showToast('Utilisateur supprimé')
                  }
                  setConfirmDel(null)
                } catch (err) {
                  showToast(err.message || 'Erreur lors de la suppression')
                }
              }}>Supprimer</button>
              <button className="adm-btn adm-btn-ghost" onClick={() => setConfirmDel(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Rejeter une annonce avec motif */}
      {rejectModal && (
        <div className="adm-confirm-overlay">
          <div className="adm-confirm-box" style={{ maxWidth:400, textAlign:'left' }}>
            <div style={{ marginBottom:10, color:'var(--red)', textAlign:'center' }}><IcoBan /></div>
            <div style={{ fontFamily:'\'Syne\',sans-serif', fontSize:18, fontWeight:800, color:'var(--blue)', marginBottom:6, textAlign:'center' }}>Rejeter cette annonce ?</div>
            <div style={{ fontSize:13, fontWeight:700, color:'var(--blue)', marginBottom:14, textAlign:'center' }}>{rejectModal.title}</div>
            <label className="adm-label">Motif du rejet (optionnel)</label>
            <textarea
              className="adm-input adm-textarea"
              rows={4}
              maxLength={300}
              placeholder="Ex : photos insuffisantes, informations manquantes, prix incohérent…"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              autoFocus
            />
            <div style={{ fontSize:11, color:'var(--slate-lt)', textAlign:'right', marginBottom:16 }}>{rejectReason.length}/300</div>
            <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
              <button className="adm-btn adm-btn-danger" onClick={confirmReject} disabled={rejectSaving}>
                {rejectSaving ? 'Envoi…' : 'Rejeter l’annonce'}
              </button>
              <button className="adm-btn adm-btn-ghost" disabled={rejectSaving} onClick={() => setRejectModal(null)}>Annuler</button>
            </div>
          </div>
        </div>
      )}

      {sbOpen && <div className="adm-sb-overlay" onClick={() => setSbOpen(false)} />}
      {toast && <div className="adm-toast">{toast}</div>}
    </div>
  )
}